import React, { useEffect, useMemo, useState, useRef } from 'react';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import moment from 'moment';
import SettingsCard from '../../../../../shared/components/structure/SettingsCard';
import ApplicationModel from '../../../../../shared/models/applications/api/ApplicationModel';
import ApiKeyModel from '../../../../../shared/models/auth/api/ApiKeyModel';
import OnboardingProgressModel from '../../../../../shared/models/onboarding/api/OnboardingProgressModel';
import Button from '../../../../../shared/components/general/Button';
import Loader from '../../../../../shared/components/general/Loader';
import TooltipWrapper from '../../../../../shared/components/general/TooltipWrapper';
import ConditionalTooltipWrapper from '../../../../../shared/components/general/ConditionalTooltipWrapper';
import TextField from '../../../../../shared/components/forms/TextField';
import ConfirmationDialog from '../../../../../shared/components/structure/ConfirmationDialog';
import ApiKeyContainer from '../../../../../shared/state/containers/ApiKeyContainer';
import MemberContainer from '../../../../../shared/state/containers/MemberContainer';
import UserContainer from '../../../../../shared/state/containers/UserContainer';
import { useLocation } from 'react-router-dom';
import AppliedApplicationTemplateContainer from '../../../../../shared/state/containers/AppliedApplicationTemplateContainer';
import ContentCollapsingSection from '../../../../../shared/components/structure/ContentCollapsingSection';
import TestCaseContainer from '../../../../../shared/hooks/TestCaseContainer';
import { getTestCases } from '../../../../../shared/api/controllers/testCases';
import { generateOnboardingLink, getOnboardingProgressForApplication, updateOnboardingProgress, previewOnboardingEmail, sendOnboardingEmail, updateOnboardingEmailTriggerStatus, bulkUpdateOnboardingEmailTriggerStatus, OnboardingEmailTriggerId, OnboardingEmailTriggerState, OnboardingEmailTriggerStatus, SendOnboardingEmailRequest, PreviewOnboardingEmailRequest } from '../../../../../shared/api/controllers/onboarding';
import { getTelephonyDeployments } from '../../../../../shared/api/controllers/telephonyDeployment';
import { TelephonyDeploymentResponse } from '../../../../../shared/models/telephonyDeployment/TelephonyDeploymentResponse';
import { updateApplication } from '../../../../../shared/api/controllers/applications';
import { loadApplicationModuleFields, flattenOrganizationOwnedApplicationModuleFields } from '../../../../onboarding/api/applicationModuleFields';
import { FoodOrderingFeatureFlagId, HealthcareSchedulingFeatureFlagId, OutboundCallFeatureFlagId } from '../../../../../shared/constants/featureFlagIds';
import { getOnboardingBaseUrl } from '../../../../../shared/constants/Urls';
import {
    color_colors_ocean,
    color_shades_dark,
    color_shades_darker,
    color_shades_darkest,
    color_shades_light,
    color_shades_lighter,
    color_shades_lightest,
    dark_grey_blue,
    silver_two,
    status_error,
    status_success,
    voicify_spark_lightest,
    color_colors_growth,
    highlight_green,
    color_colors_gold,
    color_variants_gold_light,
    color_variants_ocean_light_1
} from '../../../../../shared/constants/colors';
import { getOnboardingFlow, OnboardingFlowConfig, OnboardingFlowType, OnboardingQuestionConfig } from '../../../../onboarding/config/onboardingFlows';
import {
    formatHoursForDisplay,
    formatProcedureTypeAnswerValue,
    isProcedureTypeAnswerValue,
    formatSpecialDatesForDisplay,
    isSpecialDatesAnswerValue,
    ProcedureTypeAnswerValue,
    SpecialDatesAnswerValue,
    WeeklyHoursAnswerValue
} from '../../../../onboarding/utils/onboardingFieldUtils';
import {
    getOnboardingProgressAnswers,
    getOnboardingProgressPhase,
    getTestingStepFeedback,
    getTestingStepFeedbackHistory,
    getTestingProgressState,
    getTestingFeedbackResolutionState,
    parseOnboardingProgressDetails,
    setTestingFeedbackResolutionState,
    serializeOnboardingProgressDetails
} from '../../../../onboarding/utils/onboardingProgressDetails';
import { getTestingStepCount as getTestingStepCountForFlow, getTestingSteps } from '../../../../onboarding/utils/onboardingTestingSteps';
import { saveOnboardingProgressForApplication } from '../../../../onboarding/utils/saveOnboardingProgressForApplication';
import {
    ExplicitOnboardingQuestionOwnership,
    OnboardingQuestionOwnership as ResolvedQuestionOwnership,
    resolveOnboardingQuestionOwnership
} from '../../../../onboarding/utils/onboardingQuestionOwnership';
import InviteSparkFormModal from '../../../../../shared/scenes/applicationAppliedTemplateManager/components/InviteSparkFormModal';
import BulkEmailTriggerManagerDialog, {
    getLocationEmailTriggerStatus,
    getUnappliedDefaultTriggerIds,
    inboundLocationEmailTriggers,
    LocationEmailTriggerDefinition,
    LocationEmailTriggerDefaults,
    outboundLocationEmailTriggers,
    updateLocationEmailTriggerDefaults
} from './BulkEmailTriggerManagerDialog';
import OnboardingActionRequiredSection, { OnboardingActionItem } from './OnboardingActionRequiredSection';

interface OnboardingLinksCardProps {
    commonApp?: ApplicationModel;
    locationApps?: ApplicationModel[];
    apiKeyContainer: ApiKeyContainer;
    memberContainer: MemberContainer;
    userContainer: UserContainer;
    organizationId?: string;
    isLoading?: boolean;
    appliedTemplateContainer?: AppliedApplicationTemplateContainer;
}

interface LocationApplication extends ApplicationModel {
    id: string;
    name: string;
}

type LinkState = {
    loading?: boolean;
    url?: string;
    copied?: boolean;
    error?: string;
};

type ProgressFieldValue = string | WeeklyHoursAnswerValue | SpecialDatesAnswerValue | ProcedureTypeAnswerValue;

interface ProgressSectionItem {
    key: string;
    stepId?: string;
    label: string;
    value: string;
    isMultiline?: boolean;
    history?: Array<{
        savedAt: string;
        value: string;
    }>;
}

interface ProgressSectionSummary {
    key: string;
    title: string;
    items: ProgressSectionItem[];
}

interface ProgressSummary {
    sectionTitle?: string;
    answerCount: number;
    feedbackCount?: number;
    flowTitle: string;
    responseSections: ProgressSectionSummary[];
    testingFeedbackSections: ProgressSectionSummary[];
}

type ProgressState = {
    loading?: boolean;
    data?: OnboardingProgressModel | null;
    summary?: ProgressSummary | null;
    error?: string;
};

type ProgressDetailsDialogState = {
    application: LocationApplication;
    summary: ProgressSummary;
    progress?: OnboardingProgressModel | null;
};

type TestingFeedbackDialogState = {
    application: LocationApplication;
    summary: ProgressSummary;
    progress?: OnboardingProgressModel | null;
};

const copyIcon = require('../../../../../shared/content/images/copy-text.svg');
const linkIcon = require('../../../../../shared/content/images/ssml/hyperlink-3-blue.svg');
const lockShieldIcon = require('../../../../../shared/content/images/spark/lock-shield.svg');
const infoIcon = require('../../../../../shared/content/images/information-circle.svg');
const onboardingApiKeyPrefix = 'Onboarding';

const dayLabels: Array<{ key: keyof WeeklyHoursAnswerValue; label: string }> = [
    { key: 'mondayHours', label: 'Monday' },
    { key: 'tuesdayHours', label: 'Tuesday' },
    { key: 'wednesdayHours', label: 'Wednesday' },
    { key: 'thursdayHours', label: 'Thursday' },
    { key: 'fridayHours', label: 'Friday' },
    { key: 'saturdayHours', label: 'Saturday' },
    { key: 'sundayHours', label: 'Sunday' }
];

const isLocationApplication = (app: ApplicationModel): app is LocationApplication => {
    return typeof app?.id === 'string' && typeof app?.name === 'string';
};

const detectFlowType = (application: ApplicationModel): OnboardingFlowType => {
    const featureFlags = application.featureFlags || [];
    if (featureFlags.some(flag => flag.id === FoodOrderingFeatureFlagId)) {
        return 'food-ordering';
    }
    if (featureFlags.some(flag => flag.id === HealthcareSchedulingFeatureFlagId)) {
        return 'healthcare';
    }
    return 'food-ordering';
};

const getProgressAnswers = (progress?: OnboardingProgressModel | null): Record<string, ProgressFieldValue> => {
    return getOnboardingProgressAnswers(progress) as Record<string, ProgressFieldValue>;
};

const isWeeklyHoursAnswer = (value: ProgressFieldValue | undefined): value is WeeklyHoursAnswerValue => {
    return !!value && typeof value === 'object' && 'mondayHours' in value;
};

const hasWeeklyHoursAnswer = (value: WeeklyHoursAnswerValue): boolean => {
    return dayLabels.some(day => !!formatHoursForDisplay(value[day.key]));
};

const humanizeAnswerKey = (value: string): string => value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, match => match.toUpperCase());

const formatAnswerValue = (question: OnboardingQuestionConfig | undefined, value: ProgressFieldValue): { value: string; isMultiline: boolean } => {
    if (isWeeklyHoursAnswer(value)) {
        const formatted = dayLabels
            .map(day => ({
                label: day.label,
                value: formatHoursForDisplay(value[day.key])
            }))
            .filter(day => !!day.value)
            .map(day => `${day.label}: ${day.value}`)
            .join('\n');
        return { value: formatted, isMultiline: true };
    }

    if (isSpecialDatesAnswerValue(value)) {
        const formatted = formatSpecialDatesForDisplay(value);
        return {
            value: formatted,
            isMultiline: formatted.includes('\n')
        };
    }

    if (isProcedureTypeAnswerValue(value)) {
        return {
            value: formatProcedureTypeAnswerValue(value),
            isMultiline: true
        };
    }

    const stringValue = typeof value === 'string' ? value.trim() : '';
    if (!stringValue) {
        return { value: '', isMultiline: false };
    }

    if (question?.type === 'item-list') {
        const parts = stringValue.split(',');
        const isRawItemList = parts.length === 3 && parts.every(p => p.trim().length < 100);
        if (isRawItemList && question.getSaveInstructions) {
            const instructions = question.getSaveInstructions(stringValue, { application: {} as any, moduleFieldLookup: {} });
            const fullValue = instructions[0]?.value?.trim() || '';
            if (fullValue) {
                return { value: fullValue, isMultiline: fullValue.includes('\n') };
            }
        }
        return { value: stringValue, isMultiline: stringValue.includes('\n') };
    }

    if (question?.allowMultipleChoices) {
        const selectedValues = stringValue.split(',').map(entry => entry.trim()).filter(Boolean);
        const formatted = selectedValues
            .map(selectedValue => question.choices?.find(choice => choice.value === selectedValue)?.label || selectedValue)
            .join(', ');
        return {
            value: formatted,
            isMultiline: false
        };
    }

    const selectedChoice = question?.choices?.find(choice => choice.value === stringValue);
    const displayValue = selectedChoice?.label || stringValue;
    return {
        value: displayValue,
        isMultiline: displayValue.includes('\n')
    };
};

const getCurrentStepTitle = (flow: OnboardingFlowConfig, currentStep?: string): string => {
    if (!currentStep?.trim()) {
        return '';
    }
    const [stepId] = currentStep.split(':');
    return flow.steps.find(step => step.id === stepId)?.title || '';
};

const getCurrentTestingStepTitle = (application: LocationApplication, progress?: OnboardingProgressModel | null): string => {
    const answers = getOnboardingProgressAnswers(progress);
    const flowType = detectFlowType(application);
    const testingState = getTestingProgressState(progress);
    return getTestingSteps(flowType, answers).find(step => step.id === testingState.currentStepId)?.title || '';
};

export const buildProgressSummary = (
    application: LocationApplication, 
    progress?: OnboardingProgressModel | null,
    orgLocationSpecificFieldKeys?: string[]
): ProgressSummary | null => {
    if (!progress) {
        return null;
    }

    const isOrgOnboarding = application?.allowsTemplating === true || 
        (application?.name || "").trim().toLowerCase() === "shared configuration" || 
        (application?.name || "").trim().toLowerCase() === "shared topics";

    const answers = getProgressAnswers(progress);
    const greetingMode = typeof answers['greetingMode'] === 'string' ? answers['greetingMode'] : null;
    const useOwnGreeting = greetingMode === 'use-own';
    const details = parseOnboardingProgressDetails(progress?.detailsJson);
    const locSpecificKeys = (orgLocationSpecificFieldKeys && orgLocationSpecificFieldKeys.length > 0)
        ? orgLocationSpecificFieldKeys
        : (details.locationSpecificFieldKeys || []);

    const flow = getOnboardingFlow(detectFlowType(application));
    const questionLookup = flow.steps.reduce((accumulator, step) => {
        step.questions.forEach(question => {
            accumulator[question.id] = question;
        });
        return accumulator;
    }, {} as Record<string, OnboardingQuestionConfig>);

    const answerEntries = Object.entries(answers).filter(([key, value]) => {
        if (key === 'greetingMode' && useOwnGreeting) {
            return false;
        }
        if ((key === 'englishGreeting' || key === 'closedGreeting') && !useOwnGreeting) {
            return false;
        }
        if (isWeeklyHoursAnswer(value as ProgressFieldValue)) {
            return hasWeeklyHoursAnswer(value as WeeklyHoursAnswerValue);
        }
        if (isSpecialDatesAnswerValue(value as ProgressFieldValue)) {
            return (value as SpecialDatesAnswerValue).length > 0;
        }
        if (isProcedureTypeAnswerValue(value as ProgressFieldValue)) {
            return (value as ProcedureTypeAnswerValue).length > 0;
        }
        return typeof value === 'string' && value.trim().length > 0;
    }).filter(([key]) => {
        const question = questionLookup[key];
        if (!question) {
            return isOrgOnboarding;
        }

        const ownershipVariables = question.ownershipVariables?.length
            ? question.ownershipVariables
            : [
                ...(question.associatedVariable ? [question.associatedVariable] : []),
                ...(question.associatedVariables || [])
            ];
        const explicitOwnership: ExplicitOnboardingQuestionOwnership | undefined = question.ownership === 'organization'
            ? ResolvedQuestionOwnership.Shared
            : question.ownership === 'location'
                ? ResolvedQuestionOwnership.Location
                : question.ownership === 'both'
                    ? ResolvedQuestionOwnership.Both
                    : undefined;
        const resolvedOwnership = resolveOnboardingQuestionOwnership({
            id: question.id,
            associatedVariable: ownershipVariables[0],
            associatedVariables: ownershipVariables.slice(1)
        }, {
            explicitOwnership,
            progressCache: {
                locationSpecificFieldKeys: locSpecificKeys,
                sparkLocationSpecificVariables: details.sparkLocationSpecificVariables || []
            }
        }).ownership;
        const canMoveToLocation = question.ownership !== 'location'
            && question.ownership !== 'both'
            && (question.ownership !== 'organization' || !!question.allowLocationOverride);
        const effectiveOwnership = locSpecificKeys.includes(key) && canMoveToLocation
            ? ResolvedQuestionOwnership.Location
            : resolvedOwnership;

        return isOrgOnboarding
            ? effectiveOwnership !== ResolvedQuestionOwnership.Location
            : effectiveOwnership === ResolvedQuestionOwnership.Location
                || effectiveOwnership === ResolvedQuestionOwnership.Both;
    });

    const sectionMap: Record<string, ProgressSectionSummary> = {};

    answerEntries.forEach(([answerKey, rawValue]) => {
        const question = questionLookup[answerKey];
        const formatted = formatAnswerValue(question, rawValue as ProgressFieldValue);
        if (!formatted.value) {
            return;
        }

        if (isOrgOnboarding && formatted.value.includes('{') && formatted.value.includes('}')) {
            return;
        }

        const sectionKey = question?.section || 'Other answers';
        if (!sectionMap[sectionKey]) {
            sectionMap[sectionKey] = {
                key: sectionKey,
                title: sectionKey,
                items: []
            };
        }

        sectionMap[sectionKey].items.push({
            key: answerKey,
            label: question?.title || humanizeAnswerKey(answerKey),
            value: formatted.value,
            isMultiline: formatted.isMultiline
        });
    });

    const orderedSectionTitles = flow.steps
        .reduce((accumulator, step) => {
            if (!accumulator.includes(step.title)) {
                accumulator.push(step.title);
            }
            return accumulator;
        }, [] as string[]);

    const orderedSections = orderedSectionTitles
        .map(title => sectionMap[title])
        .filter(Boolean);

    const remainingSections = Object.values(sectionMap)
        .filter(section => !orderedSections.some(orderedSection => orderedSection.key === section.key));

    const responseSections = orderedSections.concat(remainingSections);
    const testingSteps = getTestingSteps(detectFlowType(application), answers);
    const testingFeedback = getTestingStepFeedback(progress);
    const testingFeedbackHistory = getTestingStepFeedbackHistory(progress);
    const feedbackEntries = testingSteps
        .filter(step => step.id !== 'ready-to-go')
        .map(step => {
            const value = typeof testingFeedback[step.id] === 'string' ? testingFeedback[step.id].trim() : '';
            const newestFirstHistory = (testingFeedbackHistory[step.id] || [])
                .filter(entry => !!entry.value.trim())
                .slice()
                .reverse()
                .map(entry => ({
                    savedAt: entry.savedAt,
                    value: entry.value
                }));
            const history = newestFirstHistory[0]?.value.trim() === value
                ? newestFirstHistory.slice(1)
                : newestFirstHistory;

            return {
                stepId: step.id,
                label: step.title,
                value,
                history
            };
        })
        .filter(entry => !!entry.value || entry.history.length > 0);

    const testingFeedbackSections = feedbackEntries.length > 0 ? [{
        key: 'testing-feedback',
        title: 'Testing feedback',
        items: feedbackEntries.map(entry => ({
            key: `testing-feedback-${entry.stepId}`,
            stepId: entry.stepId,
            label: entry.label,
            value: entry.value,
            isMultiline: true,
            history: entry.history
        }))
    }] : [];

    return {
        sectionTitle: getOnboardingProgressPhase(progress) === 'testing'
            ? getCurrentTestingStepTitle(application, progress)
            : getCurrentStepTitle(flow, progress.currentStep),
        answerCount: answerEntries.length,
        feedbackCount: feedbackEntries.filter(entry => !!entry.value).length,
        flowTitle: flow.title,
        responseSections,
        testingFeedbackSections
    };
};

const emailHtmlToEditableText = (html: string): string => {
    if (!html) return '';
    let text = html;
    text = text.replace(/<\/p>\s*<p>/gi, '\n\n');
    text = text.replace(/<p>/gi, '').replace(/<\/p>/gi, '');
    text = text.replace(/<br\s*\/?>/gi, '\n');
    text = text.replace(/<li>/gi, '• ').replace(/<\/li>/gi, '\n');
    text = text.replace(/<ul>/gi, '').replace(/<\/ul>/gi, '');
    text = text.replace(/&nbsp;/g, ' ')
               .replace(/&lt;/g, '<')
               .replace(/&gt;/g, '>')
               .replace(/&amp;/g, '&');
    return text.trim();
};

const editableTextToEmailHtml = (text: string): string => {
    if (!text) return '';
    let normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const paragraphs = normalized.split('\n\n');
    const formattedParagraphs = paragraphs.map(p => {
        const lines = p.split('\n');
        const formattedLines = lines.map(line => {
            const trimmed = line.trim();
            if (trimmed.startsWith('•') || trimmed.startsWith('*') || trimmed.startsWith('-')) {
                const content = trimmed.substring(1).trim();
                return `• ${content}`;
            }
            return line;
        });
        return formattedLines.join('<br />');
    });
    return formattedParagraphs.map(p => `<p>${p}</p>`).join('');
};

export const getUnsentLocationEmailTriggers = (
    emails: LocationEmailTriggerDefinition[],
    emailTriggers: Partial<Record<OnboardingEmailTriggerId, OnboardingEmailTriggerState>>,
    isLocationOnboardingComplete = false
): LocationEmailTriggerDefinition[] => emails.filter(email =>
    emailTriggers[email.triggerId]?.status !== 'Sent'
    && !(isLocationOnboardingComplete && email.triggerId === 'loc-reminder')
);

export const getTargetLaunchDateDisplay = (app: ApplicationModel): string => app.targetLaunchDate
    ? moment.utc(app.targetLaunchDate).format('MM/DD/YYYY')
    : "your scheduled go-live date";

export const populateTargetLaunchDate = (body: string, app: ApplicationModel): string => app.targetLaunchDate
    ? body.replace(/your scheduled go-live date/gi, getTargetLaunchDateDisplay(app))
    : body;

const getClientSideEmailFallback = (
    app: LocationApplication,
    triggerId: string,
    recipientFirstName: string,
    recipientEmail: string,
    linkState: any
) => {
    const greeting = recipientFirstName ? `Hi ${recipientFirstName},` : "Hi there,";
    let link = linkState.url || `${getOnboardingBaseUrl()}/[Generated Token]`;
    if (triggerId === 'outbound-launched' || triggerId === 'outbound-reminder') {
        if (linkState.url) {
            const token = linkState.url.split("/").pop();
            link = `${getOnboardingBaseUrl()}/outbound/${token}`;
        } else {
            link = `${getOnboardingBaseUrl()}/outbound/[Generated Token]`;
        }
    }
    const clientName = app.name || "your location";
    
    const goLiveDate = getTargetLaunchDateDisplay(app);
    const deadlineDate = app.launchDate
        ? moment.utc(app.launchDate).subtract(7, 'days').format('MM/DD/YYYY')
        : moment.utc().add(7, 'days').format('MM/DD/YYYY');
        
    let subject = '';
    let rawBody = '';

    if (triggerId === 'org-launched') {
        subject = `Welcome to Voicify Onboarding: Let's set up your Organization!`;
        rawBody = `Hi there,\n\nWelcome to Voicify!\n\nTo get started, please complete your <a href="[LINK]" target="_blank" style="color: #31b6d3; text-decoration: underline; font-weight: bold;">organization onboarding form</a>.\n\nA few quick notes:\n• Designate one organization point of contact to submit the form\n• The form takes approximately 15 minutes to complete\n• Please submit by [DATE]\n• Once submitted, the form cannot be edited\n\nThanks for helping us get your AI Assistant up and running!\n\n— The Voicify Team`;
    } else if (triggerId === 'org-reminder') {
        subject = `Friendly Reminder: Complete Onboarding for Your Organization`;
        rawBody = `Hi there,\n\nJust a friendly reminder that your <a href="[LINK]" target="_blank" style="color: #31b6d3; text-decoration: underline; font-weight: bold;">organization onboarding form</a> is still waiting to be completed.\n\nOnce it's submitted, we'll be able to move forward with setting up your AI Assistant and keep your onboarding on schedule.\n\nA couple of quick reminders:\n\n• Only <strong>one person</strong> should complete the form to avoid conflicting changes.\n• It takes about <strong>15 minutes</strong> to finish.\n\nIf you have any questions along the way, just let us know—we're happy to help!\n\nThanks,\n\n<strong>— The Voicify Team</strong>`;
    } else if (triggerId === 'telephony-reminder') {
        subject = `Action Required: Set up your Telephony for Voicify Assistant`;
        rawBody = `Hi there,\n\nThis is a friendly reminder that one of the final steps before launching your Voicify AI Assistant is coordinating with your phone provider.\n\nTo ensure everything is ready for your go-live date, please contact your phone provider as soon as possible to:\n\n• Confirm you have (or can add) a second business phone number that rings at your office.\n• Discuss forwarding your main business number to your Voicify AI Assistant on your scheduled go-live date.\n• Understand any lead times or scheduling requirements to complete these changes.\n\nYou can use the email below as a starting point:\n\n---\n\nHello,\n\nWe are preparing to launch a new AI phone assistant for our business and need to make a few updates to our phone system.\n\nWe need assistance with:\n\n• Confirming we have (or adding) a second business phone number that rings at our office.\n• Planning call forwarding so our main business number forwards to the AI assistant on our scheduled go-live date.\n\nPlease let us know what is required on our end, any associated costs, and how much advance notice you need to complete these changes.\n\nThank you!\n\n---\n\nGetting this process started early helps prevent last-minute delays and keeps your Voicify launch on schedule. Because some phone providers require advance notice to make these changes, we recommend contacting them as soon as possible.\n\nBest,\n\nThe Voicify Team`;
    } else if (triggerId === 'org-submitted') {
        subject = `Voicify Organization Onboarding Submitted Successfully!`;
        rawBody = `Hi there,\n\nThank you for completing your organization onboarding! Our engineering team is now reviewing your requirements.\n\nWe will reach out shortly with instructions for location-level onboarding.\n\nBest,\nThe Voicify Team`;
    } else if (triggerId === 'loc-launched') {
        subject = `Set up your Voicify Voice Assistant for ${clientName}`;
        rawBody = `Hi there,\n\nIt's time to complete ${clientName}'s <a href="[LINK LIST]" target="_blank" style="color: #31b6d3; text-decoration: underline; font-weight: bold;">onboarding form</a>.\n\nA few quick things before you get started:\n\n• Only one person should complete the form to avoid overwriting each other's changes.\n• It takes about 20 minutes to finish.\n• Please complete it by [DATE].\n\nTaking a few extra minutes to provide as much detail as possible now will help us stay on track and make testing and launch go much more smoothly.\n\nThanks! We can't wait to get your AI Assistant up and running.\n\n— The Voicify Team`;
    } else if (triggerId === 'loc-reminder') {
        subject = `Friendly Reminder: Complete Onboarding for ${clientName}`;
        rawBody = `Hi there,\n\nJust a friendly reminder to complete the ${clientName} <a href="[LINK]" target="_blank" style="color: #31b6d3; text-decoration: underline; font-weight: bold;">onboarding form</a>. Doing so will prevent testing delays. Thanks in advance!\n\n— The Voicify Team`;
    } else if (triggerId === 'loc-submitted') {
        subject = `Location Onboarding Form Submitted for ${clientName}`;
        rawBody = `Hi there,\n\nWe have received your onboarding submission for ${clientName}!\n\nWe are preparing your environment and will notify you when automated testing is ready to begin.\n\nBest,\nThe Voicify Team`;
    } else if (triggerId === 'testing-ready') {
        subject = `Your assistant is ready for testing - ${clientName}`;
        rawBody = `Hi there,\n\nGreat news! We've finished our internal engineering review and automated QA for ${clientName}, and your AI Assistant is now ready for you to test.\n\nPlease <a href="[LINK]" target="_blank" style="color: #31b6d3; text-decoration: underline; font-weight: bold;">click here to begin testing</a>.\n\nA couple of quick notes before you get started:\n\n• Please assign one person to complete the testing form so feedback stays organized and nothing gets overwritten.\n• Be sure to test real-world scenarios you'd expect your callers to have and leave feedback directly in the form.\n\nOnce you've submitted your testing feedback, our team will review it and make any final adjustments needed before launch.\n\nThanks! We're excited to help you get your AI Assistant live.\n\n— The Voicify Team`;
    } else if (triggerId === 'phase1-feedback-submitted') {
        subject = `Round 1 Feedback Submitted for ${clientName}`;
        rawBody = `Hi there,\n\nThank you for completing Round 1 testing for ${clientName}! We have received your feedback and our engineering team is actively working on resolving the reported issues.\n\nWe will notify you as soon as Phase 2 is ready for review.\n\nBest,\nThe Voicify Team`;
    } else if (triggerId === 'phase2-testing-ready') {
        subject = `Phase 2 Testing is Ready - ${clientName}`;
        rawBody = `Hi there,\n\nWe've made updates based on the testing feedback you provided for <strong>${clientName}</strong>. Thanks for helping us improve your AI Assistant!\n\nPlease use the link below to retest the updated experience and verify that everything is working as expected.\n\nIf you notice anything else during testing, you can submit additional feedback using the same testing form.\n\nThanks again—we appreciate your help getting everything ready for launch!\n\n<strong>— The Voicify Team</strong>`;
    } else if (triggerId === 'phase2-feedback-submitted') {
        subject = `Round 2 Feedback Submitted for ${clientName}`;
        rawBody = `Hi there,\n\nThank you for submitting Round 2 feedback for ${clientName}. Our team is finalizing the adjustments for launch!\n\nBest,\nThe Voicify Team`;
    } else if (triggerId === 'launch-ready') {
        subject = `Your assistant is ready to launch! - ${clientName}`;
        rawBody = `Hi there,\n\nCongratulations! Your AI Assistant for <strong>${clientName}</strong> has successfully completed testing and is ready to go live.\n\nBefore your scheduled launch on <strong>${goLiveDate}</strong>, please take a moment to confirm that your call forwarding is configured and ready to route calls to your AI Assistant.\n\n<strong>Launching on a different date?</strong> Let us know and we'll update it.\n\nWe're excited to help you launch! If you have any last-minute questions before go-live, we're here to help.\n\nThanks,\n\n<strong>— The Voicify Team</strong>`;
    } else if (triggerId === 'go-live-reminder') {
        subject = `Go-Live Reminder: ${clientName} transitioning to Production`;
        rawBody = `Hi there,\n\nThis is a reminder that the voice assistant for ${clientName} is scheduled to go live on ${goLiveDate}.\n\nPlease ensure your team is ready for the transition.\n\nBest,\nThe Voicify Team`;
    } else if (triggerId === 'go-live') {
        subject = `Your Assistant is now LIVE! - ${clientName}`;
        rawBody = `Hi there,\n\nGreat news! Your AI Assistant for ${clientName} is now LIVE and ready to answer customer calls.\n\nYou can now access insights, including conversation data and transcripts, through the Voicify <a href="https://spark.voicify.com/" target="_blank" style="color: #31b6d3; text-decoration: underline; font-weight: bold;">Spark</a> dashboard. If you haven't received an invite yet, please reach out to a member of our team and we'll get you set up.\n\nAs your AI Assistant starts handling conversations, our team will continue to monitor its performance and make improvements as needed.\n\nCongratulations on your launch!\n\n— The Voicify Team`;
    } else if (triggerId === 'outbound-launched') {
        subject = `Set up your Outbound Calling campaigns for ${clientName}`;
        rawBody = `Hi there,\n\nWe are ready to configure the AI Outbound Calling campaigns for ${clientName}!\n\nOutbound Calling allows your Smart AI Assistant to automatically call patients to confirm upcoming appointments, schedule overdue hygiene appointments, and reschedule missed visits.\n\nTo configure these campaigns, please complete the standalone <a href="[LINK]" target="_blank" style="color: #31b6d3; text-decoration: underline; font-weight: bold;">outbound setup form</a>.\n\nA few quick notes:\n• This form should be completed by an office manager or practice administrator\n• It takes approximately 10 minutes to complete\n\nIf you have any questions, please contact our support team.\n\n— The Voicify Team`;
    } else if (triggerId === 'outbound-reminder') {
        subject = `Friendly Reminder: Complete Outbound Setup for ${clientName}`;
        rawBody = `Hi there,\n\nJust a friendly reminder to complete the <a href="[LINK]" target="_blank" style="color: #31b6d3; text-decoration: underline; font-weight: bold;">Outbound Calling campaigns setup form</a> for ${clientName}.\n\nPlease complete this as soon as possible so we can proceed with preparing your campaigns.\n\nBest,\nThe Voicify Team`;
    }

    const body = rawBody
        .replace(/Hi there,/g, greeting)
        .replace(/\[LINK\]/g, link)
        .replace(/\[LINK LIST\]/g, link)
        .replace(/\[DATE\]/g, deadlineDate);

    return { subject, body, link };
};

const getTestingStepCountForApplication = (application: LocationApplication, progress?: OnboardingProgressModel | null): number => {
    const answers = getOnboardingProgressAnswers(progress);
    return getTestingStepCountForFlow(detectFlowType(application), answers);
};

const isTestingProgressComplete = (progress?: OnboardingProgressModel | null): boolean => {
    return !!getTestingProgressState(progress).completedAt;
};

interface TestingRoundSubmission {
    submittedAt?: string;
    feedbackCount: number;
}

export const getTestingRoundSubmission = (
    progress: OnboardingProgressModel | null | undefined,
    roundIndex: number
): TestingRoundSubmission | null => {
    const testingState = getTestingProgressState(progress) as any;
    const rounds = Array.isArray(testingState?.rounds) ? testingState.rounds : [];
    const resolvedRound = rounds[roundIndex];

    if (resolvedRound && typeof resolvedRound === 'object') {
        const feedback = resolvedRound.feedback && typeof resolvedRound.feedback === 'object'
            ? resolvedRound.feedback
            : {};
        return {
            submittedAt: typeof resolvedRound.completedAt === 'string' ? resolvedRound.completedAt : undefined,
            feedbackCount: Object.keys(feedback).filter(stepId => typeof feedback[stepId] === 'string' && feedback[stepId].trim()).length
        };
    }

    // A round is submitted by the client before it is added to `rounds`, which
    // happens only after an internal user resolves it and opens the next round.
    if (roundIndex === rounds.length && typeof testingState?.completedAt === 'string' && testingState.completedAt) {
        const feedback = testingState.stepFeedbackByStepId && typeof testingState.stepFeedbackByStepId === 'object'
            ? testingState.stepFeedbackByStepId
            : {};
        return {
            submittedAt: testingState.completedAt,
            feedbackCount: Object.keys(feedback).filter(stepId => typeof feedback[stepId] === 'string' && feedback[stepId].trim()).length
        };
    }

    return null;
};

const getDisplayedTestingPercentComplete = (application: LocationApplication, progress?: OnboardingProgressModel | null): number => {
    const testingState = getTestingProgressState(progress);
    if (testingState.completedAt) {
        return 100;
    }

    return Math.round(((testingState.completedStepIds?.length || 0) / Math.max(getTestingStepCountForApplication(application, progress), 1)) * 100);
};

const getDisplayedPercentComplete = (application: LocationApplication, progress?: OnboardingProgressModel | null): number => {
    if (getOnboardingProgressPhase(progress) === 'testing') {
        return getDisplayedTestingPercentComplete(application, progress);
    }
    return Math.max(0, Math.min(progress?.percentComplete || 0, 100));
};

export const getDisplayedOnboardingPercentComplete = (progress?: OnboardingProgressModel | null): number => {
    const onboardingSnapshot = parseOnboardingProgressDetails(progress?.detailsJson).onboardingSnapshot;
    const isComplete = !!progress?.isComplete || !!onboardingSnapshot?.isComplete;

    if (isComplete) {
        return 100;
    }

    const percentComplete = onboardingSnapshot?.percentComplete ?? progress?.percentComplete ?? 0;
    return Math.max(0, Math.min(percentComplete, 100));
};

export const shouldDisplayOnboardingProgress = (percentComplete: number): boolean => {
    return percentComplete > 0 && percentComplete < 100;
};

export const getOnboardingResponseStatus = (
    progress?: OnboardingProgressModel | null,
    hasResponses = false
): 'Not Started' | 'In Progress' | 'Complete' => {
    const onboardingSnapshot = parseOnboardingProgressDetails(progress?.detailsJson).onboardingSnapshot;

    if (progress?.isComplete || onboardingSnapshot?.isComplete) {
        return 'Complete';
    }

    const percentComplete = onboardingSnapshot?.percentComplete ?? progress?.percentComplete ?? 0;
    if (percentComplete > 0 || hasResponses) {
        return 'In Progress';
    }

    return 'Not Started';
};

const isAtLeastOneFieldComplete = (progressState?: ProgressState | null): boolean => {
    if (!progressState) return false;
    return (
        (progressState.data?.percentComplete || 0) > 0 ||
        (progressState.summary?.answerCount || 0) > 0 ||
        (progressState.summary?.responseSections || []).some(sec => (sec.items || []).length > 0)
    );
};

const getProgressStatusLabel = (progress?: OnboardingProgressModel | null): string => {
    if (!progress) {
        return 'Not started';
    }
    if (getOnboardingProgressPhase(progress) === 'testing') {
        return isTestingProgressComplete(progress) ? 'Testing complete' : 'Testing mode';
    }
    if (progress.isComplete) {
        return 'Complete';
    }
    if ((progress.percentComplete || 0) > 0) {
        return 'In progress';
    }
    return 'Not started';
};

const getLocationTimelineStatus = (
    hasApiKey: boolean,
    emailSent: boolean,
    percentComplete: number,
    isComplete: boolean,
    progress: OnboardingProgressModel | null | undefined
): string => {
    if (!progress) {
        if (hasApiKey || emailSent) return 'Invited';
        return 'Not Invited';
    }

    const phase = getOnboardingProgressPhase(progress);
    const testingState = getTestingProgressState(progress) as any;
    const rounds = testingState?.rounds || [];
    const completedAt = testingState?.completedAt;

    if (completedAt) {
        return 'Testing Complete';
    }

    if (phase === 'testing') {
        const details = parseOnboardingProgressDetails(progress?.detailsJson);
        const emailTriggers = details.emailTriggers || {};
        if (emailTriggers['go-live']?.status === 'Sent') {
            return 'Go Live';
        }
        if (emailTriggers['launch-ready']?.status === 'Sent') {
            return 'Launch Ready';
        }
        if (rounds.length >= 2) {
            return 'Round 2 Resolved';
        }
        if (emailTriggers['phase2-testing-ready']?.status === 'Sent') {
            return 'Round 2 Testing';
        }
        if (rounds.length >= 1) {
            return 'Round 1 Resolved';
        }
        if (emailTriggers['testing-ready']?.status === 'Sent' || testingState.emailSentAt) {
            return 'Round 1 Testing';
        }
        return 'Automated QA';
    }

    if (isComplete) {
        return 'Onboarding Submitted';
    }

    if (percentComplete > 0) {
        return 'In Progress';
    }

    if (hasApiKey || emailSent) {
        return 'Invited';
    }

    return 'Not Invited';
};

const isLocationLaunchTimelineComplete = (
    app: LocationApplication | ApplicationModel,
    progress: OnboardingProgressModel | null | undefined,
    commonApp: ApplicationModel | null | undefined,
    commonAppProgress: OnboardingProgressModel | null | undefined,
    isOrgOnboardingComplete: boolean
): boolean => {
    if (!progress) return false;

    // Check Organization triggers and status
    const orgProgress = commonAppProgress;
    const orgDetails = parseOnboardingProgressDetails(orgProgress?.detailsJson);
    const orgBypassedSteps = orgDetails.bypassedSteps || [];
    const orgTriggers = orgDetails.emailTriggers || {};
    const isOrgComplete = !!orgProgress?.isComplete
        || orgBypassedSteps.includes('org-submitted')
        || orgTriggers['org-submitted']?.status === 'Skipped';

    if (orgTriggers['org-launched']?.status !== 'Sent' && orgTriggers['org-launched']?.status !== 'Skipped') return false;
    if (orgTriggers['org-reminder']?.status !== 'Sent' && orgTriggers['org-reminder']?.status !== 'Skipped' && !isOrgComplete) return false;
    if (orgTriggers['telephony-reminder']?.status !== 'Sent' && orgTriggers['telephony-reminder']?.status !== 'Skipped' && !isOrgComplete) return false;
    if (!isOrgComplete) return false;

    // Check Location triggers and status
    const locProgress = progress;
    const locDetails = parseOnboardingProgressDetails(locProgress?.detailsJson);
    const locBypassedSteps = locDetails.bypassedSteps || [];
    const locTriggers = locDetails.emailTriggers || {};
    const isLocComplete = !!locProgress?.isComplete
        || locBypassedSteps.includes('loc-submitted')
        || locTriggers['loc-submitted']?.status === 'Skipped';

    if (locTriggers['loc-launched']?.status !== 'Sent' && locTriggers['loc-launched']?.status !== 'Skipped') return false;
    if (locTriggers['loc-reminder']?.status !== 'Sent' && locTriggers['loc-reminder']?.status !== 'Skipped' && !isLocComplete) return false;
    if (!isLocComplete) return false;

    // Check Testing phase
    const testingState = getTestingProgressState(locProgress) as any;
    const rounds = testingState?.rounds || [];
    const hasTestingStarted = !!testingState.startedAt || rounds.length > 0 || isTestingProgressComplete(locProgress);

    if (!isOrgOnboardingComplete) return false;
    if (!locDetails?.isAutomatedTestComplete
        && !hasTestingStarted
        && locTriggers['testing-ready']?.status !== 'Sent'
        && locTriggers['testing-ready']?.status !== 'Skipped') return false;
    if (locTriggers['testing-ready']?.status !== 'Sent' && locTriggers['testing-ready']?.status !== 'Skipped') return false;
    if (rounds.length === 0 && !isTestingRoundReviewSkipped(1, locBypassedSteps, locTriggers)) return false;
    if (locTriggers['phase2-testing-ready']?.status !== 'Sent' && locTriggers['phase2-testing-ready']?.status !== 'Skipped') return false;
    if (rounds.length < 2 && !isTestingRoundReviewSkipped(2, locBypassedSteps, locTriggers)) return false;
    if (locTriggers['launch-ready']?.status !== 'Sent' && locTriggers['launch-ready']?.status !== 'Skipped') return false;
    if (locTriggers['go-live']?.status !== 'Sent' && locTriggers['go-live']?.status !== 'Skipped') return false;

    return true;
};

const getLaunchReadinessTab = (
    app: LocationApplication | ApplicationModel,
    progress: OnboardingProgressModel | null | undefined,
    progressState: ProgressState | null | undefined,
    commonApp: ApplicationModel | null | undefined,
    commonAppProgress: OnboardingProgressModel | null | undefined,
    isOrgOnboardingComplete: boolean
): 'Not Started' | 'In Progress' | 'Complete' => {
    if (!progress) {
        return 'Not Started';
    }

    const isLocation = commonApp && app.id !== commonApp.id;

    if (isLocation) {
        // For locations, status is 'Complete' only when the whole launch timeline is complete
        const isComplete = isLocationLaunchTimelineComplete(app, progress, commonApp, commonAppProgress, isOrgOnboardingComplete);
        if (isComplete) {
            return 'Complete';
        }
    } else {
        // For organization, status is 'Complete' when organization onboarding form is complete
        const phase = getOnboardingProgressPhase(progress);
        const details = parseOnboardingProgressDetails(progress?.detailsJson);
        const onboardingComplete = progress.isComplete || details.onboardingSnapshot?.isComplete || phase === 'testing';
        
        if (onboardingComplete) {
            return 'Complete';
        }
    }

    const details = parseOnboardingProgressDetails(progress?.detailsJson);
    const emailTriggers = details.emailTriggers || {};
    const hasLaunched = emailTriggers['org-launched']?.status === 'Sent' || emailTriggers['loc-launched']?.status === 'Sent';

    const onboardingTouched = 
        (progress.percentComplete || 0) > 0 || 
        isAtLeastOneFieldComplete(progressState) ||
        hasLaunched;

    if (onboardingTouched) {
        return 'In Progress';
    }

    return 'Not Started';
};

const getClientTestingStatus = (
    progress: OnboardingProgressModel | null | undefined
): 'Not Started' | 'In Progress' | 'Complete' => {
    if (!progress) {
        return 'Not Started';
    }
    const phase = getOnboardingProgressPhase(progress);
    if (phase !== 'testing') {
        return 'Not Started';
    }
    
    const testingState = getTestingProgressState(progress);
    if (testingState.completedAt) {
        return 'Complete';
    }
    
    const hasCompletedSteps = !!testingState.completedStepIds && testingState.completedStepIds.length > 0;
    const hasFeedback = !!testingState.stepFeedbackByStepId && Object.keys(testingState.stepFeedbackByStepId).length > 0;
    const hasFeedbackHistory = !!testingState.stepFeedbackHistoryByStepId && Object.keys(testingState.stepFeedbackHistoryByStepId).length > 0;
    const hasCallerPhone = !!(testingState as any).callerPhoneLast4;
    
    if (hasCompletedSteps || hasFeedback || hasFeedbackHistory || hasCallerPhone) {
        return 'In Progress';
    }
    
    return 'Not Started';
};

const getStatusTooltipText = (status: string): string => {
    if (status === 'Not Started') return "Onboarding hasn't been initiated";
    if (status === 'In Progress') return "Onboarding in progress";
    if (status === 'Complete') return "Onboarding complete";
    return '';
};

const getTestingStatusTooltipText = (status: string): string => {
    if (status === 'Not Started') return "Testing hasn't been initiated";
    if (status === 'In Progress') return "Testing in progress";
    if (status === 'Complete') return "Testing complete";
    return '';
};

const getCtaButtonText = (triggerId: string): string => {
    if (triggerId === 'org-launched' || triggerId === 'org-reminder') return 'Complete Organization Onboarding';
    if (triggerId === 'telephony-reminder') return 'Access Onboarding Portal';
    if (triggerId === 'loc-launched') return 'Complete Location Onboarding';
    if (triggerId === 'loc-reminder') return 'Complete Onboarding Form';
    if (triggerId === 'testing-ready' || triggerId === 'phase2-testing-ready') return 'Start Assistant Testing';
    if (triggerId === 'outbound-launched' || triggerId === 'outbound-reminder') return 'Configure Outbound Campaigns';
    if (triggerId === 'go-live') return 'Open Spark';
    return 'Open Portal';
};

const getRequiredTimelineIndexForTrigger = (triggerId: string): number => {
    switch (triggerId) {
        case 'loc-launched': return 4;
        case 'loc-reminder': return 5;
        case 'testing-ready': return 8;
        case 'phase2-testing-ready': return 10;
        case 'launch-ready': return 12;
        case 'go-live': return 13;
        default: return 0;
    }
};

export const isTestingRoundReviewSkipped = (
    round: 1 | 2,
    bypassedSteps: string[],
    emailTriggers: Record<string, OnboardingEmailTriggerState | undefined>
): boolean => {
    const reviewStepKey = round === 1 ? 'round1-feedback' : 'round2-feedback';
    const invitationTriggerId = round === 1 ? 'testing-ready' : 'phase2-testing-ready';
    const feedbackTriggerId = round === 1 ? 'phase1-feedback-submitted' : 'phase2-feedback-submitted';

    return bypassedSteps.includes(reviewStepKey)
        || emailTriggers[invitationTriggerId]?.status === 'Skipped'
        || emailTriggers[feedbackTriggerId]?.status === 'Skipped';
};

const buildBrandedEmailHtml = (
    text: string,
    link: string,
    triggerId: string
): string => {
    if (!text) return '';

    let cleanedText = text
        .replace(/This is an automated email and replies are not monitored.*/gi, '')
        .replace(/This is an automated email from Voicify.*/gi, '')
        .replace(/If you need assistance, please contact techsupport@voicify\.com.*/gi, '')
        .trim();

    let normalized = cleanedText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const blocks = normalized.split(/\n\n+/);

    const shouldRenderCta = triggerId !== 'launch-ready';
    const buttonText = shouldRenderCta ? getCtaButtonText(triggerId) : '';
    const ctaLink = triggerId === 'go-live' ? 'https://spark.voicify.com/' : link;
    
    const buttonHtml = `
      <table border="0" cellspacing="0" cellpadding="0" style="margin: 25px 0;" align="left">
        <tr>
          <td align="center" bgcolor="#31b6d3" style="border-radius: 6px; padding: 14px 28px;">
            <a href="${ctaLink}" target="_blank" style="font-size: 18px; font-family: Arial, Helvetica, sans-serif; font-weight: bold; color: #ffffff; text-decoration: none; display: inline-block;">
              ${buttonText}
            </a>
          </td>
        </tr>
      </table>
      <div style="clear: both; font-size: 14px; color: #94a3b8; margin-top: 8px; margin-bottom: 25px; word-break: break-all; line-height: 1.4; font-family: Arial, Helvetica, sans-serif; text-align: left;">
        If the button above does not work, please copy and paste this URL into your browser:<br />
        <a href="${ctaLink}" target="_blank" style="color: #31b6d3; text-decoration: underline; word-break: break-all; word-wrap: break-word; overflow-wrap: break-word;">${ctaLink}</a>
      </div>
    `.trim();

    let hasRenderedButton = false;

    const formattedBlocks = blocks.map(block => {
        const lines = block.split('\n');
        
        const isList = lines.every(line => {
            const trimmed = line.trim();
            return !trimmed || trimmed.startsWith('•') || trimmed.startsWith('*') || trimmed.startsWith('-');
        });

        if (isList) {
            const listItems = lines.map(line => {
                const trimmed = line.trim();
                if (!trimmed) return '';
                const content = trimmed.substring(1).trim();
                return `
                  <tr style="vertical-align: top;">
                    <td style="width: 20px; font-size: 18px; color: #31b6d3; font-weight: bold; padding-bottom: 10px; font-family: Arial, Helvetica, sans-serif; text-align: left;">•</td>
                    <td style="font-size: 18px; line-height: 1.6; color: #334155; font-family: Arial, Helvetica, sans-serif; padding-bottom: 10px; text-align: left; word-break: break-word; word-wrap: break-word; overflow-wrap: break-word;">${content}</td>
                  </tr>
                `.trim();
            }).filter(Boolean).join('');

            return `<table border="0" cellpadding="0" cellspacing="0" width="100%" style="margin: 0 0 16px 0; table-layout: fixed;">${listItems}</table>`;
        }

        let blockText = lines.join('<br />');

        if (blockText.includes('[LINK]') || blockText.includes('[LINK LIST]') || blockText.includes('[LINK_LIST]')) {
            hasRenderedButton = true;
            blockText = blockText.replace(/\[LINK\]|\[LINK LIST\]|\[LINK_LIST\]/g, shouldRenderCta ? buttonHtml : '');
            return `<div style="margin-bottom: 16px; font-size: 18px; line-height: 1.6; color: #334155; font-family: Arial, Helvetica, sans-serif; text-align: left; word-break: break-word; word-wrap: break-word; overflow-wrap: break-word;">${blockText}</div>`;
        }

        return `<p style="margin-top: 0; margin-bottom: 16px; font-size: 18px; line-height: 1.6; color: #334155; font-family: Arial, Helvetica, sans-serif; text-align: left; word-break: break-word; word-wrap: break-word; overflow-wrap: break-word;">${blockText}</p>`;
    });

    if (!hasRenderedButton && ctaLink && shouldRenderCta) {
        formattedBlocks.push(buttonHtml);
    }

    const finalContentHtml = formattedBlocks.join('');

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Voicify</title>
  <style>
    @media screen and (max-width: 600px) {
      .content-table {
        width: 100% !important;
        border-radius: 0px !important;
      }
      .content-padding {
        padding: 30px 20px 24px 20px !important;
      }
      .logo-img {
        width: 150px !important;
      }
    }
  </style>
</head>
<body style="margin: 0; padding: 0; background-color: #f4f6f8; font-family: Arial, Helvetica, sans-serif; -webkit-font-smoothing: antialiased; -ms-text-size-adjust: 100%; -webkit-text-size-adjust: 100%;">
  <table border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f4f6f8; padding: 40px 0;">
    <tr>
      <td align="center">
        <!--[if mso]>
        <table align="center" border="0" cellspacing="0" cellpadding="0" width="600">
        <tr>
        <td align="center" valign="top" width="600">
        <![endif]-->
        <table class="content-table" border="0" cellpadding="0" cellspacing="0" width="600" style="width: 600px; max-width: 100%; table-layout: fixed; background-color: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; border-top: 4px solid #31b6d3; box-shadow: 0 4px 12px rgba(0,0,0,0.05); border-collapse: separate;">
          <tr>
            <td class="content-padding" style="padding: 40px 40px 30px 40px;">
              <!-- Header Logo -->
              <table border="0" cellpadding="0" cellspacing="0" width="100%">
                <tr>
                  <td align="center" style="padding-bottom: 30px;">
                    <img class="logo-img" src="https://voicify-prod-files.s3.amazonaws.com/images/voicify_logo_horizontal_CMYK.png" alt="Voicify" width="180" style="display: block; margin: 0 auto; border: 0; outline: none; height: auto;" />
                  </td>
                </tr>
              </table>
              
              <!-- Divider Line -->
              <table border="0" cellpadding="0" cellspacing="0" width="100%">
                <tr>
                  <td style="border-top: 1px solid #e2e8f0; padding-bottom: 25px;"></td>
                </tr>
              </table>

              <!-- Main Content -->
              <table border="0" cellpadding="0" cellspacing="0" width="100%" style="table-layout: fixed;">
                <tr>
                  <td style="color: #334155; font-size: 18px; line-height: 1.6; font-family: Arial, Helvetica, sans-serif; text-align: left; word-break: break-word; word-wrap: break-word; overflow-wrap: break-word;">
                    ${finalContentHtml}
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <!-- Footer section inside card -->
          <tr>
            <td style="background-color: #f8fafc; border-bottom-left-radius: 8px; border-bottom-right-radius: 8px; padding: 24px; border-top: 1px solid #e2e8f0; text-align: center; color: #64748b; font-size: 14px; line-height: 1.6; font-family: Arial, Helvetica, sans-serif;">
              Questions or need help? We're here to assist you.<br />
              For technical inquiries, please contact <a href="mailto:techsupport@voicify.com" style="color: #31b6d3; text-decoration: none; font-weight: bold;">techsupport@voicify.com</a>. For all other questions, feel free to reach out to <a href="mailto:support@voicify.com" style="color: #31b6d3; text-decoration: none; font-weight: bold;">support@voicify.com</a>.
            </td>
          </tr>
        </table>
        <!--[if mso]>
        </td>
        </tr>
        </table>
        <![endif]-->
      </td>
    </tr>
  </table>
</body>
</html>
    `.trim();
};

const buildTestingModeProgressModel = (
    application: LocationApplication,
    progress: OnboardingProgressModel | null | undefined,
    enabled: boolean,
    startNewRound: boolean = false
): OnboardingProgressModel => {
    const nowIso = new Date().toISOString();
    const existingDetails = parseOnboardingProgressDetails(progress?.detailsJson);
    const defaultTestingStepId = detectFlowType(application) === 'healthcare' ? 'greeting-office-info' : 'greeting';

    return {
        ...(progress || {}),
        applicationId: application.id,
        currentStep: progress?.currentStep || '',
        percentComplete: progress?.percentComplete || 0,
        isComplete: !!progress?.isComplete,
        lastUpdated: nowIso,
        detailsJson: serializeOnboardingProgressDetails({
            ...existingDetails,
            phase: enabled ? 'testing' : 'onboarding',
            testingEnabledAt: enabled ? (existingDetails.testingEnabledAt || nowIso) : existingDetails.testingEnabledAt,
            onboardingSnapshot: enabled
                ? {
                    currentStep: progress?.currentStep || existingDetails.onboardingSnapshot?.currentStep || '',
                    percentComplete: progress?.percentComplete || existingDetails.onboardingSnapshot?.percentComplete || 0,
                    isComplete: !!(progress?.isComplete ?? existingDetails.onboardingSnapshot?.isComplete)
                }
                : existingDetails.onboardingSnapshot,
            testing: (enabled
                ? {
                    ...existingDetails.testing,
                    currentStepId: startNewRound ? defaultTestingStepId : (existingDetails.testing?.currentStepId || defaultTestingStepId),
                    completedStepIds: startNewRound ? [] : (existingDetails.testing?.completedStepIds || []),
                    startedAt: startNewRound ? nowIso : (existingDetails.testing?.startedAt || nowIso),
                    completedAt: startNewRound ? undefined : existingDetails.testing?.completedAt,
                    stepFeedbackByStepId: startNewRound ? {} : existingDetails.testing?.stepFeedbackByStepId,
                    stepNoFeedbackByStepId: startNewRound ? {} : existingDetails.testing?.stepNoFeedbackByStepId
                }
                : existingDetails.testing) as any
        })
    };
};

const fontFamilyStack = `'Muli', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif`;

const LocalTabContainer = styled.div`
    display: flex;
    flex-direction: row;
    border-bottom: 1px solid #e2e8f0;
    gap: 8px;
    overflow-x: auto;
    width: 100%;
    padding-bottom: 1px;
    margin-bottom: 20px;
    font-family: ${fontFamilyStack};

    /* Webkit scrollbar styles for cleaner mobile scroll */
    &::-webkit-scrollbar {
        height: 4px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 2px;
    }
`;

const LocalTabButton = styled.button<{ isActive: boolean }>`
    background: transparent;
    border: none;
    border-bottom: 3px solid ${props => props.isActive ? '#0284c7' : 'transparent'};
    color: ${props => props.isActive ? '#0284c7' : '#64748b'};
    padding: 8px 16px;
    font-size: 13px;
    font-weight: ${props => props.isActive ? '700' : '600'};
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: -1px;
    font-family: ${fontFamilyStack};

    &:hover {
        color: #0284c7;
    }

    &:focus {
        outline: none;
    }
`;

const timelineButtonOverride = css`
    margin: 2px !important;
    padding: 0 12px !important;
    height: 28px !important;
    font-size: 12px !important;
    font-family: ${fontFamilyStack} !important;
`;

const disableLinkButtonStyle = css`
    background-color: #fee2e2 !important;
    background-image: none !important;
    color: #b91c1c !important;
    border: 1px solid #fca5a5 !important;
    font-family: ${fontFamilyStack} !important;
    
    &:hover {
        background-color: #fecaca !important;
        color: #b91c1c !important;
        border-color: #fca5a5 !important;
    }

    &:focus {
        background-color: #fca5a5 !important;
        color: #ffffff !important;
    }

    &:disabled {
        background-color: #fca5a5 !important;
        color: #ffffff !important;
        opacity: 0.6 !important;
        cursor: not-allowed !important;
    }
`;

const modalPreviewLoaderStyle = css`
    position: relative;
    height: 450px;
    width: 100%;
`;

const LocationOnboardingAndTestingCardBody: React.FC<{
    app: LocationApplication;
    progress: OnboardingProgressModel | null | undefined;
    onUpdateProgress: (updatedProgress: OnboardingProgressModel) => void;
    isOrgOnboardingComplete: boolean;
    apiKeyContainer: ApiKeyContainer;
    memberContainer: MemberContainer;
    userContainer: UserContainer;
    organizationId?: string;
    apiKeyToDelete: ApiKeyModel | null;
    setApiKeyToDelete: (key: ApiKeyModel | null) => void;
    percentComplete: number;
    onSeeOnboardingResponses: () => void;
    onSeeOutboundResponses?: () => void;
    onSeeClientTestingFeedback: () => void;
    matchingApiKey: ApiKeyModel | null;
    onDisableLink: () => void;
    isLocAtLeastOneFieldComplete: boolean;
    testingStatus: string;
    onOpenOnboardingPreview: (app: LocationApplication, triggerId: string, progress: OnboardingProgressModel | null | undefined) => void;
    onUpdateEmailTrigger: (application: ApplicationModel, progress: OnboardingProgressModel | null | undefined, triggerId: string, action: 'send' | 'manual' | 'reset' | 'skip') => Promise<void>;
    commonApp: ApplicationModel | null | undefined;
    commonAppProgress: OnboardingProgressModel | null | undefined;
    onUpdateCommonProgress: (updatedProgress: OnboardingProgressModel) => void;
    onSaveProgressForApplication: (applicationId: string, progress: OnboardingProgressModel) => ReturnType<typeof updateOnboardingProgress>;
    setStatusModal: (modal: { title: string; details: React.ReactNode; customFooter?: React.ReactNode } | null) => void;
    onOpenLaunchDateModal: (app: LocationApplication, isLive: boolean, currentDate: string | null | undefined) => void;
    orgLocationSpecificFieldKeys?: string[];
    onBypassStep?: (appId: string, stepKey: string, isOrg: boolean, action: 'skip' | 'unskip') => Promise<void>;
}> = ({ app, progress, onUpdateProgress, isOrgOnboardingComplete, apiKeyContainer, memberContainer, userContainer, organizationId, apiKeyToDelete, setApiKeyToDelete, percentComplete, onSeeOnboardingResponses, onSeeOutboundResponses, matchingApiKey, onDisableLink, isLocAtLeastOneFieldComplete, testingStatus, onOpenOnboardingPreview, onUpdateEmailTrigger, commonApp, commonAppProgress, onUpdateCommonProgress, onSaveProgressForApplication, setStatusModal, onOpenLaunchDateModal, onSeeClientTestingFeedback, orgLocationSpecificFieldKeys, onBypassStep }) => {
    const onboardingPercentComplete = getDisplayedOnboardingPercentComplete(progress);
    const onboardingStatus = getOnboardingResponseStatus(progress, isLocAtLeastOneFieldComplete);
    const testCaseContainer = TestCaseContainer.useContainer();
    const [testCases, setTestCases] = useState<string[]>([]);
    const [isLoadingTestCases, setIsLoadingTestCases] = useState(false);
    const [isSavingAutomatedTest, setIsSavingAutomatedTest] = useState(false);
    const [localTab, setLocalTab] = useState<'onboarding' | 'client-feedback' | 'emails' | 'outbound'>('onboarding');
    const [emailSubTab, setEmailSubTab] = useState<'inbound' | 'outbound'>('inbound');
    
    const [manualExpanded, setManualExpanded] = useState<Record<string, boolean>>({});
    const [selectedEmailIds, setSelectedEmailIds] = useState<string[]>([]);
    const [isBulkUpdating, setIsBulkUpdating] = useState(false);
    
    const [outboundLink, setOutboundLink] = useState('');
    const [outboundLoading, setOutboundLoading] = useState(false);
    const [outboundCopied, setOutboundCopied] = useState(false);

    const handleBulkEmailAction = async (action: 'skip' | 'reset', targetIds?: string[]) => {
        setIsBulkUpdating(true);
        setErrorMessage('');
        setSuccessMessage('');
        const ids = targetIds || selectedEmailIds;
        if (ids.length === 0) {
            setIsBulkUpdating(false);
            return;
        }

        const res = await bulkUpdateOnboardingEmailTriggerStatus(app.id, {
            triggerIds: ids as OnboardingEmailTriggerId[],
            action
        });

        if (res.resultType === 'Ok' && res.data) {
            let finalProgress = res.data;
            if (action === 'reset' && (ids.includes('testing-ready') || ids.includes('phase2-testing-ready'))) {
                const updatedDetails = parseOnboardingProgressDetails(finalProgress?.detailsJson);
                const updatedTriggers = updatedDetails.emailTriggers || {};
                const isAnyTestingSent = updatedTriggers['testing-ready']?.status === 'Sent' || updatedTriggers['phase2-testing-ready']?.status === 'Sent';
                if (!isAnyTestingSent && updatedDetails.phase === 'testing') {
                    const revertedModel: OnboardingProgressModel = {
                        ...finalProgress,
                        detailsJson: serializeOnboardingProgressDetails({
                            ...updatedDetails,
                            phase: 'onboarding'
                        })
                    };
                    const saveRes = await updateOnboardingProgress(revertedModel);
                    if (saveRes.resultType === 'Ok') {
                        finalProgress = saveRes.data || revertedModel;
                    }
                }
            }
            onUpdateProgress(finalProgress);
            setSelectedEmailIds([]);
            setSuccessMessage(`Successfully ${action === 'skip' ? 'skipped' : 'reset'} email trigger status for selected items.`);
            setTimeout(() => setSuccessMessage(''), 4000);
        } else {
            setErrorMessage(res.errors?.[0] || `Failed to ${action} selected email triggers.`);
            setTimeout(() => setErrorMessage(''), 5000);
        }
        setIsBulkUpdating(false);
    };
    const upcomingTriggerId = useMemo(() => {
        const details = parseOnboardingProgressDetails(progress?.detailsJson);
        const emailTriggers = details.emailTriggers || {};
        const emails = [
            'loc-launched',
            'loc-reminder',
            'testing-ready',
            'phase2-testing-ready',
            'launch-ready',
            'go-live'
        ];
        const found = emails.find(id => {
            const status = emailTriggers[id]?.status;
            return status !== 'Sent' && status !== 'Skipped';
        });
        return found || 'loc-launched';
    }, [progress]);
    
    // QA fixes local states
    const testingState = useMemo(() => getTestingProgressState(progress) as any, [progress]);
    const [fixedStatus, setFixedStatus] = useState<Record<string, boolean>>(testingState.stepFixedStatusByStepId || {});
    const [fixNotes, setFixNotes] = useState<Record<string, string>>(testingState.stepFixNotesByStepId || {});
    const [isSavingQA, setIsSavingQA] = useState(false);

    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // Sync QA fixes on progress changes
    useEffect(() => {
        const ts = getTestingProgressState(progress) as any;
        setFixedStatus(ts.stepFixedStatusByStepId || {});
        setFixNotes(ts.stepFixNotesByStepId || {});
    }, [progress]);

    // Load test cases for this specific app
    useEffect(() => {
        if (app.id) {
            setIsLoadingTestCases(true);
            getTestCases(app.id, undefined, true).then(res => {
                setIsLoadingTestCases(false);
                if (res.resultType === 'Ok' && res.data) {
                    const appLevel = res.data.applicationTestCases ?? [];
                    const contentLevel = Object.values(res.data.contentItemTestCases ?? {}).flat();
                    setTestCases(appLevel.concat(contentLevel));
                }
            }).catch(() => setIsLoadingTestCases(false));
        }
    }, [app.id]);

    // Run Automated QA
    const handleRunQA = async () => {
        testCaseContainer.setApplication(app);
        await testCaseContainer.loadAppLevelTestCases(app.id);
        await testCaseContainer.runAppLevelTestCases();
    };

    // Save QA Fixes
    const handleSaveQAFixes = async () => {
        setIsSavingQA(true);
        const existingDetails = parseOnboardingProgressDetails(progress?.detailsJson);
        const updatedTesting = {
            ...(existingDetails.testing || {}),
            stepFixedStatusByStepId: fixedStatus,
            stepFixNotesByStepId: fixNotes
        };
        const updatedProgress = {
            ...(progress || {}),
            applicationId: app.id,
            detailsJson: serializeOnboardingProgressDetails({
                ...existingDetails,
                testing: updatedTesting
            })
        };
        const res = await updateOnboardingProgress(updatedProgress);
        setIsSavingQA(false);
        if (res.resultType === 'Ok') {
            onUpdateProgress(res.data || updatedProgress);
            setSuccessMessage('QA fixes and notes saved successfully!');
            setTimeout(() => setSuccessMessage(''), 3000);
        } else {
            setErrorMessage(res.errors?.[0] || 'Failed to save QA fixes.');
            setTimeout(() => setErrorMessage(''), 3000);
        }
    };

    const handleReopenOutboundOnboarding = async () => {
        setErrorMessage('');
        setSuccessMessage('');
        try {
            const existingDetails = parseOnboardingProgressDetails(progress?.detailsJson);
            const currentOutbound = existingDetails.outboundOnboarding || {};
            const updatedOutbound = {
                ...currentOutbound,
                status: 'In Progress',
                reopenedAt: new Date().toISOString()
            };
            const updatedProgress: OnboardingProgressModel = {
                ...(progress || {}),
                applicationId: app.id,
                detailsJson: serializeOnboardingProgressDetails({
                    ...existingDetails,
                    outboundOnboarding: updatedOutbound
                })
            };
            const res = await updateOnboardingProgress(updatedProgress);
            if (res.resultType === 'Ok') {
                onUpdateProgress(res.data || updatedProgress);
                setSuccessMessage('Outbound onboarding has been re-enabled for this location.');
                setTimeout(() => setSuccessMessage(''), 3500);
            } else {
                setErrorMessage(res.errors?.[0] || 'Failed to re-enable outbound onboarding.');
                setTimeout(() => setErrorMessage(''), 3500);
            }
        } catch (err) {
            setErrorMessage((err as any)?.message || 'Failed to re-enable outbound onboarding.');
            setTimeout(() => setErrorMessage(''), 3500);
        }
    };

    const handleCompleteAutomatedTests = async () => {
        setIsSavingAutomatedTest(true);
        setStatusModal({
            title: 'Run Automated Tests',
            details: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', color: '#334155' }}>
                    <p>Please run the automated test suite manually at <a href="https://d2j7asfzevril6.cloudfront.net/" target="_blank" rel="noopener noreferrer" style={{ color: '#0284c7', textDecoration: 'underline' }}>https://d2j7asfzevril6.cloudfront.net/</a>.</p>
                </div>
            ),
            customFooter: (
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
                    <Button
                        themes={['primary']}
                        text="Saving..."
                        type="button"
                        disabled={true}
                        onClick={() => {}}
                    />
                </div>
            )
        });
        const existingDetails = parseOnboardingProgressDetails(progress?.detailsJson);
        const updatedProgress = {
            ...(progress || {}),
            applicationId: app.id,
            detailsJson: serializeOnboardingProgressDetails({
                ...existingDetails,
                isAutomatedTestComplete: true
            })
        };
        const res = await updateOnboardingProgress(updatedProgress);
        setIsSavingAutomatedTest(false);
        if (res.resultType === 'Ok') {
            onUpdateProgress(res.data || updatedProgress);
            setStatusModal(null);
            setSuccessMessage('Automated tests completed!');
            setTimeout(() => setSuccessMessage(''), 3000);
        } else {
            setErrorMessage(res.errors?.[0] || 'Failed to complete automated tests.');
            setTimeout(() => setErrorMessage(''), 3000);
            setStatusModal({
                title: 'Run Automated Tests',
                details: (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', color: '#334155' }}>
                        <p>Please run the automated test suite manually at <a href="https://d2j7asfzevril6.cloudfront.net/" target="_blank" rel="noopener noreferrer" style={{ color: '#0284c7', textDecoration: 'underline' }}>https://d2j7asfzevril6.cloudfront.net/</a>.</p>
                    </div>
                ),
                customFooter: (
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
                        <Button
                            themes={['primary']}
                            text="I'm Done Testing"
                            type="button"
                            onClick={handleCompleteAutomatedTests}
                        />
                    </div>
                )
            });
        }
    };

    const updateTimelineTriggerStatuses = async (
        applicationId: string,
        startingProgress: OnboardingProgressModel,
        triggerIds: OnboardingEmailTriggerId[],
        action: 'skip' | 'reset'
    ): Promise<{ progress: OnboardingProgressModel; error?: string }> => {
        let latestProgress = startingProgress;

        for (const triggerId of triggerIds) {
            const result = await updateOnboardingEmailTriggerStatus(applicationId, triggerId, { action });
            if (result.resultType !== 'Ok') {
                return {
                    progress: latestProgress,
                    error: result.errors?.[0] || `Failed to ${action} timeline step.`
                };
            }

            const accumulatedDetails = parseOnboardingProgressDetails(latestProgress.detailsJson);
            const responseDetails = parseOnboardingProgressDetails(result.data?.detailsJson);
            const emailTriggers = {
                ...(responseDetails.emailTriggers || {}),
                ...(accumulatedDetails.emailTriggers || {})
            };
            emailTriggers[triggerId] = {
                ...(emailTriggers[triggerId] || {}),
                status: action === 'skip' ? 'Skipped' : 'Pending'
            };
            latestProgress = {
                ...latestProgress,
                ...(result.data || {}),
                detailsJson: serializeOnboardingProgressDetails({
                    ...responseDetails,
                    ...accumulatedDetails,
                    emailTriggers
                })
            };
        }

        return { progress: latestProgress };
    };

    const handleFastForwardToStep = async (targetIndex: number) => {
        setErrorMessage('');
        setSuccessMessage('');
        try {
            if (commonApp && targetIndex > 0) {
                const existingOrgDetails = parseOnboardingProgressDetails(commonAppProgress?.detailsJson);
                const existingOrgEmailTriggers = existingOrgDetails.emailTriggers || {};
                const orgTriggerIdsToSkip = ([
                    targetIndex > 0 ? 'org-launched' : null,
                    targetIndex > 1 ? 'org-reminder' : null,
                    targetIndex > 2 ? 'telephony-reminder' : null,
                    targetIndex >= 4 ? 'org-submitted' : null
                ].filter(Boolean) as OnboardingEmailTriggerId[]).filter(triggerId =>
                    existingOrgEmailTriggers[triggerId]?.status !== 'Sent'
                    && existingOrgEmailTriggers[triggerId]?.status !== 'Skipped'
                );
                let persistedOrgProgress = commonAppProgress || {
                    applicationId: commonApp.id,
                    percentComplete: 0,
                    isComplete: false
                };

                if (orgTriggerIdsToSkip.length > 0) {
                    const triggerResult = await updateTimelineTriggerStatuses(
                        commonApp.id,
                        persistedOrgProgress,
                        orgTriggerIdsToSkip,
                        'skip'
                    );
                    if (triggerResult.error) {
                        setErrorMessage(triggerResult.error);
                        return;
                    }
                    persistedOrgProgress = triggerResult.progress;
                }

                let persistedOrgDetails = parseOnboardingProgressDetails(persistedOrgProgress.detailsJson);
                const orgTimelineTriggerIndexes: Array<{ triggerId: OnboardingEmailTriggerId; index: number }> = [
                    { triggerId: 'org-launched', index: 0 },
                    { triggerId: 'org-reminder', index: 1 },
                    { triggerId: 'telephony-reminder', index: 2 },
                    { triggerId: 'org-submitted', index: 3 }
                ];
                const futureOrgTriggerIdsToReset = orgTimelineTriggerIndexes
                    .filter(({ triggerId, index }) =>
                        index >= targetIndex
                        && persistedOrgDetails.emailTriggers?.[triggerId]?.status === 'Skipped'
                    )
                    .map(({ triggerId }) => triggerId);
                if (futureOrgTriggerIdsToReset.length > 0) {
                    const resetResult = await updateTimelineTriggerStatuses(
                        commonApp.id,
                        persistedOrgProgress,
                        futureOrgTriggerIdsToReset,
                        'reset'
                    );
                    if (resetResult.error) {
                        setErrorMessage(resetResult.error);
                        return;
                    }
                    persistedOrgProgress = resetResult.progress;
                    persistedOrgDetails = parseOnboardingProgressDetails(persistedOrgProgress.detailsJson);
                }
                const updatedOrgEmailTriggers = { ...(persistedOrgDetails.emailTriggers || {}) };
                let orgBypassedSteps = (persistedOrgDetails.bypassedSteps || []).filter(step =>
                    step !== 'org-submitted' || targetIndex >= 4
                );
                if (targetIndex === 2 && updatedOrgEmailTriggers['telephony-reminder']?.status !== 'Sent') {
                    updatedOrgEmailTriggers['telephony-reminder'] = {
                        ...(updatedOrgEmailTriggers['telephony-reminder'] || {}),
                        status: 'Pending'
                    };
                }
                if (targetIndex >= 4 && !orgBypassedSteps.includes('org-submitted')) {
                    orgBypassedSteps.push('org-submitted');
                }

                const updatedOrgModel: OnboardingProgressModel = {
                    ...persistedOrgProgress,
                    applicationId: commonApp.id,
                    percentComplete: persistedOrgProgress.percentComplete || 0,
                    isComplete: !!persistedOrgProgress.isComplete,
                    lastUpdated: new Date().toISOString(),
                    detailsJson: serializeOnboardingProgressDetails({
                        ...persistedOrgDetails,
                        bypassedSteps: orgBypassedSteps,
                        emailTriggers: updatedOrgEmailTriggers
                    })
                };

                const orgResult = await onSaveProgressForApplication(commonApp.id, updatedOrgModel);
                if (orgResult.resultType !== 'Ok') {
                    setErrorMessage(orgResult.errors?.[0] || 'Failed to fast-forward timeline.');
                    return;
                }
                onUpdateCommonProgress(updatedOrgModel);
            }

            if (targetIndex <= 4) {
                setSuccessMessage(`Timeline fast-forwarded to Step ${targetIndex + 1}.`);
                setTimeout(() => setSuccessMessage(''), 4000);
                return;
            }

            const existingDetails = parseOnboardingProgressDetails(progress?.detailsJson);
            let bypassedSteps = [...(existingDetails.bypassedSteps || [])];
            const existingEmailTriggers = existingDetails.emailTriggers || {};
            const locationTriggerIdsToSkip = ([
                targetIndex > 4 ? 'loc-launched' : null,
                targetIndex > 5 ? 'loc-reminder' : null,
                targetIndex >= 7 ? 'loc-submitted' : null,
                targetIndex > 8 ? 'testing-ready' : null,
                targetIndex >= 10 ? 'phase1-feedback-submitted' : null,
                targetIndex > 10 ? 'phase2-testing-ready' : null,
                targetIndex >= 12 ? 'phase2-feedback-submitted' : null,
                targetIndex > 12 ? 'launch-ready' : null
            ].filter(Boolean) as OnboardingEmailTriggerId[]).filter(triggerId =>
                existingEmailTriggers[triggerId]?.status !== 'Sent'
                && existingEmailTriggers[triggerId]?.status !== 'Skipped'
            );
            let persistedProgress = progress || {
                applicationId: app.id,
                percentComplete: 0,
                isComplete: false
            };

            if (locationTriggerIdsToSkip.length > 0) {
                const triggerResult = await updateTimelineTriggerStatuses(
                    app.id,
                    persistedProgress,
                    locationTriggerIdsToSkip,
                    'skip'
                );
                if (triggerResult.error) {
                    setErrorMessage(triggerResult.error);
                    return;
                }
                persistedProgress = triggerResult.progress;
            }

            let persistedDetails = parseOnboardingProgressDetails(persistedProgress.detailsJson);
            const locationTimelineTriggerIndexes: Array<{ triggerId: OnboardingEmailTriggerId; index: number }> = [
                { triggerId: 'loc-launched', index: 4 },
                { triggerId: 'loc-reminder', index: 5 },
                { triggerId: 'loc-submitted', index: 6 },
                { triggerId: 'testing-ready', index: 8 },
                { triggerId: 'phase1-feedback-submitted', index: 9 },
                { triggerId: 'phase2-testing-ready', index: 10 },
                { triggerId: 'phase2-feedback-submitted', index: 11 },
                { triggerId: 'launch-ready', index: 12 },
                { triggerId: 'go-live', index: 13 }
            ];
            const futureTriggerIdsToReset = locationTimelineTriggerIndexes
                .filter(({ triggerId, index }) =>
                    index >= targetIndex
                    && persistedDetails.emailTriggers?.[triggerId]?.status === 'Skipped'
                )
                .map(({ triggerId }) => triggerId);
            if (futureTriggerIdsToReset.length > 0) {
                const resetResult = await updateTimelineTriggerStatuses(
                    app.id,
                    persistedProgress,
                    futureTriggerIdsToReset,
                    'reset'
                );
                if (resetResult.error) {
                    setErrorMessage(resetResult.error);
                    return;
                }
                persistedProgress = resetResult.progress;
                persistedDetails = parseOnboardingProgressDetails(persistedProgress.detailsJson);
            }

            bypassedSteps = (persistedDetails.bypassedSteps || []).filter(step =>
                (step !== 'loc-submitted' || targetIndex >= 7)
                && (step !== 'round1-feedback' || targetIndex >= 10)
                && (step !== 'round2-feedback' || targetIndex >= 12)
            );
            if (targetIndex >= 7 && !bypassedSteps.includes('loc-submitted')) {
                bypassedSteps.push('loc-submitted');
            }
            if (targetIndex >= 10 && !bypassedSteps.includes('round1-feedback')) {
                bypassedSteps.push('round1-feedback');
            }
            if (targetIndex >= 12 && !bypassedSteps.includes('round2-feedback')) {
                bypassedSteps.push('round2-feedback');
            }

            const isEnteringTesting = targetIndex >= 8;
            const updatedModel: OnboardingProgressModel = {
                ...persistedProgress,
                applicationId: app.id,
                percentComplete: persistedProgress.percentComplete || 0,
                isComplete: !!persistedProgress.isComplete,
                lastUpdated: new Date().toISOString(),
                detailsJson: serializeOnboardingProgressDetails({
                    ...persistedDetails,
                    bypassedSteps,
                    isAutomatedTestComplete: targetIndex >= 8 ? true : persistedDetails.isAutomatedTestComplete,
                    phase: isEnteringTesting ? 'testing' : 'onboarding',
                    emailTriggers: persistedDetails.emailTriggers || {}
                })
            };

            const res = await onSaveProgressForApplication(app.id, updatedModel);
            if (res.resultType === 'Ok') {
                onUpdateProgress(updatedModel);
                setSuccessMessage(`Timeline fast-forwarded to Step ${targetIndex + 1}.`);
                setTimeout(() => setSuccessMessage(''), 4000);
            } else {
                setErrorMessage(res.errors?.[0] || 'Failed to fast-forward timeline.');
            }
        } catch (err) {
            setErrorMessage('An unexpected error occurred while updating timeline.');
        }
    };

    const handleResetTimeline = async () => {
        setErrorMessage('');
        setSuccessMessage('');
        try {
            if (commonApp) {
                const existingOrgDetails = parseOnboardingProgressDetails(commonAppProgress?.detailsJson);
                const orgTriggerIdsToReset = Object.keys(existingOrgDetails.emailTriggers || {}).filter(key =>
                    existingOrgDetails.emailTriggers?.[key]?.status === 'Skipped'
                ) as OnboardingEmailTriggerId[];
                let persistedOrgProgress = commonAppProgress || {
                    applicationId: commonApp.id,
                    percentComplete: 0,
                    isComplete: false
                };
                if (orgTriggerIdsToReset.length > 0) {
                    const triggerResult = await updateTimelineTriggerStatuses(
                        commonApp.id,
                        persistedOrgProgress,
                        orgTriggerIdsToReset,
                        'reset'
                    );
                    if (triggerResult.error) {
                        setErrorMessage(triggerResult.error);
                        return;
                    }
                    persistedOrgProgress = triggerResult.progress;
                }
                const persistedOrgDetails = parseOnboardingProgressDetails(persistedOrgProgress.detailsJson);

                const updatedOrgModel: OnboardingProgressModel = {
                    ...persistedOrgProgress,
                    applicationId: commonApp.id,
                    percentComplete: persistedOrgProgress.percentComplete || 0,
                    isComplete: !!persistedOrgProgress.isComplete,
                    lastUpdated: new Date().toISOString(),
                    detailsJson: serializeOnboardingProgressDetails({
                        ...persistedOrgDetails,
                        bypassedSteps: (persistedOrgDetails.bypassedSteps || []).filter(step => step !== 'org-submitted')
                    })
                };

                const orgResult = await onSaveProgressForApplication(commonApp.id, updatedOrgModel);
                if (orgResult.resultType !== 'Ok') {
                    setErrorMessage(orgResult.errors?.[0] || 'Failed to reset timeline.');
                    return;
                }
                onUpdateCommonProgress(orgResult.data || updatedOrgModel);
            }

            const existingDetails = parseOnboardingProgressDetails(progress?.detailsJson);
            const locationTriggerIdsToReset = Object.keys(existingDetails.emailTriggers || {}).filter(key =>
                existingDetails.emailTriggers?.[key]?.status === 'Skipped'
            ) as OnboardingEmailTriggerId[];
            let persistedProgress = progress || {
                applicationId: app.id,
                percentComplete: 0,
                isComplete: false
            };
            if (locationTriggerIdsToReset.length > 0) {
                const triggerResult = await updateTimelineTriggerStatuses(
                    app.id,
                    persistedProgress,
                    locationTriggerIdsToReset,
                    'reset'
                );
                if (triggerResult.error) {
                    setErrorMessage(triggerResult.error);
                    return;
                }
                persistedProgress = triggerResult.progress;
            }
            const persistedDetails = parseOnboardingProgressDetails(persistedProgress.detailsJson);

            const updatedModel: OnboardingProgressModel = {
                ...persistedProgress,
                applicationId: app.id,
                percentComplete: persistedProgress.percentComplete || 0,
                isComplete: !!persistedProgress.isComplete,
                lastUpdated: new Date().toISOString(),
                detailsJson: serializeOnboardingProgressDetails({
                    ...persistedDetails,
                    bypassedSteps: [],
                    isAutomatedTestComplete: false,
                    phase: 'onboarding'
                })
            };

            const res = await onSaveProgressForApplication(app.id, updatedModel);
            if (res.resultType === 'Ok') {
                onUpdateProgress(res.data || updatedModel);
                setSuccessMessage('Timeline reset back to initial step.');
                setTimeout(() => setSuccessMessage(''), 4000);
            } else {
                setErrorMessage(res.errors?.[0] || 'Failed to reset timeline.');
            }
        } catch (err) {
            setErrorMessage('An unexpected error occurred while resetting timeline.');
        }
    };

    const timelineRef = useRef<HTMLDivElement>(null);

    const timelineData = useMemo(() => {
        const orgProgress = commonAppProgress;
        const orgPercent = commonApp ? getDisplayedPercentComplete(commonApp as unknown as LocationApplication, orgProgress) : 0;
        const orgDetails = parseOnboardingProgressDetails(orgProgress?.detailsJson);
        const orgBypassedSteps = orgDetails.bypassedSteps || [];
        const orgTriggers = orgDetails.emailTriggers || {};
        const isOrgComplete = !!orgProgress?.isComplete
            || orgBypassedSteps.includes('org-submitted')
            || orgTriggers['org-submitted']?.status === 'Skipped';

        const locApp = app;
        const locProgress = progress;
        const locDetails = parseOnboardingProgressDetails(locProgress?.detailsJson);
        const locBypassedSteps = locDetails.bypassedSteps || [];
        const locTriggers = locDetails.emailTriggers || {};
        const isLocComplete = !!locProgress?.isComplete
            || locBypassedSteps.includes('loc-submitted')
            || locTriggers['loc-submitted']?.status === 'Skipped';
        const locPercent = percentComplete;

        return {
            orgProgress,
            orgPercent,
            isOrgComplete,
            orgTriggers,
            locProgress,
            isLocComplete,
            locTriggers,
            locPercent,
            locApp,
            orgBypassedSteps,
            locBypassedSteps
        };
    }, [app, progress, percentComplete, commonApp, commonAppProgress]);

    const activeTimelineIndex = useMemo(() => {
        const { isOrgComplete, orgTriggers, isLocComplete, locTriggers, locProgress, locBypassedSteps } = timelineData;

        if (orgTriggers['org-launched']?.status !== 'Sent' && orgTriggers['org-launched']?.status !== 'Skipped') {
            return 0; // Org Invite is active
        }
        if (orgTriggers['org-reminder']?.status !== 'Sent' && orgTriggers['org-reminder']?.status !== 'Skipped' && !isOrgComplete) {
            return 1; // Org Reminder is active
        }
        if (orgTriggers['telephony-reminder']?.status !== 'Sent'
            && orgTriggers['telephony-reminder']?.status !== 'Skipped'
            && (!isOrgComplete || orgTriggers['telephony-reminder']?.status === 'Pending')) {
            return 2; // Telephony Setup is active
        }
        if (!isOrgComplete) {
            return 3; // Org Submitted phase is active
        }
        if (locTriggers['loc-launched']?.status !== 'Sent' && locTriggers['loc-launched']?.status !== 'Skipped') {
            return 4; // Location Invite is active
        }
        if (locTriggers['loc-reminder']?.status !== 'Sent' && locTriggers['loc-reminder']?.status !== 'Skipped' && !isLocComplete) {
            return 5; // Location Reminder is active
        }
        if (!isLocComplete) {
            return 6; // Location Submitted phase is active
        }

        // --- End of onboarding, start of testing! ---
        const testingState = getTestingProgressState(locProgress) as any;
        const rounds = testingState?.rounds || [];
        const hasTestingStarted = !!testingState.startedAt || rounds.length > 0 || isTestingProgressComplete(locProgress);
        const locDetails = parseOnboardingProgressDetails(locProgress?.detailsJson);

        if (!isOrgOnboardingComplete) {
            return 6; // Waiting for Org onboarding complete / Automated QA
        }
        if (!locDetails?.isAutomatedTestComplete
            && !hasTestingStarted
            && locTriggers['testing-ready']?.status !== 'Sent'
            && locTriggers['testing-ready']?.status !== 'Skipped') {
            return 7; // Run Automated Tests is active
        }
        if (locTriggers['testing-ready']?.status !== 'Sent' && locTriggers['testing-ready']?.status !== 'Skipped') {
            return 8; // Testing Ready invitation
        }
        if (rounds.length === 0 && !isTestingRoundReviewSkipped(1, locBypassedSteps, locTriggers)) {
            return 9; // Round 1 Feedback
        }
        if (locTriggers['phase2-testing-ready']?.status !== 'Sent' && locTriggers['phase2-testing-ready']?.status !== 'Skipped') {
            return 10; // Phase 2 Ready
        }
        if (rounds.length < 2 && !isTestingRoundReviewSkipped(2, locBypassedSteps, locTriggers)) {
            return 11; // Round 2 Feedback
        }
        if (locTriggers['launch-ready']?.status !== 'Sent' && locTriggers['launch-ready']?.status !== 'Skipped') {
            return 12; // Launch Ready
        }
        if (locTriggers['go-live']?.status !== 'Sent' && locTriggers['go-live']?.status !== 'Skipped') {
            return 13; // Go Live
        }
        return 14; // All complete!
    }, [timelineData, isOrgOnboardingComplete]);

    const scrollToNode = (idx: number) => {
        if (timelineRef.current) {
            const track = timelineRef.current.firstElementChild as HTMLElement;
            if (track && track.children && track.children[idx]) {
                const activeNode = track.children[idx] as HTMLElement;
                const wrapperWidth = timelineRef.current.clientWidth;
                if (wrapperWidth > 0) {
                    const nodeOffsetLeft = activeNode.offsetLeft;
                    const nodeWidth = activeNode.clientWidth;
                    timelineRef.current.scrollTo({
                        left: nodeOffsetLeft + nodeWidth / 2 - wrapperWidth / 2,
                        behavior: 'smooth'
                    });
                }
            }
        }
    };

    useEffect(() => {
        if (!timelineRef.current) return;

        const observer = new (window as any).ResizeObserver((entries: any[]) => {
            for (const entry of entries) {
                if (entry.contentRect.width > 0) {
                    const timer = setTimeout(() => {
                        scrollToNode(activeTimelineIndex);
                    }, 50);
                    return () => clearTimeout(timer);
                }
            }
        });

        observer.observe(timelineRef.current);
        return () => observer.disconnect();
    }, [activeTimelineIndex]);

    // Render Body Card Sections
    const isLive = !!app.isLive;
    const hasAnySkippedOrBypassed = (timelineData.orgBypassedSteps?.length || 0) > 0
        || Object.values(timelineData.orgTriggers || {}).some((t: any) => t?.status === 'Skipped')
        || (timelineData.locBypassedSteps?.length || 0) > 0
        || Object.values(timelineData.locTriggers || {}).some((t: any) => t?.status === 'Skipped');
    const activeLocalTab = isLive && localTab === 'emails' ? 'onboarding' : localTab;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            
            {isLive ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: '8px',
                    padding: '16px 20px',
                    marginBottom: '4px',
                    flexWrap: 'wrap',
                    gap: '12px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            backgroundColor: '#16a34a',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff',
                            flexShrink: 0
                        }}>
                            <UserLiveIcon size={18} color="#ffffff" />
                        </div>
                        <div>
                            <div style={{ fontSize: '15px', fontWeight: 700, color: '#166534' }}>
                                Location is Live in Production
                            </div>
                            <div style={{ fontSize: '13px', color: '#15803d' }}>
                                {app.launchDate ? `Launched on ${moment.utc(app.launchDate).format('MMMM D, YYYY')}` : 'Active and live in production.'}
                            </div>
                        </div>
                    </div>
                    <Button
                        themes={['secondary-small']}
                        text="Update Launch Date"
                        type="button"
                        onClick={() => onOpenLaunchDateModal(app, true, app.launchDate)}
                    />
                </div>
            ) : commonApp ? (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', flexWrap: 'wrap', gap: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <h4 style={{ fontSize: '15px', fontWeight: 'bold', color: '#2c3d52', margin: 0 }}>Launch Timeline</h4>
                            <span style={{ fontSize: '13px', color: '#64748b' }}>
                                (Step {activeTimelineIndex + 1} of 15: <strong style={{ color: '#1e293b' }}>{activeTimelineIndex < 14 ? 'In Progress' : 'Completed'}</strong>)
                            </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            {activeTimelineIndex < 8 && (
                                <Button
                                    themes={['secondary-small']}
                                    text="Skip to Testing"
                                    type="button"
                                    onClick={() => handleFastForwardToStep(8)}
                                />
                            )}
                            {activeTimelineIndex >= 8 && activeTimelineIndex < 12 && (
                                <Button
                                    themes={['secondary-small']}
                                    text="Skip to Launch Ready"
                                    type="button"
                                    onClick={() => handleFastForwardToStep(12)}
                                />
                            )}
                            {hasAnySkippedOrBypassed && (
                                <Button
                                    themes={['secondary-small']}
                                    text="Reset Timeline"
                                    type="button"
                                    onClick={handleResetTimeline}
                                />
                            )}
                        </div>
                    </div>
                    <HorizontalTimelineWrapper ref={timelineRef} style={{ padding: '24px', border: '1px solid #cbd5e1', borderBottom: '3px solid #cbd5e1', borderRadius: '8px', marginBottom: 0 }}>
                        <TimelineTrack>
                            {(() => {
                                const { orgProgress, orgPercent, isOrgComplete, orgTriggers, locProgress, isLocComplete, locTriggers, locPercent, locApp, orgBypassedSteps, locBypassedSteps } = timelineData;

                                const steps = [
                                    {
                                        label: 'Send Org Invite',
                                        type: 'manual_email' as const,
                                        isEmail: true,
                                        triggerId: 'org-launched',
                                        app: commonApp,
                                        progress: orgProgress
                                    },
                                    {
                                        label: 'Send Org Reminder',
                                        type: 'automated_email' as const,
                                        isEmail: true,
                                        triggerId: 'org-reminder',
                                        app: commonApp,
                                        progress: orgProgress
                                    },
                                    {
                                        label: 'Send Telephony Setup Reminder',
                                        type: 'automated_email' as const,
                                        isEmail: true,
                                        triggerId: 'telephony-reminder',
                                        app: commonApp,
                                        progress: orgProgress
                                    },
                                    {
                                        label: 'Track Org Submission',
                                        type: 'info_onboarding' as const,
                                        isEmail: false,
                                        title: 'Organization Submission Status',
                                        details: (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', color: '#334155' }}>
                                                <p><strong>Status:</strong> {isOrgComplete ? 'Completed' : 'In Progress'}</p>
                                                <p><strong>Completed:</strong> {orgPercent}%</p>
                                                {orgProgress?.lastUpdated && (
                                                    <p><strong>Last Updated:</strong> {moment(orgProgress.lastUpdated).format('MM/DD/YYYY [at] h:mm A')}</p>
                                                )}
                                                {orgBypassedSteps.includes('org-submitted') && (
                                                    <p style={{ color: '#f59e0b', fontWeight: 600 }}>• Step was bypassed manually.</p>
                                                )}
                                            </div>
                                        )
                                    },
                                    {
                                        label: 'Send Location Invite',
                                        type: 'manual_email' as const,
                                        isEmail: true,
                                        triggerId: 'loc-launched',
                                        app: locApp,
                                        progress: locProgress
                                    },
                                    {
                                        label: 'Send Location Reminder',
                                        type: 'automated_email' as const,
                                        isEmail: true,
                                        triggerId: 'loc-reminder',
                                        app: locApp,
                                        progress: locProgress
                                    },
                                    {
                                        label: 'Track Location Submission',
                                        type: 'info_onboarding' as const,
                                        isEmail: false,
                                        title: 'Location Submission Status',
                                        details: (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', color: '#334155' }}>
                                                <p><strong>Status:</strong> {isLocComplete ? 'Completed' : 'In Progress'}</p>
                                                <p><strong>Completed:</strong> {locPercent}%</p>
                                                {locProgress?.lastUpdated && (
                                                    <p><strong>Last Updated:</strong> {moment(locProgress.lastUpdated).format('MM/DD/YYYY [at] h:mm A')}</p>
                                                )}
                                                {locBypassedSteps.includes('loc-submitted') && (
                                                    <p style={{ color: '#f59e0b', fontWeight: 600 }}>• Step was bypassed manually.</p>
                                                )}
                                            </div>
                                        )
                                    },
                                    {
                                        label: 'Run Automated Tests',
                                        type: 'info_testing' as const,
                                        isEmail: false,
                                        title: 'Automated Testing Suite',
                                        details: (() => {
                                            const testingState = getTestingProgressState(locProgress) as any;
                                            const rounds = testingState?.rounds || [];
                                            const hasTestingStarted = !!testingState.startedAt || rounds.length > 0 || isTestingProgressComplete(locProgress);
                                            const isDone = parseOnboardingProgressDetails(locProgress?.detailsJson)?.isAutomatedTestComplete || hasTestingStarted;
                                            return (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', color: '#334155' }}>
                                                    <p><strong>Automated Tests:</strong> {isDone ? 'Completed' : 'Pending Review'}</p>
                                                    <p>Please run the automated test suite manually at <a href="https://d2j7asfzevril6.cloudfront.net/" target="_blank" rel="noopener noreferrer" style={{ color: '#0284c7', textDecoration: 'underline' }}>https://d2j7asfzevril6.cloudfront.net/</a>.</p>
                                                </div>
                                            );
                                        })()
                                    },
                                    {
                                        label: 'Send Testing Invitation',
                                        type: 'manual_email' as const,
                                        isEmail: true,
                                        triggerId: 'testing-ready',
                                        app: locApp,
                                        progress: locProgress
                                    },
                                    {
                                        label: 'Review Round 1 Feedback',
                                        type: 'info_testing' as const,
                                        isEmail: false,
                                        isSkipped: isTestingRoundReviewSkipped(1, locBypassedSteps, locTriggers),
                                        title: 'Round 1 Testing Feedback',
                                        details: (() => {
                                            const submission = getTestingRoundSubmission(locProgress, 0);
                                            return (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', color: '#334155' }}>
                                                    <p><strong>Round 1 Feedback Status:</strong> {submission ? 'Submitted' : 'Pending'}</p>
                                                    {submission && (
                                                        <>
                                                            {submission.submittedAt && <p><strong>Submitted On:</strong> {moment(submission.submittedAt).format('MM/DD/YYYY [at] h:mm A')}</p>}
                                                            <p><strong>Issues Logged:</strong> {submission.feedbackCount}</p>
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        })()
                                    },
                                    {
                                        label: 'Send Phase 2 Testing Invite',
                                        type: 'manual_email' as const,
                                        isEmail: true,
                                        triggerId: 'phase2-testing-ready',
                                        app: locApp,
                                        progress: locProgress
                                    },
                                    {
                                        label: 'Review Round 2 Feedback',
                                        type: 'info_testing' as const,
                                        isEmail: false,
                                        isSkipped: isTestingRoundReviewSkipped(2, locBypassedSteps, locTriggers),
                                        title: 'Round 2 Testing Feedback',
                                        details: (() => {
                                            const submission = getTestingRoundSubmission(locProgress, 1);
                                            return (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', color: '#334155' }}>
                                                    <p><strong>Round 2 Feedback Status:</strong> {submission ? 'Submitted' : 'Pending'}</p>
                                                    {submission && (
                                                        <>
                                                            {submission.submittedAt && <p><strong>Submitted On:</strong> {moment(submission.submittedAt).format('MM/DD/YYYY [at] h:mm A')}</p>}
                                                            <p><strong>Issues Logged:</strong> {submission.feedbackCount}</p>
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        })()
                                    },
                                    {
                                        label: 'Send Launch Ready Notification',
                                        type: 'manual_email' as const,
                                        isEmail: true,
                                        triggerId: 'launch-ready',
                                        app: locApp,
                                        progress: locProgress
                                    },
                                    {
                                        label: 'Send Go Live Notification',
                                        type: 'manual_email' as const,
                                        isEmail: true,
                                        triggerId: 'go-live',
                                        app: locApp,
                                        progress: locProgress
                                    },
                                     {
                                        label: app.isLive
                                            ? (app.launchDate ? `Go Live: ${moment.utc(app.launchDate).format('MM/DD/YYYY')}` : 'Go Live: TBD')
                                            : (app.targetLaunchDate ? `Target Launch: ${moment.utc(app.targetLaunchDate).format('MM/DD/YYYY')}` : 'Target Launch: TBD'),
                                        type: 'info_testing' as const,
                                        isEmail: false,
                                        isTarget: true,
                                        title: 'Target Step',
                                        details: null
                                    }
                                ];

                                return steps.map((step, idx) => {
                                    const isTarget = (step as any).isTarget;
                                    const isSkipped = !!(step as any).isSkipped;
                                    const status = idx < activeTimelineIndex ? 'past' : idx === activeTimelineIndex ? 'active' : 'future';

                                      const handleClick = () => {
                                          scrollToNode(idx);
                                          if (isSkipped) {
                                              setStatusModal({
                                                  title: step.title!,
                                                  details: (
                                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', color: '#334155' }}>
                                                          <p><strong>Status:</strong> Skipped</p>
                                                          <p style={{ color: '#64748b' }}>
                                                              This review is not required because its corresponding testing invitation was skipped.
                                                          </p>
                                                      </div>
                                                  )
                                              });
                                              return;
                                          }
                                          if (status === 'future') {
                                              setStatusModal({
                                                  title: `Jump to Step ${idx + 1}: ${step.label}`,
                                                  details: (
                                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', color: '#334155' }}>
                                                          <p>
                                                              Step <strong>{idx + 1} ({step.label})</strong> is currently locked because preceding launch steps have not been completed.
                                                          </p>
                                                          <p style={{ color: '#64748b' }}>
                                                              Fast-forwarding will mark preceding onboarding forms and automated QA as skipped/bypassed so you can start working on this step immediately.
                                                          </p>
                                                      </div>
                                                  ),
                                                  customFooter: (
                                                      <div style={{ display: 'flex', width: '100%', justifyContent: 'flex-end', gap: '8px' }}>
                                                          <Button
                                                              themes={['secondary']}
                                                              text="Cancel"
                                                              type="button"
                                                              onClick={() => setStatusModal(null)}
                                                          />
                                                          <Button
                                                              themes={['primary']}
                                                              text="Skip Steps & Jump Here"
                                                              type="button"
                                                              onClick={() => {
                                                                  setStatusModal(null);
                                                                  handleFastForwardToStep(idx);
                                                              }}
                                                          />
                                                      </div>
                                                  )
                                              });
                                              return;
                                          }
                                          if (isTarget) {
                                              onOpenLaunchDateModal(app, !!app.isLive, app.isLive ? app.launchDate : app.targetLaunchDate);
                                          } else if (step.isEmail) {
                                              onOpenOnboardingPreview(step.app as LocationApplication, step.triggerId!, step.progress);
                                            } else if (step.label === 'Run Automated Tests') {
                                                const testingState = getTestingProgressState(locProgress) as any;
                                                const rounds = testingState?.rounds || [];
                                                const hasTestingStarted = !!testingState.startedAt || rounds.length > 0 || isTestingProgressComplete(locProgress);
                                                setStatusModal({
                                                    title: step.title!,
                                                    details: step.details!,
                                                    customFooter: !(parseOnboardingProgressDetails(locProgress?.detailsJson)?.isAutomatedTestComplete || hasTestingStarted) ? (
                                                        <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
                                                           <Button
                                                               themes={['white', 'secondary']}
                                                               text="Skip Step"
                                                               type="button"
                                                               onClick={handleCompleteAutomatedTests}
                                                           />
                                                           <Button
                                                               themes={['primary']}
                                                               text="I'm Done Testing"
                                                               type="button"
                                                               onClick={handleCompleteAutomatedTests}
                                                           />
                                                       </div>
                                                   ) : undefined
                                               });
                                           } else if (step.label === 'Track Org Submission') {
                                               const isBypassed = orgBypassedSteps.includes('org-submitted')
                                                   || orgTriggers['org-submitted']?.status === 'Skipped';
                                               setStatusModal({
                                                   title: step.title!,
                                                   details: step.details!,
                                                   customFooter: isBypassed ? (
                                                       <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
                                                           <Button
                                                               themes={['primary']}
                                                               text="Unskip Step"
                                                               type="button"
                                                               onClick={() => onBypassStep?.(commonApp!.id, 'org-submitted', true, 'unskip')}
                                                           />
                                                       </div>
                                                   ) : (!orgProgress?.isComplete ? (
                                                       <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
                                                           <Button
                                                               themes={['white', 'secondary']}
                                                               text="Skip Step"
                                                               type="button"
                                                               onClick={() => onBypassStep?.(commonApp!.id, 'org-submitted', true, 'skip')}
                                                           />
                                                       </div>
                                                   ) : undefined)
                                               });
                                           } else if (step.label === 'Track Location Submission') {
                                               const isBypassed = locBypassedSteps.includes('loc-submitted')
                                                   || locTriggers['loc-submitted']?.status === 'Skipped';
                                               setStatusModal({
                                                   title: step.title!,
                                                   details: step.details!,
                                                   customFooter: isBypassed ? (
                                                       <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
                                                           <Button
                                                               themes={['primary']}
                                                               text="Unskip Step"
                                                               type="button"
                                                               onClick={() => onBypassStep?.(locApp.id, 'loc-submitted', false, 'unskip')}
                                                           />
                                                       </div>
                                                   ) : (!locProgress?.isComplete ? (
                                                       <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
                                                           <Button
                                                               themes={['white', 'secondary']}
                                                               text="Skip Step"
                                                               type="button"
                                                               onClick={() => onBypassStep?.(locApp.id, 'loc-submitted', false, 'skip')}
                                                           />
                                                       </div>
                                                   ) : undefined)
                                               });
                                           } else if (step.label === 'Review Round 1 Feedback') {
                                               const isBypassed = isTestingRoundReviewSkipped(1, locBypassedSteps, locTriggers);
                                               setStatusModal({
                                                   title: step.title!,
                                                   details: step.details!,
                                                   customFooter: isBypassed ? (
                                                       <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
                                                           <Button
                                                               themes={['primary']}
                                                               text="Unskip Step"
                                                               type="button"
                                                               onClick={() => onBypassStep?.(locApp.id, 'round1-feedback', false, 'unskip')}
                                                           />
                                                       </div>
                                                   ) : (!isTestingProgressComplete(locProgress) ? (
                                                       <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
                                                           <Button
                                                               themes={['white', 'secondary']}
                                                               text="Skip Step"
                                                               type="button"
                                                               onClick={() => onBypassStep?.(locApp.id, 'round1-feedback', false, 'skip')}
                                                           />
                                                       </div>
                                                   ) : undefined)
                                               });
                                           } else if (step.label === 'Review Round 2 Feedback') {
                                               const isBypassed = isTestingRoundReviewSkipped(2, locBypassedSteps, locTriggers);
                                               setStatusModal({
                                                   title: step.title!,
                                                   details: step.details!,
                                                   customFooter: isBypassed ? (
                                                       <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
                                                           <Button
                                                               themes={['primary']}
                                                               text="Unskip Step"
                                                               type="button"
                                                               onClick={() => onBypassStep?.(locApp.id, 'round2-feedback', false, 'unskip')}
                                                           />
                                                       </div>
                                                   ) : (!isTestingProgressComplete(locProgress) ? (
                                                       <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
                                                           <Button
                                                               themes={['white', 'secondary']}
                                                               text="Skip Step"
                                                               type="button"
                                                               onClick={() => onBypassStep?.(locApp.id, 'round2-feedback', false, 'skip')}
                                                           />
                                                       </div>
                                                   ) : undefined)
                                               });
                                           } else {
                                               setStatusModal({
                                                   title: step.title!,
                                                   details: step.details!
                                               });
                                           }
                                       };

                                      const tooltipText = isSkipped
                                          ? `${step.label} is skipped because its testing invitation was skipped.`
                                          : status === 'future'
                                          ? `Step ${idx + 1} (${step.label}) is locked. Click to fast-forward here.`
                                         : isTarget
                                             ? 'Update target launch date'
                                             : step.label;

                                     return (
                                         <TooltipWrapper key={step.label} text={tooltipText} place="top">
                                             <TimelineNode 
                                                 status={status} 
                                                 onClick={handleClick}
                                                 style={{
                                                     ...(isTarget ? { transform: 'scale(1.05)', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' } : {}),
                                                     ...(status === 'future' ? { cursor: 'pointer', opacity: 0.75 } : {})
                                                 }}
                                             >
                                                  <TimelineCircle
                                                      status={status}
                                                      style={isSkipped ? {
                                                          background: '#d97706',
                                                          borderColor: '#b45309',
                                                          color: '#ffffff'
                                                      } : isTarget ? {
                                                         background: status === 'past' ? '#16a34a' : status === 'active' ? '#0284c7' : '#f8fafc',
                                                         borderColor: status === 'past' ? '#15803d' : status === 'active' ? '#0369a1' : '#cbd5e1',
                                                         borderWidth: '2px',
                                                         borderStyle: 'solid',
                                                         boxShadow: '0 0 0 4px rgba(2, 132, 199, 0.15)'
                                                     } : undefined}
                                                 >
                                                     {isTarget ? (
                                                         <UserLiveIcon 
                                                             size={16} 
                                                             color={status === 'past' || status === 'active' ? '#ffffff' : '#324661'} 
                                                         />
                                                     ) : idx + 1}
                                                 </TimelineCircle>
                                                  <TimelineText status={status} style={isSkipped ? { color: '#92400e' } : isTarget ? { fontWeight: 'bold', color: '#1e293b' } : undefined}>
                                                      {step.label}
                                                  </TimelineText>
                                                   <TimelineBadge
                                                       type={isTarget ? 'info_testing' : step.type}
                                                       style={isSkipped
                                                           ? { backgroundColor: '#fffbeb', color: '#92400e', borderColor: '#f59e0b' }
                                                           : isTarget ? { backgroundColor: '#f0fdf4', color: '#166534', borderColor: '#bbf7d0' } : undefined}
                                                   >
                                                       {isSkipped ? 'Skipped' : isTarget ? 'Target' : (
                                                          <>
                                                              {step.type === 'manual_email' && 'Manual'}
                                                              {step.type === 'automated_email' && 'Auto'}
                                                              {step.type === 'info_onboarding' && 'Onboarding'}
                                                              {step.type === 'info_testing' && 'Testing'}
                                                          </>
                                                      )}
                                                  </TimelineBadge>
                                             </TimelineNode>
                                         </TooltipWrapper>
                                     );
                                 });
                             })()}
                         </TimelineTrack>
                     </HorizontalTimelineWrapper>
                 </>
             ) : null}

            <LocalTabContainer>
                <LocalTabButton isActive={activeLocalTab === 'onboarding'} onClick={() => setLocalTab('onboarding')} type="button">Onboarding Responses</LocalTabButton>
                <LocalTabButton isActive={activeLocalTab === 'client-feedback'} onClick={() => setLocalTab('client-feedback')} type="button">Client Testing Feedback</LocalTabButton>
                {!isLive && (
                    <LocalTabButton isActive={activeLocalTab === 'emails'} onClick={() => setLocalTab('emails')} type="button">Emails</LocalTabButton>
                )}
                {app.featureFlags?.some(flag => flag.id === OutboundCallFeatureFlagId) && (
                    <LocalTabButton isActive={activeLocalTab === 'outbound'} onClick={() => setLocalTab('outbound')} type="button">Outbound Onboarding</LocalTabButton>
                )}
            </LocalTabContainer>

            {errorMessage && (
                <div style={{ padding: '8px 12px', backgroundColor: '#ffebee', border: '1px solid #ffcdd2', borderRadius: '4px', color: '#c62828', fontSize: '13px' }}>
                    {errorMessage}
                </div>
            )}
            {successMessage && (
                <div style={{ padding: '8px 12px', backgroundColor: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: '4px', color: '#2e7d32', fontSize: '13px' }}>
                    {successMessage}
                </div>
            )}

            {activeLocalTab === 'onboarding' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #cbd5e1', flexWrap: 'wrap', gap: '12px' }}>
                        <div>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: '#475569', marginRight: '8px' }}>Status:</span>
                            <TooltipWrapper text={getStatusTooltipText(onboardingStatus)} place="top">
                                <StatusBadge variant={onboardingStatus}>{onboardingStatus}</StatusBadge>
                            </TooltipWrapper>
                        </div>
                        <InlineButtonGroup style={{ margin: 0, padding: 0, background: 'transparent', border: 'none' }}>
                            <Button
                                themes={['primary-small']}
                                text="See Onboarding Responses"
                                type="button"
                                disabled={!isLocAtLeastOneFieldComplete}
                                onClick={onSeeOnboardingResponses}
                            />
                                  {matchingApiKey && onboardingStatus !== 'Complete' && (
                                      <TooltipWrapper
                                          text="This removes the underlying organization API key and immediately invalidates any onboarding URLs that rely on it."
                                          place="top"
                                          maxWidth={300}
                                      >
                                          <Button
                                              themes={['secondary-small', 'destructive']}
                                              className={disableLinkButtonStyle}
                                              text="Disable Link"
                                              type="button"
                                              onClick={onDisableLink}
                                          />
                                      </TooltipWrapper>
                                  )}
                        </InlineButtonGroup>
                    </div>

                    {progress && shouldDisplayOnboardingProgress(onboardingPercentComplete) && (
                        <ProgressBarSection>
                            <ProgressBarHeader>
                                <ProgressBarLabel>Onboarding Completion Progress</ProgressBarLabel>
                                <ProgressBarPercent>{onboardingPercentComplete}%</ProgressBarPercent>
                            </ProgressBarHeader>
                            <ProgressBarTrack>
                                <ProgressBarFill percent={onboardingPercentComplete} />
                            </ProgressBarTrack>
                        </ProgressBarSection>
                    )}
                </div>
            )}





            {activeLocalTab === 'client-feedback' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #cbd5e1', flexWrap: 'wrap', gap: '12px' }}>
                        <div>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: '#475569', marginRight: '8px' }}>Status:</span>
                            <TooltipWrapper text={getTestingStatusTooltipText(testingStatus)} place="top">
                                <StatusBadge variant={testingStatus}>{testingStatus}</StatusBadge>
                            </TooltipWrapper>
                        </div>
                        <InlineButtonGroup style={{ margin: 0, padding: 0, background: 'transparent', border: 'none' }}>
                            <Button
                                themes={['primary-small']}
                                text="See Client Testing Feedback"
                                type="button"
                                onClick={onSeeClientTestingFeedback}
                            />
                        </InlineButtonGroup>
                    </div>
                </div>
            )}

            {!isLive && activeLocalTab === 'emails' && (() => {
                const hasOutboundCalling = app.featureFlags?.some(flag => flag.id === OutboundCallFeatureFlagId);
                const details = parseOnboardingProgressDetails(progress?.detailsJson);
                const emailTriggers = details.emailTriggers || {};
                const allActiveEmails = (hasOutboundCalling && emailSubTab === 'outbound')
                    ? outboundLocationEmailTriggers
                    : inboundLocationEmailTriggers;
                const activeEmailsList = getUnsentLocationEmailTriggers(allActiveEmails, emailTriggers, timelineData.isLocComplete);
                const activeTriggerIds = activeEmailsList.map(email => email.triggerId);

                const activeUpcomingTriggerId = (() => {
                    if (hasOutboundCalling && emailSubTab === 'outbound') {
                        const found = activeTriggerIds.find(id => {
                            const status = emailTriggers[id]?.status;
                            return status !== 'Sent' && status !== 'Skipped';
                        });
                        return found || 'outbound-launched';
                    }
                    return upcomingTriggerId;
                })();

                const isAllActiveSelected = activeTriggerIds.every(id => selectedEmailIds.includes(id)) && activeTriggerIds.length > 0;
                const activeSelectedIds = activeTriggerIds.filter(id => selectedEmailIds.includes(id));
                const activeSelectedCount = activeSelectedIds.length;
                const bulkUpdateTooltipText = isBulkUpdating ? 'Email queue changes are being saved.' : '';

                return (
                    <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '24px', backgroundColor: '#fcfdfd' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#2c3d52', marginBottom: '16px' }}>Email Queue</h3>
                        
                        {hasOutboundCalling && (
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setEmailSubTab('inbound');
                                        setSelectedEmailIds([]);
                                    }}
                                    style={{
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        border: 'none',
                                        background: emailSubTab === 'inbound' ? '#1E7EB9' : 'transparent',
                                        color: emailSubTab === 'inbound' ? '#ffffff' : '#475569',
                                        fontSize: '13px',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        transition: 'all 150ms ease'
                                    }}
                                >
                                    Inbound
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setEmailSubTab('outbound');
                                        setSelectedEmailIds([]);
                                    }}
                                    style={{
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        border: 'none',
                                        background: emailSubTab === 'outbound' ? '#1E7EB9' : 'transparent',
                                        color: emailSubTab === 'outbound' ? '#ffffff' : '#475569',
                                        fontSize: '13px',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        transition: 'all 150ms ease'
                                    }}
                                >
                                    Outbound
                                </button>
                            </div>
                        )}

                        {activeEmailsList.length > 0 ? (
                            <>
                        {/* Beautiful Top-Level Controls & Multiselect Actions */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: '#f1f5f9',
                            padding: '12px 16px',
                            borderRadius: '6px',
                            border: '1px solid #cbd5e1',
                            marginBottom: '20px',
                            flexWrap: 'wrap',
                            gap: '12px'
                        }}>
                            <>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input
                                            type="checkbox"
                                            checked={isAllActiveSelected}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedEmailIds(prev => {
                                                        const filtered = prev.filter(id => !activeTriggerIds.includes(id as OnboardingEmailTriggerId));
                                                        return [...filtered, ...activeTriggerIds];
                                                    });
                                                } else {
                                                    setSelectedEmailIds(prev => prev.filter(id => !activeTriggerIds.includes(id as OnboardingEmailTriggerId)));
                                                }
                                            }}
                                            ref={(el) => {
                                                if (el) {
                                                    el.indeterminate = activeSelectedCount > 0 && activeSelectedCount < activeTriggerIds.length;
                                                }
                                            }}
                                            style={{
                                                width: '16px',
                                                height: '16px',
                                                cursor: 'pointer',
                                                accentColor: '#0284c7'
                                            }}
                                        />
                                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                                            {activeSelectedCount > 0 ? `${activeSelectedCount} Selected` : "Select All"}
                                        </span>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {activeSelectedCount > 0 ? (
                                    <>
                                        <ConditionalTooltipWrapper showTooltip={isBulkUpdating} text={bulkUpdateTooltipText} place="top">
                                            <div>
                                                <Button
                                                    themes={['secondary-small']}
                                                    className={timelineButtonOverride}
                                                    text="Skip Selected"
                                                    type="button"
                                                    disabled={isBulkUpdating}
                                                    onClick={() => handleBulkEmailAction('skip', activeSelectedIds)}
                                                />
                                            </div>
                                        </ConditionalTooltipWrapper>
                                        <ConditionalTooltipWrapper showTooltip={isBulkUpdating} text={bulkUpdateTooltipText} place="top">
                                            <div>
                                                <Button
                                                    themes={['secondary-small']}
                                                    className={timelineButtonOverride}
                                                    text="Reset Selected"
                                                    type="button"
                                                    disabled={isBulkUpdating}
                                                    onClick={() => handleBulkEmailAction('reset', activeSelectedIds)}
                                                />
                                            </div>
                                        </ConditionalTooltipWrapper>
                                    </>
                                ) : (
                                    <>
                                        <ConditionalTooltipWrapper showTooltip={isBulkUpdating} text={bulkUpdateTooltipText} place="top">
                                            <div>
                                                <Button
                                                    themes={['secondary-small']}
                                                    className={timelineButtonOverride}
                                                    text="Skip All"
                                                    type="button"
                                                    disabled={isBulkUpdating}
                                                    onClick={() => handleBulkEmailAction('skip', activeTriggerIds)}
                                                />
                                            </div>
                                        </ConditionalTooltipWrapper>
                                        <ConditionalTooltipWrapper showTooltip={isBulkUpdating} text={bulkUpdateTooltipText} place="top">
                                            <div>
                                                <Button
                                                    themes={['secondary-small']}
                                                    className={timelineButtonOverride}
                                                    text="Unskip All"
                                                    type="button"
                                                    disabled={isBulkUpdating}
                                                    onClick={() => handleBulkEmailAction('reset', activeTriggerIds)}
                                                />
                                            </div>
                                        </ConditionalTooltipWrapper>
                                    </>
                                )}
                                    </div>
                            </>
                        </div>

                        <Timeline>
                            {activeEmailsList.map((email, idx, arr) => {
                                 const triggerState = emailTriggers[email.triggerId];
                                 const isSent = triggerState?.status === 'Sent';
                                 const isSkipped = triggerState?.status === 'Skipped';
                                 const sentAt = triggerState?.sentAt;
                                 const recipientEmail = triggerState?.recipientEmail;
                                 const isLocked = !isSent && !isSkipped && activeTimelineIndex < getRequiredTimelineIndexForTrigger(email.triggerId);

                                 const isExpanded = manualExpanded[email.triggerId] !== undefined
                                     ? manualExpanded[email.triggerId]
                                     : email.triggerId === activeUpcomingTriggerId;

                                 const toggleExpand = () => {
                                     setManualExpanded(prev => ({
                                         ...prev,
                                         [email.triggerId]: !isExpanded
                                     }));
                                 };

                                 return (
                                     <TimelineItem key={email.triggerId} isLast={idx === arr.length - 1}>
                                         <TimelineDot isSent={isSent} isSkipped={isSkipped} />
                                         <TimelineContent>
                                             <div 
                                                 style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', userSelect: 'none' }}
                                             >
                                                 <span style={{ display: 'inline-flex' }}>
                                                          <input
                                                              type="checkbox"
                                                              checked={selectedEmailIds.includes(email.triggerId)}
                                                              onChange={(e) => {
                                                                  if (e.target.checked) {
                                                                      setSelectedEmailIds(prev => [...prev, email.triggerId]);
                                                                 } else {
                                                                     setSelectedEmailIds(prev => prev.filter(id => id !== email.triggerId));
                                                                 }
                                                             }}
                                                              style={{
                                                                  width: '15px',
                                                                  height: '15px',
                                                                  cursor: 'pointer',
                                                                  accentColor: '#0284c7'
                                                              }}
                                                          />
                                                 </span>
                                                 <div 
                                                     style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', cursor: 'pointer', flexGrow: 1 }}
                                                     onClick={toggleExpand}
                                                 >
                                                     <TimelineLabel style={{ display: 'flex', alignItems: 'center', gap: '6px', color: isSkipped ? '#92400e' : isLocked ? '#94a3b8' : undefined }}>
                                                         {email.label}
                                                         <span style={{ fontSize: '12px', color: '#64748b' }}>
                                                             {isExpanded ? '▼' : '▶'}
                                                         </span>
                                                     </TimelineLabel>
                                                     <TimelineBadge type={email.isAutomated ? 'automated_email' : 'manual_email'}>
                                                         {email.isAutomated ? 'Auto' : 'Manual'}
                                                     </TimelineBadge>
                                                     {isSkipped && (
                                                         <SkippedEmailBadge aria-label={`${email.label} is skipped`}>
                                                             Skipped
                                                         </SkippedEmailBadge>
                                                     )}
                                                 </div>
                                             </div>
                                             {isExpanded && (
                                                 <>
                                                     <div style={{ fontSize: '12px', color: '#64748b', fontStyle: 'italic' }}>
                                                         {email.description}
                                                     </div>
                                                     {isSent && (() => {
                                                         const displaySentAt = triggerState?.firstSentAt || triggerState?.sentAt || sentAt;
                                                         const isResent = (triggerState?.sendCount && triggerState.sendCount > 1) || (triggerState?.firstSentAt && triggerState?.sentAt && triggerState.sentAt !== triggerState.firstSentAt);
                                                         return (
                                                             <TimelineMeta>
                                                                 ✓ {isResent ? 'Resent' : 'Sent'} on {moment(displaySentAt).format('MM/DD/YYYY [at] hh:mm A')}
                                                                 {recipientEmail ? ` to ${recipientEmail}` : ''}
                                                                 {isResent && triggerState?.sendCount ? ` (Resent ${triggerState.sendCount - 1} time${triggerState.sendCount - 1 === 1 ? '' : 's'})` : ''}
                                                             </TimelineMeta>
                                                         );
                                                     })()}
                                                     {isSkipped && (
                                                         <TimelineMeta style={{ color: '#f59e0b' }}>
                                                             {email.isAutomated ? 'Skipped from Automated Queue' : 'Skipped'}
                                                         </TimelineMeta>
                                                     )}
                                                      {isLocked && (
                                                          <TimelineMeta style={{ color: '#94a3b8' }}>
                                                              ● Sending locked (pending previous timeline steps)
                                                          </TimelineMeta>
                                                     )}
                                                     {!isSent && !isSkipped && !isLocked && (
                                                         <TimelineMeta style={{ color: '#64748b' }}>
                                                             ● Pending
                                                         </TimelineMeta>
                                                     )}
                                                     <div style={{ display: 'flex', gap: '4px', marginTop: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                                         {isSkipped ? (
                                                             <Button
                                                                 themes={['secondary-small']}
                                                                 className={timelineButtonOverride}
                                                                 text="Unskip"
                                                                 type="button"
                                                                 onClick={() => onUpdateEmailTrigger(app, progress, email.triggerId, 'reset')}
                                                             />
                                                         ) : (
                                                             <>
                                                                  {!isSent && (
                                                                     <Button
                                                                         themes={['secondary-small']}
                                                                         className={timelineButtonOverride}
                                                                         text="Skip"
                                                                         type="button"
                                                                         onClick={() => onUpdateEmailTrigger(app, progress, email.triggerId, 'skip')}
                                                                     />
                                                                  )}
                                                                 <ConditionalTooltipWrapper showTooltip={isLocked} text="Complete or skip preceding timeline steps to unlock sending this email." place="top">
                                                                     <div>
                                                                         <Button
                                                                             themes={['secondary-small']}
                                                                             className={timelineButtonOverride}
                                                                             text={isSent ? "Resend" : "Edit & Send"}
                                                                             type="button"
                                                                             disabled={isLocked}
                                                                             onClick={() => onOpenOnboardingPreview(app, email.triggerId, progress)}
                                                                         />
                                                                     </div>
                                                                 </ConditionalTooltipWrapper>
                                                                 {isSent && (
                                                                     <TimelineResetLink style={{ marginTop: 0 }} onClick={() => onUpdateEmailTrigger(app, progress, email.triggerId, 'reset')}>
                                                                         Reset Status
                                                                     </TimelineResetLink>
                                                                 )}
                                                             </>
                                                         )}
                                                     </div>
                                                 </>
                                             )}
                                         </TimelineContent>
                                     </TimelineItem>
                                 );
                            })}
                         </Timeline>
                            </>
                        ) : (
                            <div style={{ fontSize: '13px', color: '#64748b', padding: '8px 0' }}>
                                All emails have been sent.
                            </div>
                        )}
                    </div>
                );
            })()}

            {activeLocalTab === 'outbound' && (() => {
                const details = parseOnboardingProgressDetails(progress?.detailsJson);
                const outboundOnboarding = details.outboundOnboarding || {};
                const hasAnswers = !!outboundOnboarding.answers;
                const statusStr = outboundOnboarding.status || 'Not Started';

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #cbd5e1', flexWrap: 'wrap', gap: '12px' }}>
                            <div>
                                <span style={{ fontSize: '13px', fontWeight: 700, color: '#475569', marginRight: '8px' }}>Status:</span>
                                <TooltipWrapper text="Outbound Setup Progress Status" place="top">
                                    <StatusBadge variant={statusStr === 'Submitted' ? 'Complete' : statusStr === 'In Progress' ? 'In Progress' : 'Not Started'}>
                                        {statusStr}
                                    </StatusBadge>
                                </TooltipWrapper>
                                {outboundOnboarding.submitter && (
                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>
                                        By: <strong>{outboundOnboarding.submitter.firstName} {outboundOnboarding.submitter.lastName}</strong> ({outboundOnboarding.submitter.email})
                                    </div>
                                )}
                            </div>
                            <InlineButtonGroup style={{ margin: 0, padding: 0, background: 'transparent', border: 'none' }}>
                                {statusStr === 'Submitted' && (
                                    <TooltipWrapper text="Re-enable outbound onboarding so the client can update their campaign settings." place="top">
                                        <div>
                                            <Button
                                                themes={['secondary-small']}
                                                text="Re-enable Onboarding"
                                                type="button"
                                                onClick={handleReopenOutboundOnboarding}
                                            />
                                        </div>
                                    </TooltipWrapper>
                                )}
                                <Button
                                    themes={['primary-small']}
                                    text="See Outbound Responses"
                                    type="button"
                                    disabled={!hasAnswers}
                                    onClick={onSeeOutboundResponses}
                                />
                                {statusStr !== 'Submitted' && (
                                    <Button
                                        themes={['primary-small']}
                                        text="Share Outbound Onboarding"
                                        type="button"
                                        onClick={() => onOpenOnboardingPreview(
                                            app,
                                            statusStr === 'In Progress' ? 'outbound-reminder' : 'outbound-launched',
                                            progress
                                        )}
                                    />
                                )}
                            </InlineButtonGroup>
                        </div>

                        {outboundOnboarding.percentComplete !== undefined && (
                            <ProgressBarSection>
                                <ProgressBarHeader>
                                    <ProgressBarLabel>Outbound Onboarding Progress</ProgressBarLabel>
                                    <ProgressBarPercent>{outboundOnboarding.percentComplete}%</ProgressBarPercent>
                                </ProgressBarHeader>
                                <ProgressBarTrack>
                                    <ProgressBarFill percent={outboundOnboarding.percentComplete} />
                                </ProgressBarTrack>
                            </ProgressBarSection>
                        )}
                    </div>
                );
            })()}
        </div>
    );
};

const UserLiveIcon = ({ size = 20, color = 'currentColor', style, className }: { size?: number, color?: string, style?: React.CSSProperties, className?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
        <path d="M10 23V18" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M12 18V19.7C12.0005 20.8746 12.3485 22.0227 13 23C13.6515 22.0227 13.9995 20.8746 14 19.7V18" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M6 18V22C6 22.5523 6.44772 23 7 23H8" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M18 23H17C16.4477 23 16 22.5523 16 22V19C16 18.4477 16.4477 18 17 18H18" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M16 21H18" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M12 10.5C12.9665 10.5 13.75 9.7165 13.75 8.75C13.75 7.7835 12.9665 7 12 7C11.0335 7 10.25 7.7835 10.25 8.75C10.25 9.7165 11.0335 10.5 12 10.5Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M15.5 14C15.092 12.4001 13.6511 11.2805 12 11.2805C10.3489 11.2805 8.90797 12.4001 8.5 14H15.5Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M7.98101 10.975C6.45138 8.90774 6.73931 6.01732 8.64669 4.29244C10.5541 2.56757 13.4588 2.57082 15.3623 4.29997C17.2658 6.02911 17.5473 8.92017 16.013 10.984" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M6.5 12.327C4.24597 9.46067 4.5699 5.34407 7.24451 2.86556C9.91913 0.387062 14.0484 0.37695 16.7352 2.84232C19.4219 5.3077 19.766 9.42266 17.526 12.3" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const LockIconSvg = ({ size = 14, color = 'currentColor', style, className }: { size?: number, color?: string, style?: React.CSSProperties, className?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={style} className={className}>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
);

const limitConcurrency = async <T, R>(
    items: T[],
    limit: number,
    fn: (item: T) => Promise<R>
): Promise<R[]> => {
    const results: R[] = [];
    const executing = new Set<Promise<any>>();
    for (const item of items) {
        const p = Promise.resolve().then(() => fn(item));
        results.push(p as any);
        executing.add(p);
        const clean = () => executing.delete(p);
        p.then(clean, clean);
        if (executing.size >= limit) {
            await Promise.race(executing);
        }
    }
    return Promise.all(results);
};

const isAutomatedTrigger = (triggerId: string): boolean => {
    const automatedTriggers: string[] = ['org-reminder', 'loc-reminder', 'outbound-reminder', 'outbound-submitted'];
    return automatedTriggers.includes(triggerId);
};

const OnboardingLinksCard: React.FC<OnboardingLinksCardProps> = (props) => {
    const location = useLocation();
    const isSendingEmailRef = useRef(false);
    const onboardingProgressApiKeysRef = useRef<Record<string, string>>({});
    const automaticallyDeletingApiKeyIdsRef = useRef<Set<string>>(new Set());
    const [linkStateByAppId, setLinkStateByAppId] = useState<Record<string, LinkState>>({});
    const [progressStateByAppId, setProgressStateByAppId] = useState<Record<string, ProgressState>>({});
    const [commonAppProgressState, setCommonAppProgressState] = useState<ProgressState>({});
    const [commonAppLinkState, setCommonAppLinkState] = useState<LinkState>({});
    const [globalError, setGlobalError] = useState<string>('');
    const [globalSuccess, setGlobalSuccess] = useState<string>('');
    const [apiKeyToDelete, setApiKeyToDelete] = useState<ApiKeyModel>(null);
    const [progressDetailsDialog, setProgressDetailsDialog] = useState<ProgressDetailsDialogState>(null);
    const [testingFeedbackDialog, setTestingFeedbackDialog] = useState<TestingFeedbackDialogState>(null);
    const [outboundResponsesDialog, setOutboundResponsesDialog] = useState<{
        application: LocationApplication;
        progress?: OnboardingProgressModel | null;
        telephonyDeployments?: TelephonyDeploymentResponse[];
        loadingDeployments?: boolean;
    } | null>(null);
    const [telephonyDeploymentsByAppId, setTelephonyDeploymentsByAppId] = useState<Record<string, TelephonyDeploymentResponse[]>>({});
    const [deleteError, setDeleteError] = useState<string>('');
    const [appSearch, setAppSearch] = useState<string>('');
    const [copiedKey, setCopiedKey] = useState<string>(null);
    const [manualBypasses, setManualBypasses] = useState<Record<string, boolean>>({});
    const [activeOnboardingTab, setActiveOnboardingTab] = useState<'All' | 'Not Started' | 'In Progress' | 'Complete'>('All');
    const [isBulkUpdating, setIsBulkUpdating] = useState<boolean>(false);
    const [isProgressLoading, setIsProgressLoading] = useState<boolean>(true);
    const [isBulkEmailTriggerManagerOpen, setIsBulkEmailTriggerManagerOpen] = useState<boolean>(false);

    const [feedbackFixed, setFeedbackFixed] = useState<Record<string, boolean>>({});
    const [feedbackNotes, setFeedbackNotes] = useState<Record<string, string>>({});
    const [isSavingFeedback, setIsSavingFeedback] = useState(false);
    const [isResolvingRound, setIsResolvingRound] = useState(false);
    const [dialogError, setDialogError] = useState('');
    const [dialogSuccess, setDialogSuccess] = useState('');

    const locationApps = useMemo<LocationApplication[]>(() => {
        return (props.locationApps ?? [])
            .filter(isLocationApplication);
    }, [props.locationApps]);

    const isOrgOnboardingFormSubmitted = !!commonAppProgressState?.data?.isComplete;

    // Gating check for locations based on commonApp completed or shared configuration completed
    const isOrgOnboardingComplete = useMemo(() => {
        const commonAppAppliedTemplates = props.appliedTemplateContainer?.state.perApplicationAppliedTemplates?.find(
            appliedTemplate => appliedTemplate.applicationId === props.commonApp?.id
        );
        const hasIncompleteFormFields = commonAppAppliedTemplates?.appliedTemplates?.some(template => {
            const incompleteSections = props.appliedTemplateContainer?.getIncompleteSections(template) ?? [];
            return incompleteSections.length > 0;
        }) ?? true;
        const isSharedConfigComplete = !hasIncompleteFormFields && (commonAppAppliedTemplates?.appliedTemplates?.length ?? 0) > 0;
        const commonProgressDetails = parseOnboardingProgressDetails(commonAppProgressState.data?.detailsJson);
        const isOrgTimelineBypassed = commonProgressDetails.bypassedSteps?.includes('org-submitted')
            || commonProgressDetails.emailTriggers?.['org-submitted']?.status === 'Skipped';
        return !props.commonApp || isSharedConfigComplete || isOrgOnboardingFormSubmitted || isOrgTimelineBypassed;
    }, [props.commonApp, props.appliedTemplateContainer?.state.perApplicationAppliedTemplates, isOrgOnboardingFormSubmitted, commonAppProgressState.data?.detailsJson]);

    const isExistingClient = useMemo(() => {
        const hasLiveLocation = locationApps.some(app => !!app.isLive);
        const isAnyLocationStarted = locationApps.some(app => {
            const progressState = progressStateByAppId[app.id];
            const percent = getDisplayedPercentComplete(app, progressState?.data);
            return percent > 0 || isAtLeastOneFieldComplete(progressState);
        });
        return hasLiveLocation || isAnyLocationStarted;
    }, [locationApps, progressStateByAppId]);

    const areAllOnboardingLinksComplete = useMemo(() => {
        if (locationApps.length === 0) return false;
        return locationApps.every(app => {
            const progressState = progressStateByAppId[app.id] || {};
            const progress = progressState.data;
            const status = getLaunchReadinessTab(app, progress, progressState, props.commonApp, commonAppProgressState.data, isOrgOnboardingComplete);
            return status === 'Complete';
        });
    }, [locationApps, progressStateByAppId, props.commonApp, commonAppProgressState, isOrgOnboardingComplete]);

    const isOnboardingLoading = useMemo(() => {
        return isProgressLoading || 
               !!commonAppProgressState.loading || 
               (locationApps.length > 0 && locationApps.some(app => progressStateByAppId[app.id]?.loading));
    }, [isProgressLoading, commonAppProgressState.loading, locationApps, progressStateByAppId]);

    const isOrgAdmin = props.memberContainer.state.isOrgAdmin;

    useEffect(() => {
        if (!location.pathname.endsWith('/onboarding')) {
            return;
        }
        if (props.isLoading) {
            return;
        }
        if (!isOrgAdmin) {
            setProgressStateByAppId({});
            setCommonAppProgressState({});
            setIsProgressLoading(false);
            return;
        }
        loadApplicationProgress();
    }, [location.pathname, isOrgAdmin, props.commonApp?.id, locationApps.length, props.isLoading]);

    const orgLocationSpecificFieldKeys = useMemo(() => {
        const details = parseOnboardingProgressDetails(commonAppProgressState.data?.detailsJson);
        return details.locationSpecificFieldKeys || [];
    }, [commonAppProgressState.data?.detailsJson]);

    const [localAppOverrides, setLocalAppOverrides] = useState<Record<string, Partial<LocationApplication>>>({});
    const [launchDateModal, setLaunchDateModal] = useState<{
        app: LocationApplication;
        isLive: boolean;
        currentDate: string | null | undefined;
    } | null>(null);
    const [tempLaunchDateVal, setTempLaunchDateVal] = useState<string>('');

    const [forcedOpenLocations, setForcedOpenLocations] = useState<Record<string, boolean>>({});

    const handleGoToLocation = (locationId: string) => {
        setForcedOpenLocations(prev => ({
            ...prev,
            [locationId]: true
        }));
        setTimeout(() => {
            const element = document.getElementById(`location-section-${locationId}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 150);
    };

    const handleGoToOrganization = () => {
        setTimeout(() => {
            const element = document.getElementById('organization-onboarding-section');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 150);
    };

    const [selectedLocationId, setSelectedLocationId] = useState<string>('');
    const [statusModal, setStatusModal] = useState<{
        title: string;
        details: React.ReactNode;
        customFooter?: React.ReactNode;
    } | null>(null);

    useEffect(() => {
        if (props.locationApps?.length && !selectedLocationId) {
            setSelectedLocationId(props.locationApps[0].id);
        }
    }, [props.locationApps]);

    const [previewEmailModal, setPreviewEmailModal] = useState<{
        title: string;
        subject: string;
        body: string;
        triggerKey: string;
        app: LocationApplication;
        recipientEmail: string;
        recipientFirstName: string;
        link: string;
        progress?: OnboardingProgressModel | null | undefined;
        isLoadingPreview?: boolean;
    } | null>(null);
    const [sparkInviteApplication, setSparkInviteApplication] = useState<LocationApplication | null>(null);
    const [isManualEmail, setIsManualEmail] = useState<boolean>(false);

    const activeApp = useMemo(() => {
        return (props.locationApps?.find(x => x.id === selectedLocationId) || props.commonApp) as unknown as LocationApplication;
    }, [selectedLocationId, props.locationApps, props.commonApp]);

    const timelineData = useMemo(() => {
        const orgProgress = commonAppProgressState.data;
        const orgPercent = getDisplayedPercentComplete(props.commonApp as unknown as LocationApplication, orgProgress);
        const orgDetails = parseOnboardingProgressDetails(orgProgress?.detailsJson);
        const orgBypassedSteps = orgDetails.bypassedSteps || [];
        const orgTriggers = orgDetails.emailTriggers || {};
        const isOrgComplete = !!orgProgress?.isComplete
            || orgBypassedSteps.includes('org-submitted')
            || orgTriggers['org-submitted']?.status === 'Skipped';

        const locApp = activeApp;
        const locProgress = progressStateByAppId[locApp?.id]?.data;
        const locDetails = parseOnboardingProgressDetails(locProgress?.detailsJson);
        const locBypassedSteps = locDetails.bypassedSteps || [];
        const locTriggers = locDetails.emailTriggers || {};
        const isLocComplete = !!locProgress?.isComplete
            || locBypassedSteps.includes('loc-submitted')
            || locTriggers['loc-submitted']?.status === 'Skipped';
        const locPercent = getDisplayedPercentComplete(locApp, locProgress);

        return {
            orgProgress,
            orgPercent,
            isOrgComplete,
            orgTriggers,
            locProgress,
            isLocComplete,
            locTriggers,
            locPercent,
            locApp,
            orgBypassedSteps,
            locBypassedSteps
        };
    }, [activeApp, commonAppProgressState, progressStateByAppId, props.commonApp]);

    const getEmailTriggerButtonText = (
        triggerId: OnboardingEmailTriggerId, 
        emailTriggers: Record<string, OnboardingEmailTriggerState | undefined>
    ) => {
        const triggerState = emailTriggers[triggerId];
        const isSkipped = triggerState?.status === 'Skipped';
        const isSentManually = !!triggerState?.sentManually;
        const isSent = triggerState?.status === 'Sent';

        if (triggerId === 'loc-launched' || triggerId === 'org-launched') {
            if (isSkipped || isSentManually) {
                return 'Unskip and Send Onboarding';
            }
            if (isSent) {
                return 'Resend Onboarding';
            }
            return 'Share Onboarding';
        }

        if (triggerId === 'loc-reminder' || triggerId === 'org-reminder') {
            if (isSkipped || isSentManually) {
                return 'Unskip and Send Onboarding Reminder';
            }
            return 'Send Onboarding Reminder';
        }

        if (triggerId === 'telephony-reminder') {
            if (isSkipped || isSentManually) {
                return 'Unskip and Send Telephony Setup';
            }
            if (isSent) {
                return 'Resend Telephony Setup';
            }
            return 'Send Telephony Setup';
        }

        if (triggerId === 'testing-ready') {
            if (isSkipped || isSentManually) {
                return 'Unskip and Share Testing';
            }
            if (isSent) {
                return 'Resend Testing';
            }
            return 'Share Testing';
        }

        if (triggerId === 'phase2-testing-ready') {
            if (isSkipped || isSentManually) {
                return 'Unskip and Share Phase 2 Testing';
            }
            if (isSent) {
                return 'Resend Phase 2 Testing';
            }
            return 'Share Phase 2 Testing';
        }

        if (triggerId === 'launch-ready') {
            if (isSkipped || isSentManually) {
                return 'Unskip and Share Launch Notification';
            }
            if (isSent) {
                return 'Resend Launch Notification';
            }
            return 'Share Launch Notification';
        }

        if (triggerId === 'go-live') {
            if (isSkipped || isSentManually) {
                return 'Unskip and Share Go Live Notification';
            }
            if (isSent) {
                return 'Resend Go Live Notification';
            }
            return 'Share Go Live Notification';
        }

        if (triggerId === 'outbound-launched') {
            if (isSkipped || isSentManually) {
                return 'Unskip and Send Outbound Onboarding';
            }
            if (isSent) {
                return 'Resend Outbound Onboarding';
            }
            return 'Share Outbound Onboarding';
        }

        if (triggerId === 'outbound-reminder') {
            if (isSkipped || isSentManually) {
                return 'Unskip and Send Outbound Reminder';
            }
            return 'Send Outbound Reminder';
        }

        if (isSkipped || isSentManually) {
            return 'Unskip and Send Onboarding';
        }
        return 'Share Onboarding';
    };

    const getLocationActionItem = (loc: { app: LocationApplication; status: string; progress: OnboardingProgressModel | null | undefined; progressSummary?: any }) => {
        const locApp = loc.app;
        if (locApp.isLive) {
            const matchingApiKey = onboardingApiKeys.find(key => key.name === `Onboarding - ${locApp.name}` || key.name === `Testing - ${locApp.name}` || key.name?.includes(locApp.name));
            if (matchingApiKey) {
                return { 
                    prefix: 'Disable onboarding links for ', 
                    suffix: ' (location is live)', 
                    type: 'action_needed',
                    buttonText: 'Disable Link',
                    onClick: () => openDeleteModal(matchingApiKey)
                };
            }
            return null;
        }

        const orgProgress = commonAppProgressState.data;
        const orgDetails = parseOnboardingProgressDetails(orgProgress?.detailsJson);
        const orgBypassedSteps = orgDetails.bypassedSteps || [];
        const orgTriggers = orgDetails.emailTriggers || {};
        const isOrgComplete = !!orgProgress?.isComplete
            || orgBypassedSteps.includes('org-submitted')
            || orgTriggers['org-submitted']?.status === 'Skipped';

        const locProgress = loc.progress;
        const locDetails = parseOnboardingProgressDetails(locProgress?.detailsJson);
        const locBypassedSteps = locDetails.bypassedSteps || [];
        const locTriggers = locDetails.emailTriggers || {};
        const isLocComplete = !!locProgress?.isComplete
            || locBypassedSteps.includes('loc-submitted')
            || locTriggers['loc-submitted']?.status === 'Skipped';

        if (orgTriggers['org-launched']?.status !== 'Sent' && orgTriggers['org-launched']?.status !== 'Skipped') {
            return { 
                prefix: 'Send Organization Onboarding Invitation', 
                suffix: '', 
                type: 'pending',
                triggerId: 'org-launched',
                isOrgOnboardingInvitation: true,
                buttonText: getEmailTriggerButtonText('org-launched', orgTriggers),
                themes: ['primary-small' as const],
                onClick: () => handleOpenOnboardingPreview(props.commonApp as LocationApplication, 'org-launched', orgProgress)
            };
        }
        if (orgTriggers['org-reminder']?.status !== 'Sent' && orgTriggers['org-reminder']?.status !== 'Skipped' && !isOrgComplete) {
            return { 
                prefix: 'Send Organization Onboarding Reminder', 
                suffix: '', 
                type: 'pending',
                triggerId: 'org-reminder',
                buttonText: getEmailTriggerButtonText('org-reminder', orgTriggers),
                onClick: () => handleOpenOnboardingPreview(props.commonApp as LocationApplication, 'org-reminder', orgProgress)
            };
        }
        if (orgTriggers['telephony-reminder']?.status !== 'Sent'
            && orgTriggers['telephony-reminder']?.status !== 'Skipped'
            && (!isOrgComplete || orgTriggers['telephony-reminder']?.status === 'Pending')) {
            return { 
                prefix: 'Send Telephony setup reminder for ', 
                suffix: '', 
                type: 'pending',
                triggerId: 'telephony-reminder',
                buttonText: getEmailTriggerButtonText('telephony-reminder', orgTriggers),
                onClick: () => handleOpenOnboardingPreview(props.commonApp as LocationApplication, 'telephony-reminder', orgProgress)
            };
        }
        if (!isOrgComplete) {
            const isOrgStarted = isAtLeastOneFieldComplete(commonAppProgressState);
            if (!isOrgStarted) {
                return {
                    prefix: "Organization Onboarding: Client hasn't started the form yet",
                    suffix: '',
                    type: 'pending' as const,
                    buttonText: undefined,
                    onClick: undefined
                };
            }
            return { 
                prefix: 'Awaiting Organization Form Submission for ', 
                suffix: '', 
                type: 'pending',
                buttonText: 'Review Responses',
                onClick: () => openProgressDetails(props.commonApp!, commonAppProgressState.summary!, orgProgress)
            };
        }
        if (locTriggers['loc-launched']?.status !== 'Sent' && locTriggers['loc-launched']?.status !== 'Skipped') {
            return { 
                prefix: 'Send Onboarding Invitation for ', 
                suffix: '', 
                type: 'action_needed',
                triggerId: 'loc-launched',
                buttonText: getEmailTriggerButtonText('loc-launched', locTriggers),
                onClick: () => handleOpenOnboardingPreview(locApp, 'loc-launched', locProgress)
            };
        }
        if (locTriggers['loc-reminder']?.status !== 'Sent' && locTriggers['loc-reminder']?.status !== 'Skipped' && !isLocComplete) {
            return { 
                prefix: 'Send Onboarding Reminder for ', 
                suffix: '', 
                type: 'action_needed',
                triggerId: 'loc-reminder',
                buttonText: getEmailTriggerButtonText('loc-reminder', locTriggers),
                onClick: () => handleOpenOnboardingPreview(locApp, 'loc-reminder', locProgress)
            };
        }
        if (!isLocComplete) {
            return { 
                prefix: 'Awaiting Onboarding Form Submission for ', 
                suffix: '', 
                type: 'pending',
                buttonText: 'Review Responses',
                onClick: () => openProgressDetails(locApp, loc.progressSummary, locProgress)
            };
        }

        const testingState = getTestingProgressState(locProgress) as any;
        const rounds = testingState?.rounds || [];
        const hasTestingStarted = !!testingState.startedAt || rounds.length > 0 || isTestingProgressComplete(locProgress);

        if (!isOrgOnboardingComplete) {
            return { 
                prefix: 'Complete Shared Config & Run Automated QA for ', 
                suffix: '', 
                type: 'action_needed',
                buttonText: 'View Location',
                onClick: () => handleGoToLocation(locApp.id)
            };
        }
        if (!locDetails?.isAutomatedTestComplete
            && !hasTestingStarted
            && locTriggers['testing-ready']?.status !== 'Sent'
            && locTriggers['testing-ready']?.status !== 'Skipped') {
            return { 
                prefix: 'Run Automated Tests for ', 
                suffix: '', 
                type: 'action_needed',
                buttonText: 'Run Tests',
                onClick: () => handleGoToLocation(locApp.id)
            };
        }
        if (locTriggers['testing-ready']?.status !== 'Sent' && locTriggers['testing-ready']?.status !== 'Skipped') {
            return { 
                prefix: 'Send Testing Invitation for ', 
                suffix: '', 
                type: 'action_needed',
                triggerId: 'testing-ready',
                buttonText: getEmailTriggerButtonText('testing-ready', locTriggers),
                onClick: () => handleOpenOnboardingPreview(locApp, 'testing-ready', locProgress)
            };
        }
        if (rounds.length === 0 && !isTestingRoundReviewSkipped(1, locBypassedSteps, locTriggers)) {
            const isFeedbackComplete = isTestingProgressComplete(locProgress);
            return { 
                prefix: isFeedbackComplete ? 'Review Round 1 Feedback for ' : 'Awaiting Round 1 Testing Feedback for ', 
                suffix: '', 
                type: isFeedbackComplete ? 'action_needed' : 'pending',
                buttonText: isFeedbackComplete ? 'Review Feedback' : 'View Location',
                onClick: isFeedbackComplete ? () => openTestingFeedback(locApp, progressStateByAppId[locApp.id]?.summary!, locProgress) : () => handleGoToLocation(locApp.id)
            };
        }
        if (locTriggers['phase2-testing-ready']?.status !== 'Sent' && locTriggers['phase2-testing-ready']?.status !== 'Skipped') {
            return { 
                prefix: 'Send Phase 2 Testing Invitation for ', 
                suffix: '', 
                type: 'action_needed',
                triggerId: 'phase2-testing-ready',
                buttonText: getEmailTriggerButtonText('phase2-testing-ready', locTriggers),
                onClick: () => handleOpenOnboardingPreview(locApp, 'phase2-testing-ready', locProgress)
            };
        }
        if (rounds.length < 2 && !isTestingRoundReviewSkipped(2, locBypassedSteps, locTriggers)) {
            const isFeedbackComplete = isTestingProgressComplete(locProgress);
            return { 
                prefix: isFeedbackComplete ? 'Review Round 2 Feedback for ' : 'Awaiting Round 2 Testing Feedback for ', 
                suffix: '', 
                type: isFeedbackComplete ? 'action_needed' : 'pending',
                buttonText: isFeedbackComplete ? 'Review Feedback' : 'View Location',
                onClick: isFeedbackComplete ? () => openTestingFeedback(locApp, progressStateByAppId[locApp.id]?.summary!, locProgress) : () => handleGoToLocation(locApp.id)
            };
        }
        if (locTriggers['launch-ready']?.status !== 'Sent' && locTriggers['launch-ready']?.status !== 'Skipped') {
            return { 
                prefix: 'Send Launch Ready Notification for ', 
                suffix: '', 
                type: 'action_needed',
                triggerId: 'launch-ready',
                buttonText: getEmailTriggerButtonText('launch-ready', locTriggers),
                onClick: () => handleOpenOnboardingPreview(locApp, 'launch-ready', locProgress)
            };
        }
        if (locTriggers['go-live']?.status !== 'Sent' && locTriggers['go-live']?.status !== 'Skipped') {
            return { 
                prefix: 'Send Go Live Notification for ', 
                suffix: '', 
                type: 'action_needed',
                triggerId: 'go-live',
                buttonText: getEmailTriggerButtonText('go-live', locTriggers),
                onClick: () => handleOpenOnboardingPreview(locApp, 'go-live', locProgress)
            };
        }
        const matchingApiKey = onboardingApiKeys.find(key => key.name === `Onboarding - ${locApp.name}` || key.name === `Testing - ${locApp.name}` || key.name?.includes(locApp.name));
        if (locApp.isLive && matchingApiKey) {
            return { 
                prefix: 'Disable onboarding links for ', 
                suffix: ' (location is live)', 
                type: 'action_needed',
                buttonText: 'Disable Link',
                onClick: () => openDeleteModal(matchingApiKey)
            };
        }
        return { 
            prefix: 'Onboarding Complete & Live for ', 
            suffix: '!', 
            type: 'completed',
            buttonText: 'Update Date',
            onClick: () => handleOpenLaunchDateModal(locApp, !!locApp.isLive, locApp.isLive ? locApp.launchDate : locApp.targetLaunchDate)
        };
    };

    const getOrgActionItem = () => {
        if (!props.commonApp) return null;
        const orgProgress = commonAppProgressState.data;
        const orgDetails = parseOnboardingProgressDetails(orgProgress?.detailsJson);
        const orgBypassedSteps = orgDetails.bypassedSteps || [];
        const orgTriggers = orgDetails.emailTriggers || {};
        const isOrgComplete = !!orgProgress?.isComplete
            || orgBypassedSteps.includes('org-submitted')
            || orgTriggers['org-submitted']?.status === 'Skipped';

        if (orgTriggers['org-launched']?.status !== 'Sent' && orgTriggers['org-launched']?.status !== 'Skipped') {
            return { 
                prefix: 'Send Organization Onboarding Form for ',
                suffix: '', 
                type: 'action_needed',
                triggerId: 'org-launched',
                isOrgOnboardingInvitation: true,
                buttonText: getEmailTriggerButtonText('org-launched', orgTriggers),
                themes: ['primary-small' as const],
                disabled: isExistingClient,
                onClick: () => handleOpenOnboardingPreview(props.commonApp as LocationApplication, 'org-launched', orgProgress)
            };
        }
        if (orgTriggers['org-reminder']?.status !== 'Sent' && orgTriggers['org-reminder']?.status !== 'Skipped' && !isOrgComplete) {
            return { 
                prefix: 'Send Organization Onboarding Reminder', 
                suffix: '', 
                type: 'pending',
                triggerId: 'org-reminder',
                buttonText: getEmailTriggerButtonText('org-reminder', orgTriggers),
                disabled: isExistingClient,
                onClick: () => handleOpenOnboardingPreview(props.commonApp as LocationApplication, 'org-reminder', orgProgress)
            };
        }
        if (orgTriggers['telephony-reminder']?.status !== 'Sent' && orgTriggers['telephony-reminder']?.status !== 'Skipped' && !isOrgComplete) {
            return { 
                prefix: 'Send Telephony setup reminder for ', 
                suffix: '', 
                type: 'pending',
                triggerId: 'telephony-reminder',
                buttonText: getEmailTriggerButtonText('telephony-reminder', orgTriggers),
                disabled: isExistingClient,
                onClick: () => handleOpenOnboardingPreview(props.commonApp as LocationApplication, 'telephony-reminder', orgProgress)
            };
        }
        if (!isOrgComplete) {
            const isOrgStarted = isAtLeastOneFieldComplete(commonAppProgressState);
            if (!isOrgStarted) {
                return {
                    prefix: "Organization Onboarding: Client hasn't started the form yet",
                    suffix: '',
                    type: 'pending' as const,
                    buttonText: undefined,
                    onClick: undefined
                };
            }
            return { 
                prefix: 'Awaiting Organization Form Submission for ', 
                suffix: '', 
                type: 'pending',
                buttonText: 'Review Responses',
                onClick: () => openProgressDetails(props.commonApp!, commonAppProgressState.summary!, orgProgress)
            };
        }
        return null;
    };

    const activeTimelineIndex = useMemo(() => {
        const { isOrgComplete, orgTriggers, isLocComplete, locTriggers, locProgress, locBypassedSteps } = timelineData;

        if (orgTriggers['org-launched']?.status !== 'Sent' && orgTriggers['org-launched']?.status !== 'Skipped') {
            return 0; // Org Invite is active
        }
        if (orgTriggers['org-reminder']?.status !== 'Sent' && orgTriggers['org-reminder']?.status !== 'Skipped' && !isOrgComplete) {
            return 1; // Org Reminder is active
        }
        if (orgTriggers['telephony-reminder']?.status !== 'Sent' && orgTriggers['telephony-reminder']?.status !== 'Skipped' && !isOrgComplete) {
            return 2; // Telephony Setup is active
        }
        if (!isOrgComplete) {
            return 3; // Org Submitted phase is active
        }
        if (locTriggers['loc-launched']?.status !== 'Sent' && locTriggers['loc-launched']?.status !== 'Skipped') {
            return 4; // Location Invite is active
        }
        if (locTriggers['loc-reminder']?.status !== 'Sent' && locTriggers['loc-reminder']?.status !== 'Skipped' && !isLocComplete) {
            return 5; // Location Reminder is active
        }
        if (!isLocComplete) {
            return 6; // Location Submitted phase is active
        }

        // --- End of onboarding, start of testing! ---
        const testingState = getTestingProgressState(locProgress) as any;
        const rounds = testingState?.rounds || [];
        const locDetails = parseOnboardingProgressDetails(locProgress?.detailsJson);
        const hasTestingStarted = !!testingState.startedAt || rounds.length > 0 || isTestingProgressComplete(locProgress);

        if (!isOrgOnboardingComplete) {
            return 6; // Waiting for Org onboarding complete / Automated QA
        }
        if (!locDetails?.isAutomatedTestComplete
            && !hasTestingStarted
            && locTriggers['testing-ready']?.status !== 'Sent'
            && locTriggers['testing-ready']?.status !== 'Skipped') {
            return 7; // Run Automated Tests is active
        }
        if (locTriggers['testing-ready']?.status !== 'Sent' && locTriggers['testing-ready']?.status !== 'Skipped') {
            return 8; // Testing Ready invitation
        }
        if (rounds.length === 0 && !isTestingRoundReviewSkipped(1, locBypassedSteps, locTriggers)) {
            return 9; // Round 1 Feedback
        }
        if (locTriggers['phase2-testing-ready']?.status !== 'Sent' && locTriggers['phase2-testing-ready']?.status !== 'Skipped') {
            return 10; // Phase 2 Ready
        }
        if (rounds.length < 2 && !isTestingRoundReviewSkipped(2, locBypassedSteps, locTriggers)) {
            return 11; // Round 2 Feedback
        }
        if (locTriggers['launch-ready']?.status !== 'Sent' && locTriggers['launch-ready']?.status !== 'Skipped') {
            return 12; // Launch Ready
        }
        if (locTriggers['go-live']?.status !== 'Sent' && locTriggers['go-live']?.status !== 'Skipped') {
            return 13; // Go Live
        }
        return 14; // All complete!
    }, [timelineData, isOrgOnboardingComplete]);

    const [editSubject, setEditSubject] = useState('');
    const [editBody, setEditBody] = useState('');

    const [orgContactFirstName, setOrgContactFirstName] = useState('');
    const [orgContactEmail, setOrgContactEmail] = useState('');
    const [locContactFirstName, setLocContactFirstName] = useState<Record<string, string>>({});
    const [locContactEmail, setLocContactEmail] = useState<Record<string, string>>({});

    const [isSavingLaunchDate, setIsSavingLaunchDate] = useState(false);
    const [launchDateError, setLaunchDateError] = useState<string>('');
    const [copiedTemplate, setCopiedTemplate] = useState(false);

    const handleOpenLaunchDateModal = (app: LocationApplication, isLive: boolean, currentDate: string | null | undefined) => {
        setLaunchDateModal({ app, isLive, currentDate });
        setTempLaunchDateVal(currentDate ? moment.utc(currentDate).format('YYYY-MM-DD') : '');
        setLaunchDateError('');
    };

    const handleSaveLaunchDate = async (selectedDate: string) => {
        if (!launchDateModal) return;
        setIsSavingLaunchDate(true);
        setLaunchDateError('');
        try {
            const { app, isLive } = launchDateModal;
            const utcZuluString = selectedDate
                ? moment.utc(selectedDate, 'YYYY-MM-DD').toISOString()
                : null;

            const updatePayload: any = {
                shouldUpdateLaunchDate: true
            };

            if (isLive) {
                updatePayload.launchDate = utcZuluString;
            } else {
                updatePayload.targetLaunchDate = utcZuluString;
            }

            const result = await updateApplication(app.id, updatePayload);

            if (result.resultType === 'Ok') {
                setLocalAppOverrides(prev => ({
                    ...prev,
                    [app.id]: {
                        ...prev[app.id],
                        launchDate: isLive ? utcZuluString : app.launchDate,
                        targetLaunchDate: !isLive ? utcZuluString : app.targetLaunchDate
                    }
                }));
                setLaunchDateModal(null);
            } else {
                setLaunchDateError(result.errors?.[0] || 'Failed to update date.');
            }
        } catch (err) {
            setLaunchDateError('An unexpected error occurred.');
        } finally {
            setIsSavingLaunchDate(false);
        }
    };

    const handleSparkInviteSent = async (applicationId: string, email: string | string[]) => {
        const app = locationApps.find(a => a.id === applicationId) || sparkInviteApplication || (props.locationApps?.find(a => a.id === applicationId) as LocationApplication | undefined);
        const emailStr = Array.isArray(email) ? email.join(', ') : email;

        const currentProgress = progressStateByAppId[applicationId]?.data;
        const existingDetails = parseOnboardingProgressDetails(currentProgress?.detailsJson);
        const updatedDetails = {
            ...existingDetails,
            isSparkDashboardInvited: true,
            sparkDashboardInvitedAt: new Date().toISOString(),
            sparkDashboardInvitedEmail: emailStr
        };

        const updatedModel: OnboardingProgressModel = {
            ...(currentProgress || {}),
            applicationId,
            percentComplete: currentProgress?.percentComplete || 0,
            isComplete: !!currentProgress?.isComplete,
            lastUpdated: new Date().toISOString(),
            detailsJson: serializeOnboardingProgressDetails(updatedDetails)
        };

        // Optimistically update progress in UI immediately so the action item is removed instantly
        setProgressStateByAppId(current => ({
            ...current,
            [applicationId]: {
                ...current[applicationId],
                data: updatedModel,
                summary: app ? buildProgressSummary(app, updatedModel, orgLocationSpecificFieldKeys) : current[applicationId]?.summary
            }
        }));

        const appName = app?.name || 'location';
        setGlobalSuccess(`Client invited to Spark Dashboard for ${appName}.`);
        setTimeout(() => setGlobalSuccess(''), 4000);

        try {
            const saveRes = await updateOnboardingProgress(updatedModel);
            if (saveRes.resultType === 'Ok') {
                const finalData = saveRes.data || updatedModel;
                setProgressStateByAppId(current => ({
                    ...current,
                    [applicationId]: {
                        ...current[applicationId],
                        data: finalData,
                        summary: app ? buildProgressSummary(app, finalData, orgLocationSpecificFieldKeys) : current[applicationId]?.summary
                    }
                }));
            }
        } catch (err) {
            console.error('Error saving spark dashboard invite progress:', err);
        }
    };

    const handleOpenOnboardingPreview = async (
        app: LocationApplication,
        triggerId: string,
        progress: OnboardingProgressModel | null | undefined
    ) => {
        clearFeedback();
        const isCommon = app.id === props.commonApp?.id;

        const isTestingOrLaunchTrigger = triggerId === 'testing-ready' || triggerId === 'phase2-testing-ready' || triggerId === 'launch-ready' || triggerId === 'go-live';
        if (isTestingOrLaunchTrigger && !isCommon) {
            const locationDetails = parseOnboardingProgressDetails(progress?.detailsJson);
            const isLocComplete = !!progress?.isComplete
                || locationDetails.bypassedSteps?.includes('loc-submitted')
                || locationDetails.emailTriggers?.['loc-submitted']?.status === 'Skipped';
            if (!isLocComplete) {
                setGlobalError('Location onboarding must be completed or skipped before accessing testing or launch email notifications.');
                return;
            }
        }

        const answers = getOnboardingProgressAnswers(progress) || {};
        const fallbackFirstName = ((answers['firstName'] || '') as string).trim();
        const fallbackEmail = ((answers['contactEmail'] || '') as string).trim();

        let recipientFirstName = isCommon ? orgContactFirstName.trim() : (locContactFirstName[app.id] || '').trim();
        recipientFirstName = recipientFirstName || fallbackFirstName;

        let recipientEmail = isCommon ? orgContactEmail.trim() : (locContactEmail[app.id] || '').trim();
        recipientEmail = recipientEmail || fallbackEmail;

        // IMMEDIATELY open the modal in loading state to prevent click lag!
        setPreviewEmailModal({
            title: isCommon ? "Organization Onboarding" : `${app.name || "your location"} Onboarding`,
            subject: '',
            body: '',
            triggerKey: triggerId,
            app: app,
            recipientEmail: recipientEmail,
            recipientFirstName: recipientFirstName,
            link: '',
            progress,
            isLoadingPreview: true
        });

        try {
            if (isCommon) {
                setCommonAppProgressState(current => ({ ...current, loading: true }));
            } else {
                setProgressStateByAppId(current => ({
                    ...current,
                    [app.id]: { ...current[app.id], loading: true }
                }));
            }

            // Fetch the fresh progress state from the backend to ensure absolute synchronization
            let freshProgress = progress;
            const progressRes = await getOnboardingProgressForApplication(app.id);
            if (progressRes.resultType === 'Ok' && progressRes.data) {
                freshProgress = progressRes.data;
                if (isCommon) {
                    setCommonAppProgressState(current => ({
                        ...current,
                        data: progressRes.data,
                        summary: buildProgressSummary(app, progressRes.data, undefined)
                    }));
                } else {
                    setProgressStateByAppId(current => ({
                        ...current,
                        [app.id]: {
                            ...current[app.id],
                            data: progressRes.data,
                            summary: buildProgressSummary(app, progressRes.data, orgLocationSpecificFieldKeys)
                        }
                    }));
                }
            }

            const freshAnswers = getOnboardingProgressAnswers(freshProgress) || {};
            const freshFallbackFirstName = ((freshAnswers['firstName'] || '') as string).trim();
            const freshFallbackEmail = ((freshAnswers['contactEmail'] || '') as string).trim();

            if (isCommon) {
                recipientFirstName = orgContactFirstName.trim() || freshFallbackFirstName;
                recipientEmail = orgContactEmail.trim() || freshFallbackEmail;
                
                if (recipientFirstName && !orgContactFirstName) {
                    setOrgContactFirstName(recipientFirstName);
                }
                if (recipientEmail && !orgContactEmail) {
                    setOrgContactEmail(recipientEmail);
                }
            } else {
                recipientFirstName = (locContactFirstName[app.id] || '').trim() || freshFallbackFirstName;
                recipientEmail = (locContactEmail[app.id] || '').trim() || freshFallbackEmail;
                
                if (recipientFirstName && !locContactFirstName[app.id]) {
                    setLocContactFirstName(current => ({ ...current, [app.id]: recipientFirstName }));
                }
                if (recipientEmail && !locContactEmail[app.id]) {
                    setLocContactEmail(current => ({ ...current, [app.id]: recipientEmail }));
                }
            }

            const details = parseOnboardingProgressDetails(freshProgress?.detailsJson);
            const emailTriggers = details.emailTriggers || {};
            const triggerState = emailTriggers[triggerId];
            const wasSentManually = !!triggerState?.sentManually;

            const previewReq = {
                recipientFirstName: recipientFirstName || undefined,
                recipientEmail: recipientEmail || undefined,
                manual: wasSentManually,
                onboardingBaseUrl: getOnboardingBaseUrl()
            };

            const previewRes = await previewOnboardingEmail(app.id, triggerId as OnboardingEmailTriggerId, previewReq);

            if (isCommon) {
                setCommonAppProgressState(current => ({ ...current, loading: false }));
            } else {
                setProgressStateByAppId(current => ({
                    ...current,
                    [app.id]: { ...current[app.id], loading: false }
                }));
            }

            const linkState = isCommon ? commonAppLinkState : (linkStateByAppId[app.id] ?? {});
            const backendLink = (previewRes.resultType === 'Ok' && previewRes.data?.link) ? previewRes.data.link : null;
            const targetLinkState = backendLink ? { ...linkState, url: backendLink } : linkState;

            let emailData: { subject: string; body: string; link: string };
            let isHtml = false;

            const hasBackendData = previewRes.resultType === 'Ok' && previewRes.data && previewRes.data.subject && previewRes.data.body;

            if (hasBackendData) {
                emailData = previewRes.data;
                isHtml = true;
            } else {
                console.warn('Backend email preview failed or returned empty data, falling back to client-side generation:', previewRes.errors?.[0]);
                emailData = getClientSideEmailFallback(app, triggerId, recipientFirstName, recipientEmail, targetLinkState);
                isHtml = false;
            }

            if (triggerId === 'launch-ready') {
                emailData = {
                    ...emailData,
                    body: populateTargetLaunchDate(emailData.body, app)
                };
            }

            const editableBody = isHtml ? emailHtmlToEditableText(emailData.body) : emailData.body;

            setEditSubject(emailData.subject);
            setEditBody(editableBody);
            setIsManualEmail(wasSentManually);
            setPreviewEmailModal({
                title: isCommon ? "Organization Onboarding" : `${app.name || "your location"} Onboarding`,
                subject: emailData.subject,
                body: editableBody,
                triggerKey: triggerId,
                app: app,
                recipientEmail: recipientEmail,
                recipientFirstName: recipientFirstName,
                link: emailData.link,
                progress: freshProgress,
                isLoadingPreview: false
            });
        } catch (err) {
            console.error("Failed to open email preview:", err);
            setPreviewEmailModal(null);
            setGlobalError("Failed to load email preview. Please try again.");
            if (isCommon) {
                setCommonAppProgressState(current => ({ ...current, loading: false }));
            } else {
                setProgressStateByAppId(current => ({
                    ...current,
                    [app.id]: { ...current[app.id], loading: false }
                }));
            }
        }
    };



    const handleSendEmailFromModal = async () => {
        if (!previewEmailModal) return;
        if (isSendingEmailRef.current) return;

        const isCommon = previewEmailModal.app.id === props.commonApp?.id;
        const triggerKey = previewEmailModal.triggerKey;
        if ((triggerKey === 'testing-ready' || triggerKey === 'phase2-testing-ready' || triggerKey === 'launch-ready' || triggerKey === 'go-live') && !isCommon) {
            const locationDetails = parseOnboardingProgressDetails(previewEmailModal.progress?.detailsJson);
            const isLocComplete = !!previewEmailModal.progress?.isComplete
                || locationDetails.bypassedSteps?.includes('loc-submitted')
                || locationDetails.emailTriggers?.['loc-submitted']?.status === 'Skipped';
            if (!isLocComplete) {
                setGlobalError('Location onboarding must be completed or skipped before sending testing or launch email notifications.');
                return;
            }
        }

        isSendingEmailRef.current = true;
        try {
            const { app, link } = previewEmailModal;
            let activeProgress = previewEmailModal.progress;

            // Transition single location to testing phase when testing is shared/sent
            if (triggerKey === 'testing-ready' || triggerKey === 'phase2-testing-ready') {
                const activeDetails = parseOnboardingProgressDetails(activeProgress?.detailsJson);
                const isFirstPhase2Invite = triggerKey === 'phase2-testing-ready'
                    && activeDetails.emailTriggers?.[triggerKey]?.status !== 'Sent';
                const updatedProgress = buildTestingModeProgressModel(app, activeProgress, true, isFirstPhase2Invite);
                const saveResult = await updateOnboardingProgress(updatedProgress);
                if (saveResult.resultType !== 'Ok' || !saveResult.data) {
                    const error = saveResult.errors?.[0] || 'Failed to enable testing mode before sending the email.';
                    setGlobalError(error);
                    return;
                }

                activeProgress = saveResult.data;
                previewEmailModal.progress = saveResult.data;
            }

            const isCommon = app.id === props.commonApp?.id;
            
            const answers = getOnboardingProgressAnswers(activeProgress) || {};
            const fallbackFirstName = ((answers['firstName'] || '') as string).trim();
            const fallbackEmail = ((answers['contactEmail'] || '') as string).trim();

            const currentFirstName = (isCommon ? orgContactFirstName : (locContactFirstName[app.id] || '')).trim() || fallbackFirstName;
            const currentEmail = (isCommon ? orgContactEmail : (locContactEmail[app.id] || '')).trim() || fallbackEmail;

            if (!isManualEmail && (!currentFirstName || !currentEmail)) {
                setGlobalError('Please fill out both Contact First Name and Contact Email.');
                return;
            }

            const details = parseOnboardingProgressDetails(activeProgress?.detailsJson);
            const emailTriggers = details.emailTriggers || {};
            const triggerState = emailTriggers[triggerKey];
            const isAlreadySent = triggerState?.status === 'Sent';
            const isSkipped = triggerState?.status === 'Skipped';

            if (isSkipped) {
                const confirmed = window.confirm(`This step was marked as Skipped. Do you want to unskip and send this email?`);
                if (!confirmed) return;
            } else if (isAlreadySent) {
                const displaySentAt = triggerState?.firstSentAt || triggerState?.sentAt;
                const dateStr = displaySentAt ? moment(displaySentAt).format('MM/DD/YYYY [at] h:mm A') : 'an unknown date';
                const confirmed = window.confirm(`This email was sent on ${dateStr}. Do you want to resend?`);
                if (!confirmed) return;
            }

            if (isCommon) {
                setCommonAppProgressState(current => ({ ...current, loading: true }));
            } else {
                setProgressStateByAppId(current => ({
                    ...current,
                    [app.id]: { ...current[app.id], loading: true }
                }));
            }

            if (isSkipped) {
                const resetRes = await updateOnboardingEmailTriggerStatus(
                    app.id,
                    triggerKey as OnboardingEmailTriggerId,
                    { action: 'reset' }
                );
                if (resetRes.resultType !== 'Ok') {
                    const error = resetRes.errors?.[0] || 'Failed to reset skipped email trigger status.';
                    if (isCommon) {
                        setCommonAppProgressState(current => ({ ...current, loading: false, error }));
                    } else {
                        setProgressStateByAppId(current => ({
                            ...current,
                            [app.id]: { ...current[app.id], loading: false, error }
                        }));
                    }
                    setGlobalError(error);
                    return;
                }
            }

            const bccEmail = "support@voicify.com";
            const currentEmailWithBcc = currentEmail 
                ? `${currentEmail.trim()}; ${bccEmail}`
                : bccEmail;

            const sendReq: SendOnboardingEmailRequest = {
                recipientFirstName: currentFirstName || undefined,
                recipientEmail: currentEmail ? currentEmailWithBcc : undefined,
                subject: editSubject,
                body: buildBrandedEmailHtml(editBody, link, triggerKey),
                manual: isManualEmail,
                resend: isAlreadySent ? true : undefined
            };

            let res = await sendOnboardingEmail(app.id, triggerKey as OnboardingEmailTriggerId, sendReq);

            if (res.resultType !== 'Ok' && res.errors?.[0]?.includes('already been sent or skipped')) {
                console.warn('Onboarding trigger state out of sync. Attempting self-healing recovery...');
                const refreshRes = await getOnboardingProgressForApplication(app.id);
                if (refreshRes.resultType === 'Ok' && refreshRes.data) {
                    const freshDetails = parseOnboardingProgressDetails(refreshRes.data.detailsJson);
                    const freshTriggerState = freshDetails.emailTriggers?.[triggerKey];
                    
                    if (freshTriggerState?.status === 'Skipped') {
                        const resetRes = await updateOnboardingEmailTriggerStatus(
                            app.id,
                            triggerKey as OnboardingEmailTriggerId,
                            { action: 'reset' }
                        );
                        if (resetRes.resultType === 'Ok') {
                            res = await sendOnboardingEmail(app.id, triggerKey as OnboardingEmailTriggerId, {
                                ...sendReq,
                                resend: undefined
                            });
                        }
                    } else if (freshTriggerState?.status === 'Sent') {
                        res = await sendOnboardingEmail(app.id, triggerKey as OnboardingEmailTriggerId, {
                            ...sendReq,
                            resend: true
                        });
                    }
                }
            }

            if (res.resultType === 'Ok' && res.data) {
                if (isCommon) {
                    setCommonAppProgressState({
                        loading: false,
                        data: res.data,
                        summary: buildProgressSummary(app, res.data, undefined),
                        error: ''
                    });
                } else {
                    setProgressStateByAppId(current => ({
                        ...current,
                        [app.id]: {
                            loading: false,
                            data: res.data,
                            summary: buildProgressSummary(app, res.data, orgLocationSpecificFieldKeys),
                            error: ''
                        }
                    }));
                }

                if (isManualEmail) {
                    try {
                        await navigator.clipboard.writeText(link);
                        if (isCommon) {
                            setCommonAppLinkState({
                                loading: false,
                                url: link,
                                error: '',
                                copied: true
                            });
                        } else {
                            setAppState(app.id, {
                                loading: false,
                                url: link,
                                error: '',
                                copied: true
                            });
                        }
                        setTimeout(() => {
                            if (isCommon) {
                                setCommonAppLinkState(current => ({ ...current, copied: false }));
                            } else {
                                setAppState(app.id, { copied: false });
                            }
                        }, 2000);
                    } catch (_) {}
                } else {
                    setGlobalSuccess(`Email sent to ${currentEmail}!`);
                }
            } else {
                const error = res.errors?.[0] || 'Failed to send onboarding email.';
                if (isCommon) {
                    setCommonAppProgressState(current => ({ ...current, loading: false, error }));
                } else {
                    setProgressStateByAppId(current => ({
                        ...current,
                        [app.id]: { ...current[app.id], loading: false, error }
                    }));
                }
                setGlobalError(error);
            }

            setPreviewEmailModal(null);
            await loadOnboardingApiKeys(false);
        } finally {
            isSendingEmailRef.current = false;
        }
    };

    const filteredLocationApps = useMemo<LocationApplication[]>(() => {
        const search = appSearch.trim().toLowerCase();
        if (!search) {
            return locationApps;
        }
        return locationApps.filter(application => (application.name || '').toLowerCase().includes(search));
    }, [appSearch, locationApps]);

    const onboardingApiKeys = useMemo<ApiKeyModel[]>(() => {
        return [...(props.apiKeyContainer.state.apiKeys ?? [])]
            .filter(apiKey => (apiKey.name ?? '').startsWith(onboardingApiKeyPrefix))
            .sort((left, right) => (left.name ?? '').localeCompare(right.name ?? ''));
    }, [props.apiKeyContainer.state.apiKeys]);

    useEffect(() => {
        if (!location.pathname.endsWith('/onboarding') || !props.organizationId || !isOrgAdmin) {
            return;
        }

        const liveLocationApiKeys = locationApps
            .filter(application => application.isLive)
            .map(application => ({
                application,
                apiKey: onboardingApiKeys.find(key =>
                    key.name === `Onboarding - ${application.name}`
                )
            }))
            .filter(({ apiKey }) => !!apiKey?.id && !automaticallyDeletingApiKeyIdsRef.current.has(apiKey.id));

        if (liveLocationApiKeys.length === 0) {
            return;
        }

        const disableLiveLocationOnboardingLinks = async () => {
            let linksChanged = false;

            for (const { application, apiKey } of liveLocationApiKeys) {
                const apiKeyId = apiKey?.id;
                if (!apiKeyId) {
                    continue;
                }

                automaticallyDeletingApiKeyIdsRef.current.add(apiKeyId);
                const result = await props.apiKeyContainer.deleteApiKey(apiKeyId);
                if (result.resultType === 'Ok') {
                    linksChanged = true;
                    continue;
                }

                automaticallyDeletingApiKeyIdsRef.current.delete(apiKeyId);
                setGlobalError(result.errors?.[0] ?? `Failed to automatically disable the onboarding link for ${application.name}.`);
            }

            if (linksChanged) {
                await loadOnboardingApiKeys(false);
            }
        };

        disableLiveLocationOnboardingLinks();
    }, [location.pathname, props.organizationId, isOrgAdmin, locationApps, onboardingApiKeys]);

    useEffect(() => {
        if (!location.pathname.endsWith('/onboarding') || !props.organizationId || !isOrgAdmin || !props.commonApp) {
            return;
        }

        if (!isOrgOnboardingFormSubmitted) {
            return;
        }

        const orgApiKey = onboardingApiKeys.find(key =>
            key.name === `Onboarding - ${props.commonApp!.name}`
        );

        const apiKeyId = orgApiKey?.id;
        if (!apiKeyId || automaticallyDeletingApiKeyIdsRef.current.has(apiKeyId)) {
            return;
        }

        const disableOrgOnboardingLink = async () => {
            automaticallyDeletingApiKeyIdsRef.current.add(apiKeyId);
            const result = await props.apiKeyContainer.deleteApiKey(apiKeyId);
            if (result.resultType === 'Ok') {
                await loadOnboardingApiKeys(false);
            } else {
                automaticallyDeletingApiKeyIdsRef.current.delete(apiKeyId);
                setGlobalError(result.errors?.[0] ?? `Failed to automatically disable the organization onboarding link.`);
            }
        };

        disableOrgOnboardingLink();
    }, [location.pathname, props.organizationId, isOrgAdmin, props.commonApp, isOrgOnboardingFormSubmitted, onboardingApiKeys]);

    useEffect(() => {
        if (!props.organizationId || !isOrgAdmin) {
            return;
        }
        loadOnboardingApiKeys();
    }, [props.organizationId, isOrgAdmin]);

    const setAppState = (applicationId: string, state: LinkState) => {
        setLinkStateByAppId(current => ({
            ...current,
            [applicationId]: {
                ...current[applicationId],
                ...state
            }
        }));
    };

    const clearFeedback = () => {
        setGlobalError('');
        setGlobalSuccess('');
    };

    const handleOrgCopyLink = async () => {
        if (!props.commonApp) return;
        let url = commonAppLinkState.url;
        if (!url) {
            const linkRes = await generateOnboardingLink(props.commonApp.id);
            if (linkRes.resultType === 'Ok' && linkRes.data) {
                url = `${getOnboardingBaseUrl()}/${linkRes.data}`;
            }
        }
        if (url) {
            try {
                await navigator.clipboard.writeText(url);
                setCommonAppLinkState({
                    loading: false,
                    url,
                    error: '',
                    copied: true
                });
                await loadOnboardingApiKeys(false); // Refresh keys so matchingApiKey is updated
                setTimeout(() => {
                    setCommonAppLinkState(current => ({ ...current, copied: false }));
                }, 2000);
            } catch (_) {
                setGlobalError('Failed to copy onboarding link.');
            }
        } else {
            setGlobalError('Failed to generate onboarding link.');
        }
    };

    const bounceTooltipKeyframes = `
        @keyframes bounceTooltip {
            0% {
                opacity: 0;
                transform: translateX(-50%) translateY(0) scale(0.9);
            }
            50% {
                opacity: 1;
                transform: translateX(-50%) translateY(-12px) scale(1.05);
            }
            100% {
                opacity: 1;
                transform: translateX(-50%) translateY(-8px) scale(1);
            }
        }
    `;

    const loadOnboardingApiKeys = async (showLoadError: boolean = true) => {
        if (!props.organizationId) {
            return null;
        }
        const result = await props.apiKeyContainer.loadOrganizationApiKeys(props.organizationId);
        if (showLoadError && result.resultType !== 'Ok') {
            setGlobalError(result.errors?.[0] ?? 'Failed to load onboarding links.');
        }
        return result;
    };

    const fetchProgressStateForApplication = async (application: LocationApplication): Promise<ProgressState> => {
        const result = await getOnboardingProgressForApplication(application.id);
        if (result.resultType === 'Ok') {
            return {
                loading: false,
                data: result.data || null,
                summary: buildProgressSummary(application, result.data || null, orgLocationSpecificFieldKeys),
                error: ''
            };
        }
        if (result.resultType === 'NotFound') {
            return {
                loading: false,
                data: null,
                summary: null,
                error: ''
            };
        }
        return {
            loading: false,
            data: null,
            summary: null,
            error: result.errors?.[0] || 'Failed to load onboarding progress.'
        };
    };

    const loadApplicationProgressForApp = async (application: LocationApplication): Promise<ProgressState> => {
        setProgressStateByAppId(current => ({
            ...current,
            [application.id]: {
                ...current[application.id],
                loading: true,
                error: ''
            }
        }));
        const nextState = await fetchProgressStateForApplication(application);
        setProgressStateByAppId(current => ({
            ...current,
            [application.id]: {
                ...current[application.id],
                ...nextState
            }
        }));
        return nextState;
    };

    const loadApplicationProgress = async () => {
        setIsProgressLoading(true);
        const appIds = locationApps.map(app => app.id);
        let resolvedCommonProgress: OnboardingProgressModel | null = null;

        let commonPromise = Promise.resolve();
        if (props.commonApp) {
            setCommonAppProgressState(current => ({ ...current, loading: true, error: '' }));
            commonPromise = fetchProgressStateForApplication(props.commonApp as LocationApplication).then(async (state) => {
                setCommonAppProgressState(state);
                resolvedCommonProgress = state.data;
                if (locationApps.length > 0 && state.data) {
                    try {
                        const fieldsResult = await loadApplicationModuleFields(locationApps[0].id, undefined);
                        if (fieldsResult.result.resultType === "Ok" && props.organizationId) {
                            const lookup = flattenOrganizationOwnedApplicationModuleFields(
                                // The normalized form omits template ownership;
                                // use Spark's raw response to identify the
                                // organization-owned location schema.
                                fieldsResult.rawData || fieldsResult.parsedData,
                                props.organizationId
                            );
                            const sparkLocationSpecificVariables = Object.keys(lookup);
                            const parsedDetails = parseOnboardingProgressDetails(state.data.detailsJson);
                            const existingVars = parsedDetails.sparkLocationSpecificVariables || [];
                            const isDifferent = sparkLocationSpecificVariables.length !== existingVars.length ||
                                !sparkLocationSpecificVariables.every(v => existingVars.includes(v));

                            if (isDifferent) {
                                const updatedProgress: OnboardingProgressModel = {
                                    ...state.data,
                                    detailsJson: serializeOnboardingProgressDetails({
                                        ...parsedDetails,
                                        sparkLocationSpecificVariables
                                    })
                                };
                                const saveResult = await updateOnboardingProgress(updatedProgress);
                                if (saveResult.resultType === "Ok" && saveResult.data) {
                                    setCommonAppProgressState(current => ({
                                        ...current,
                                        data: saveResult.data
                                    }));
                                    resolvedCommonProgress = saveResult.data;
                                }
                            }
                        }
                    } catch (_) {}
                }
            });
        }

        // Setup location states to show loading/skeletons immediately
        if (appIds.length > 0) {
            setProgressStateByAppId(current => appIds.reduce((accumulator, applicationId) => ({
                ...accumulator,
                [applicationId]: {
                    ...current[applicationId],
                    loading: true,
                    error: ''
                }
            }), { ...current }));
        } else {
            setProgressStateByAppId({});
        }

        // Progressive loading: dismiss overall overlay loader as soon as common/org onboarding progress resolves
        commonPromise.then(() => {
            setIsProgressLoading(false);
        }).catch(() => {
            setIsProgressLoading(false);
        });

        // Background fetching for location apps with concurrency limited to 5 concurrent requests
        if (appIds.length > 0) {
            limitConcurrency(locationApps, 5, async (application) => {
                let state = await fetchProgressStateForApplication(application);
                let appliedFutureEmailDefaultTriggerIds: OnboardingEmailTriggerId[] = [];

                // Wait for organization progress to resolve first before attempting sync
                await commonPromise;

                if (resolvedCommonProgress) {
                    const commonDetails = parseOnboardingProgressDetails(resolvedCommonProgress.detailsJson);
                    const commonKeys = commonDetails.locationSpecificFieldKeys || [];
                    const commonSparkVars = commonDetails.sparkLocationSpecificVariables || [];

                    const localDetails = state.data ? parseOnboardingProgressDetails(state.data.detailsJson) : {};
                    const localKeys = localDetails.locationSpecificFieldKeys || [];
                    const localSparkVars = localDetails.sparkLocationSpecificVariables || [];

                    const keysDifferent = commonKeys.length !== localKeys.length || !commonKeys.every(k => localKeys.includes(k));
                    const varsDifferent = commonSparkVars.length !== localSparkVars.length || !commonSparkVars.every(v => localSparkVars.includes(v));

                    if (keysDifferent || varsDifferent || !state.data) {
                        const updatedProgress: OnboardingProgressModel = {
                            ...(state.data || {}),
                            applicationId: application.id,
                            percentComplete: state.data?.percentComplete || 0,
                            isComplete: state.data?.isComplete || false,
                            currentStep: state.data?.currentStep || '',
                            detailsJson: serializeOnboardingProgressDetails({
                                ...localDetails,
                                locationSpecificFieldKeys: commonKeys,
                                sparkLocationSpecificVariables: commonSparkVars
                            })
                        };
                        const saveRes = await updateOnboardingProgress(updatedProgress);
                        if (saveRes.resultType === 'Ok' && saveRes.data) {
                            state.data = saveRes.data;
                            state.summary = buildProgressSummary(application, saveRes.data, commonKeys);
                        }
                    }

                    const refreshedCommonDetails = parseOnboardingProgressDetails(resolvedCommonProgress.detailsJson);
                    const emailDefaults = refreshedCommonDetails.locationEmailTriggerDefaults as LocationEmailTriggerDefaults | undefined;
                    const defaultTriggerIdsToApply = getUnappliedDefaultTriggerIds(emailDefaults, application);

                    if (defaultTriggerIdsToApply.length > 0) {
                        const eligibleTriggerIds = defaultTriggerIdsToApply.filter(triggerId =>
                            getLocationEmailTriggerStatus(state.data, triggerId) === 'Pending'
                        );

                        if (eligibleTriggerIds.length > 0) {
                            const defaultsResponse = await bulkUpdateOnboardingEmailTriggerStatus(application.id, {
                                triggerIds: eligibleTriggerIds,
                                action: 'skip'
                            });
                            if (defaultsResponse.resultType === 'Ok' && defaultsResponse.data) {
                                state = {
                                    ...state,
                                    data: defaultsResponse.data,
                                    summary: buildProgressSummary(application, defaultsResponse.data, commonKeys),
                                    error: ''
                                };
                                appliedFutureEmailDefaultTriggerIds = defaultTriggerIdsToApply;
                            } else {
                                state.error = defaultsResponse.errors?.[0] || 'Failed to apply the organization email trigger defaults.';
                            }
                        } else {
                            appliedFutureEmailDefaultTriggerIds = defaultTriggerIdsToApply;
                        }
                    }
                }

                if (application.featureFlags?.some(flag => flag.id === OutboundCallFeatureFlagId)) {
                    try {
                        const depRes = await getTelephonyDeployments(application.id);
                        if (depRes.resultType === 'Ok' && depRes.data) {
                            setTelephonyDeploymentsByAppId(prev => ({
                                ...prev,
                                [application.id]: depRes.data
                            }));
                        }
                    } catch (_) {}
                }

                setProgressStateByAppId(current => ({
                    ...current,
                    [application.id]: {
                        ...current[application.id],
                        ...state,
                        loading: false
                    }
                }));
                return { applicationId: application.id, appliedFutureEmailDefaultTriggerIds };
            }).then(async results => {
                const hasNewlyAppliedDefaults = results.some(result => result.appliedFutureEmailDefaultTriggerIds.length > 0);
                if (!resolvedCommonProgress || !hasNewlyAppliedDefaults) return;

                const details = parseOnboardingProgressDetails(resolvedCommonProgress.detailsJson);
                const defaults = details.locationEmailTriggerDefaults;
                if (!defaults) return;
                const appliedApplicationIdsByTrigger = {
                    ...(defaults.appliedApplicationIdsByTrigger || {})
                } as Record<string, string[]>;
                results.forEach(result => {
                    result.appliedFutureEmailDefaultTriggerIds.forEach(triggerId => {
                        appliedApplicationIdsByTrigger[triggerId] = Array.from(new Set([
                            ...(appliedApplicationIdsByTrigger[triggerId] || []),
                            result.applicationId
                        ]));
                    });
                });
                const updatedCommonProgress: OnboardingProgressModel = {
                    ...resolvedCommonProgress,
                    detailsJson: serializeOnboardingProgressDetails({
                        ...details,
                        locationEmailTriggerDefaults: {
                            ...defaults,
                            appliedApplicationIdsByTrigger
                        }
                    })
                };
                const saveDefaultsResult = await updateOnboardingProgress(updatedCommonProgress);
                if (saveDefaultsResult.resultType === 'Ok' && saveDefaultsResult.data) {
                    resolvedCommonProgress = saveDefaultsResult.data;
                    setCommonAppProgressState(current => ({ ...current, data: saveDefaultsResult.data }));
                }
            }).catch(err => {
                console.error('Failed to load background location progress:', err);
            });
        }
    };

    const handleGenerateLink = async (application: ApplicationModel) => {
        clearFeedback();
        const isCommon = application.id === props.commonApp?.id;

        if (isCommon) {
            setCommonAppLinkState({ loading: true, error: '', copied: false });
        } else {
            setAppState(application.id, { loading: true, error: '', copied: false });
        }

        const result = await generateOnboardingLink(application.id);
        if (result.resultType !== 'Ok' || !result.data) {
            const error = result.errors?.[0] ?? 'Failed to generate onboarding link.';
            if (isCommon) {
                setCommonAppLinkState({ loading: false, error });
            } else {
                setAppState(application.id, { loading: false, error });
            }
            setGlobalError(error);
            return;
        }

        const url = `${getOnboardingBaseUrl()}/${result.data}`;
        if (isCommon) {
            setCommonAppLinkState({
                loading: false,
                url,
                error: '',
                copied: false
            });
        } else {
            setAppState(application.id, {
                loading: false,
                url,
                error: '',
                copied: false
            });
        }

        await loadOnboardingApiKeys(false);
        setGlobalSuccess(`Generated onboarding link for ${application.name}.`);
    };

    const handleBulkGenerateOnboardingLinks = async () => {
        clearFeedback();
        setGlobalSuccess('');
        setGlobalError('');

        // IMMEDIATELY open the modal in loading state to prevent click lag!
        setPreviewEmailModal({
            title: `Consolidated Onboarding Links`,
            subject: '',
            body: '',
            triggerKey: 'org-launched',
            app: props.commonApp! as unknown as LocationApplication,
            recipientEmail: orgContactEmail || '',
            recipientFirstName: orgContactFirstName || '',
            link: '',
            progress: commonAppProgressState.data,
            isLoadingPreview: true
        });

        let successCount = 0;
        let failCount = 0;
        const generatedLinks: Array<{ name: string; url: string }> = [];
        const targetLocations = locationApps.filter(loc => !loc.isLive);

        if (targetLocations.length === 0) {
            setPreviewEmailModal(null);
            setGlobalError('No non-live locations found to generate onboarding links for.');
            return;
        }

        try {
            for (const loc of targetLocations) {
                try {
                    setAppState(loc.id, { loading: true, error: '', copied: false });
                    const result = await generateOnboardingLink(loc.id);
                    if (result.resultType === 'Ok' && result.data) {
                        const url = `${getOnboardingBaseUrl()}/${result.data}`;
                        setAppState(loc.id, {
                            loading: false,
                            url,
                            error: '',
                            copied: false
                        });
                        successCount++;
                        generatedLinks.push({ name: loc.name || "Location", url });
                    } else {
                        failCount++;
                        setAppState(loc.id, { loading: false, error: result.errors?.[0] ?? 'Failed' });
                    }
                } catch (err) {
                    failCount++;
                    setAppState(loc.id, { loading: false, error: 'Error occurred' });
                }
            }

            // The organization link is only useful while the organization form is
            // incomplete. Once it has been submitted, opening that link shows the
            // locked completion screen and can be mistaken for the testing link in
            // this email.
            let orgUrl = !isOrgOnboardingComplete ? commonAppLinkState.url : undefined;
            if (!orgUrl && !isOrgOnboardingComplete && props.commonApp) {
                try {
                    const linkRes = await generateOnboardingLink(props.commonApp.id);
                    if (linkRes.resultType === 'Ok' && linkRes.data) {
                        orgUrl = `${getOnboardingBaseUrl()}/${linkRes.data}`;
                        setCommonAppLinkState({
                            loading: false,
                            url: orgUrl,
                            error: '',
                            copied: false
                        });
                    }
                } catch (_) {}
            }

            await loadOnboardingApiKeys(false);

            if (failCount > 0) {
                setPreviewEmailModal(null);
                setGlobalError(`Failed to generate links for some locations. (${failCount} failed)`);
                return;
            }

            const clientName = props.commonApp?.name || "your organization";
            const subject = `Voicify Location Onboarding Links for ${clientName}`;
            const linksList = generatedLinks.map(item => `• ${item.name}: ${item.url}`).join('\n');
            
            const orgSection = (orgUrl && !isOrgOnboardingComplete) 
                ? `To get started, please complete the overall Organization Onboarding Form:\n• Organization Onboarding: ${orgUrl}\n\nOnce the organization-level details are submitted, we can begin processing location onboarding. `
                : ``;

            const body = `Hi there,\n\n${orgSection}Please disperse the location onboarding links below to each location's Point of Contact (POC). Please ensure that exactly one person completes onboarding for their respective location (no more than one person each):\n\n${linksList}\n\n— The Voicify Team`;

            setEditSubject(subject);
            setEditBody(body);
            setIsManualEmail(false);
            setPreviewEmailModal({
                title: `Consolidated Onboarding Links`,
                subject: subject,
                body: body,
                triggerKey: 'org-launched',
                app: props.commonApp! as unknown as LocationApplication,
                recipientEmail: orgContactEmail || '',
                recipientFirstName: orgContactFirstName || '',
                link: '',
                progress: commonAppProgressState.data,
                isLoadingPreview: false
            });
        } catch (err) {
            setPreviewEmailModal(null);
            setGlobalError('An unexpected error occurred while preparing consolidated onboarding links.');
        }
    };

    const handleBulkGenerateTestingLinks = async () => {
        clearFeedback();
        setGlobalSuccess('');
        setGlobalError('');

        // IMMEDIATELY open the modal in loading state to prevent click lag!
        setPreviewEmailModal({
            title: `Consolidated Testing Links`,
            subject: '',
            body: '',
            triggerKey: 'testing-ready',
            app: props.commonApp! as unknown as LocationApplication,
            recipientEmail: orgContactEmail || '',
            recipientFirstName: orgContactFirstName || '',
            link: '',
            progress: commonAppProgressState.data,
            isLoadingPreview: true
        });

        let successCount = 0;
        let failCount = 0;
        const generatedLinks: Array<{ name: string; url: string }> = [];
        const targetLocations = locationApps.filter(loc => !loc.isLive);

        if (targetLocations.length === 0) {
            setPreviewEmailModal(null);
            setGlobalError('No non-live locations found to generate testing links for.');
            return;
        }

        try {
            for (const loc of targetLocations) {
                try {
                    setAppState(loc.id, { loading: true, error: '', copied: false });
                    
                    const progressResult = await getOnboardingProgressForApplication(loc.id);
                    const currentProgress = progressResult.resultType === 'Ok' ? progressResult.data : null;
                    
                    const updatedProgress = buildTestingModeProgressModel(loc, currentProgress, true);
                    const saveResult = await updateOnboardingProgress(updatedProgress);
                    
                    if (saveResult.resultType !== 'Ok' || !saveResult.data) {
                        failCount++;
                        setAppState(loc.id, {
                            loading: false,
                            error: saveResult.errors?.[0] ?? 'Failed to enable testing mode'
                        });
                        continue;
                    }

                    setProgressStateByAppId(current => ({
                        ...current,
                        [loc.id]: {
                            ...current[loc.id],
                            data: saveResult.data,
                            summary: buildProgressSummary(loc, saveResult.data, orgLocationSpecificFieldKeys)
                        }
                    }));

                    const result = await generateOnboardingLink(loc.id);
                    if (result.resultType === 'Ok' && result.data) {
                        const url = `${getOnboardingBaseUrl()}/${result.data}`;
                        setAppState(loc.id, {
                            loading: false,
                            url,
                            error: '',
                            copied: false
                        });
                        successCount++;
                        generatedLinks.push({ name: loc.name || "Location", url });
                    } else {
                        failCount++;
                        setAppState(loc.id, { loading: false, error: result.errors?.[0] ?? 'Failed' });
                    }
                } catch (err) {
                    failCount++;
                    setAppState(loc.id, { loading: false, error: 'Error occurred' });
                }
            }

            let orgUrl = commonAppLinkState.url;
            if (!orgUrl && props.commonApp) {
                try {
                    const linkRes = await generateOnboardingLink(props.commonApp.id);
                    if (linkRes.resultType === 'Ok' && linkRes.data) {
                        orgUrl = `${getOnboardingBaseUrl()}/${linkRes.data}`;
                        setCommonAppLinkState({
                            loading: false,
                            url: orgUrl,
                            error: '',
                            copied: false
                        });
                    }
                } catch (_) {}
            }

            await loadOnboardingApiKeys(false);

            if (failCount > 0) {
                setPreviewEmailModal(null);
                setGlobalError(`Failed to enable testing mode or generate links for some locations. (${failCount} failed)`);
                return;
            }

            const clientName = props.commonApp?.name || "your organization";
            const subject = `Your Voicify Assistant is ready for testing - ${clientName}`;
            const linksList = generatedLinks.map(item => `• ${item.name}: ${item.url}`).join('\n');

            const orgSection = orgUrl 
                ? `If you have not done so already, please complete the overall Organization Onboarding Form:\n• Organization Onboarding: ${orgUrl}\n\n`
                : ``;

            const body = `Hi there,\n\nWe have completed internal engineering and automated QA! Your assistants are now ready for testing.\n\n${orgSection}Please disperse the testing links below to each location's Point of Contact (POC). Please ensure that exactly one person completes testing for their respective location (no more than one person each):\n\n${linksList}\n\n— The Voicify Team`;

            setEditSubject(subject);
            setEditBody(body);
            setIsManualEmail(false);
            setPreviewEmailModal({
                title: `Consolidated Testing Links`,
                subject: subject,
                body: body,
                triggerKey: 'testing-ready',
                app: props.commonApp! as unknown as LocationApplication,
                recipientEmail: orgContactEmail || '',
                recipientFirstName: orgContactFirstName || '',
                link: '',
                progress: commonAppProgressState.data,
                isLoadingPreview: false
            });
        } catch (err) {
            setPreviewEmailModal(null);
            setGlobalError('An unexpected error occurred while preparing consolidated testing links.');
        }
    };

    const handleCopyLink = async (application: ApplicationModel) => {
        clearFeedback();
        const isCommon = application.id === props.commonApp?.id;
        const url = isCommon ? commonAppLinkState.url : linkStateByAppId[application.id]?.url;

        if (!url) {
            return;
        }

        if (!navigator.clipboard?.writeText) {
            const error = 'Clipboard is not available in this browser.';
            if (isCommon) {
                setCommonAppLinkState({ error, url });
            } else {
                setAppState(application.id, { error });
            }
            setGlobalError(error);
            return;
        }

        try {
            await navigator.clipboard.writeText(url);
            if (isCommon) {
                setCommonAppLinkState({ copied: true, url, error: '' });
            } else {
                setAppState(application.id, { copied: true, error: '' });
            }

            setTimeout(() => {
                if (isCommon) {
                    setCommonAppLinkState(current => ({ ...current, copied: false }));
                } else {
                    setLinkStateByAppId(current => {
                        if (!current[application.id]) {
                            return current;
                        }
                        return {
                            ...current,
                            [application.id]: {
                                ...current[application.id],
                                copied: false
                            }
                        };
                    });
                }
            }, 2000);
        } catch (_) {
            const error = 'Failed to copy onboarding link.';
            if (isCommon) {
                setCommonAppLinkState({ error, url });
            } else {
                setAppState(application.id, { error });
            }
            setGlobalError(error);
        }
    };

    const handleUpdateEmailTrigger = async (
        application: ApplicationModel,
        progress: OnboardingProgressModel | null | undefined,
        triggerId: string,
        action: 'skip' | 'reset'
    ) => {
        clearFeedback();
        
        const isCommon = application.id === props.commonApp?.id;
        if (isCommon) {
            setCommonAppProgressState(current => ({ ...current, loading: true }));
        } else {
            setProgressStateByAppId(current => ({
                ...current,
                [application.id]: { ...current[application.id], loading: true }
            }));
        }

        const res = await updateOnboardingEmailTriggerStatus(
            application.id, 
            triggerId as OnboardingEmailTriggerId, 
            { action }
        );

        if (res.resultType === 'Ok' && res.data) {
            let finalData = res.data;
            if (action === 'reset' && (triggerId === 'testing-ready' || triggerId === 'phase2-testing-ready')) {
                const updatedDetails = parseOnboardingProgressDetails(finalData?.detailsJson);
                const updatedTriggers = updatedDetails.emailTriggers || {};
                const isAnyTestingSent = updatedTriggers['testing-ready']?.status === 'Sent' || updatedTriggers['phase2-testing-ready']?.status === 'Sent';
                if (!isAnyTestingSent && updatedDetails.phase === 'testing') {
                    const revertedModel: OnboardingProgressModel = {
                        ...finalData,
                        detailsJson: serializeOnboardingProgressDetails({
                            ...updatedDetails,
                            phase: 'onboarding'
                        })
                    };
                    const saveRes = await updateOnboardingProgress(revertedModel);
                    if (saveRes.resultType === 'Ok') {
                        finalData = saveRes.data || revertedModel;
                    }
                }
            }

            if (isCommon) {
                setCommonAppProgressState({
                    loading: false,
                    data: finalData,
                    summary: buildProgressSummary(application as LocationApplication, finalData, undefined),
                    error: ''
                });
            } else {
                setProgressStateByAppId(current => ({
                    ...current,
                    [application.id]: {
                        loading: false,
                        data: finalData,
                        summary: buildProgressSummary(application as LocationApplication, finalData, orgLocationSpecificFieldKeys),
                        error: ''
                    }
                }));
            }
            setGlobalSuccess(`Email trigger state updated successfully for ${application.name}.`);
        } else {
            const error = res.errors?.[0] || 'Failed to update email trigger state.';
            if (isCommon) {
                setCommonAppProgressState(current => ({ ...current, loading: false, error }));
            } else {
                setProgressStateByAppId(current => ({
                    ...current,
                    [application.id]: { ...current[application.id], loading: false, error }
                }));
            }
            setGlobalError(error);
        }
    };

    const handlePersistentLocationEmailTriggerDefaults = async (
        action: 'skip' | 'reset',
        triggerIds: OnboardingEmailTriggerId[],
        appliedApplicationIds: string[],
        targetApplicationIds: string[]
    ): Promise<string | null> => {
        if (!props.commonApp?.id) {
            return 'The future-location default could not be saved because organization onboarding is unavailable.';
        }

        const currentProgress = commonAppProgressState.data || {
            applicationId: props.commonApp.id,
            percentComplete: 0,
            isComplete: false,
            currentStep: ''
        };
        const details = parseOnboardingProgressDetails(currentProgress.detailsJson);
        const locationEmailTriggerDefaults = updateLocationEmailTriggerDefaults(
            details.locationEmailTriggerDefaults,
            action,
            triggerIds,
            appliedApplicationIds,
            targetApplicationIds
        );
        const updatedProgress: OnboardingProgressModel = {
            ...currentProgress,
            applicationId: props.commonApp.id,
            detailsJson: serializeOnboardingProgressDetails({
                ...details,
                locationEmailTriggerDefaults
            })
        };
        const response = await updateOnboardingProgress(updatedProgress);

        if (response.resultType !== 'Ok') {
            return response.errors?.[0] || 'The future-location email trigger default could not be saved.';
        }

        const nextProgress = response.data || updatedProgress;
        setCommonAppProgressState(current => ({
            ...current,
            data: nextProgress,
            summary: buildProgressSummary(props.commonApp as LocationApplication, nextProgress, undefined),
            error: ''
        }));
        return null;
    };

    const handleBypassStep = async (appId: string, stepKey: string, isOrg: boolean, action: 'skip' | 'unskip') => {
        clearFeedback();
        const progressState = isOrg ? commonAppProgressState : progressStateByAppId[appId];
        const progress = progressState?.data;
        if (!progress) return;

        if (isOrg) {
            setCommonAppProgressState(current => ({ ...current, loading: true }));
        } else {
            setProgressStateByAppId(current => ({
                ...current,
                [appId]: { ...current[appId], loading: true }
            }));
        }

        const triggerIdByStepKey: Record<string, OnboardingEmailTriggerId> = {
            'org-submitted': 'org-submitted',
            'loc-submitted': 'loc-submitted',
            'round1-feedback': 'phase1-feedback-submitted',
            'round2-feedback': 'phase2-feedback-submitted'
        };
        const downstreamTriggerIdsByStepKey: Record<string, OnboardingEmailTriggerId[]> = {
            'org-submitted': ['org-submitted'],
            'loc-submitted': [
                'loc-submitted',
                'testing-ready',
                'phase1-feedback-submitted',
                'phase2-testing-ready',
                'phase2-feedback-submitted',
                'launch-ready',
                'go-live'
            ],
            'round1-feedback': [
                'phase1-feedback-submitted',
                'phase2-testing-ready',
                'phase2-feedback-submitted',
                'launch-ready',
                'go-live'
            ],
            'round2-feedback': ['phase2-feedback-submitted', 'launch-ready', 'go-live']
        };
        let persistedProgress = progress;
        const triggerIdsToUpdate = action === 'skip'
            ? [triggerIdByStepKey[stepKey]].filter(Boolean)
            : (downstreamTriggerIdsByStepKey[stepKey] || [triggerIdByStepKey[stepKey]]).filter(Boolean);

        for (const triggerId of triggerIdsToUpdate) {
            const persistedDetails = parseOnboardingProgressDetails(persistedProgress.detailsJson);
            const currentStatus = persistedDetails.emailTriggers?.[triggerId]?.status;
            if (action === 'unskip' && currentStatus !== 'Skipped') continue;
            if (action === 'skip' && (currentStatus === 'Skipped' || currentStatus === 'Sent')) continue;

            const triggerResult = await updateOnboardingEmailTriggerStatus(appId, triggerId, {
                action: action === 'skip' ? 'skip' : 'reset'
            });
            if (triggerResult.resultType !== 'Ok') {
                const error = triggerResult.errors?.[0] || `Failed to ${action} step.`;
                if (isOrg) {
                    setCommonAppProgressState(current => ({ ...current, loading: false, error }));
                } else {
                    setProgressStateByAppId(current => ({
                        ...current,
                        [appId]: { ...current[appId], loading: false, error }
                    }));
                }
                setGlobalError(error);
                return;
            }

            const responseDetails = parseOnboardingProgressDetails(triggerResult.data?.detailsJson);
            const emailTriggers = {
                ...(responseDetails.emailTriggers || {}),
                ...(persistedDetails.emailTriggers || {})
            };
            emailTriggers[triggerId] = {
                ...(emailTriggers[triggerId] || {}),
                status: action === 'skip' ? 'Skipped' : 'Pending'
            };
            persistedProgress = {
                ...persistedProgress,
                ...(triggerResult.data || {}),
                applicationId: appId,
                detailsJson: serializeOnboardingProgressDetails({
                    ...responseDetails,
                    ...persistedDetails,
                    emailTriggers
                })
            };
        }

        const existingDetails = parseOnboardingProgressDetails(persistedProgress.detailsJson);
        let bypassedSteps = existingDetails.bypassedSteps || [];

        if (action === 'skip') {
            if (!bypassedSteps.includes(stepKey)) {
                bypassedSteps = [...bypassedSteps, stepKey];
            }
        } else {
            const downstreamBypassKeys = stepKey === 'loc-submitted'
                ? ['loc-submitted', 'round1-feedback', 'round2-feedback']
                : stepKey === 'round1-feedback'
                    ? ['round1-feedback', 'round2-feedback']
                    : [stepKey];
            bypassedSteps = bypassedSteps.filter((key: string) => !downstreamBypassKeys.includes(key));
            if (stepKey === 'loc-submitted' || stepKey === 'org-submitted') {
                existingDetails.phase = 'onboarding';
            }
            if (stepKey === 'loc-submitted') {
                existingDetails.isAutomatedTestComplete = false;
            }
        }

        const updatedProgress = {
            ...persistedProgress,
            applicationId: appId,
            detailsJson: serializeOnboardingProgressDetails({
                ...existingDetails,
                bypassedSteps
            })
        };

        const saveAttempt = await saveOnboardingProgressForApplication(
            appId,
            updatedProgress,
            onboardingProgressApiKeysRef.current[appId]
        );
        if (saveAttempt.apiKey) {
            onboardingProgressApiKeysRef.current[appId] = saveAttempt.apiKey;
        }
        const res = saveAttempt.result;
        if (res.resultType === 'Ok') {
            const nextProgress = updatedProgress;
            const application = isOrg ? props.commonApp : (locationApps.find(a => a.id === appId) || activeApp);
            if (isOrg) {
                setCommonAppProgressState({
                    loading: false,
                    data: nextProgress,
                    summary: buildProgressSummary(application as unknown as LocationApplication, nextProgress, undefined),
                    error: ''
                });
            } else {
                setProgressStateByAppId(current => ({
                    ...current,
                    [appId]: {
                        loading: false,
                        data: nextProgress,
                        summary: buildProgressSummary(application as unknown as LocationApplication, nextProgress, orgLocationSpecificFieldKeys),
                        error: ''
                    }
                }));
            }
            setStatusModal(null);
            setGlobalSuccess(`Step ${action === 'skip' ? 'skipped' : 'unskipped'} successfully!`);
            setTimeout(() => setGlobalSuccess(''), 3000);
        } else {
            const error = res.errors?.[0] || `Failed to ${action} step.`;
            if (isOrg) {
                setCommonAppProgressState(current => ({ ...current, loading: false, error }));
            } else {
                setProgressStateByAppId(current => ({
                    ...current,
                    [appId]: { ...current[appId], loading: false, error }
                }));
            }
            setGlobalError(error);
        }
    };

    const handleUnlockOrgOnboarding = async () => {
        const application = props.commonApp;
        if (!application) return;

        clearFeedback();
        const progress = commonAppProgressState.data;
        if (!progress) return;

        const updatedProgress: OnboardingProgressModel = {
            ...progress,
            isComplete: false,
            percentComplete: 99,
            lastUpdated: new Date().toISOString()
        };

        setCommonAppProgressState(current => ({ ...current, loading: true }));
        const res = await updateOnboardingProgress(updatedProgress);
        if (res.resultType === 'Ok') {
            const nextProgress = res.data || updatedProgress;
            setCommonAppProgressState({
                loading: false,
                data: nextProgress,
                summary: buildProgressSummary(application as LocationApplication, nextProgress, undefined),
                error: ''
            });

            let url = commonAppLinkState.url;
            if (!url) {
                const linkRes = await generateOnboardingLink(application.id);
                if (linkRes.resultType === 'Ok' && linkRes.data) {
                    url = `${getOnboardingBaseUrl()}/${linkRes.data}`;
                    setCommonAppLinkState({
                        loading: false,
                        url,
                        error: '',
                        copied: true
                    });
                }
            }

            if (url) {
                try {
                    await navigator.clipboard.writeText(url);
                    setCommonAppLinkState(current => ({ ...current, copied: true, url }));
                    setGlobalSuccess(`Successfully unlocked ${application.name} Onboarding Form and copied the link to clipboard!`);
                    setTimeout(() => {
                        setCommonAppLinkState(current => ({ ...current, copied: false }));
                    }, 2000);
                } catch (_) {
                    setGlobalSuccess(`Successfully unlocked ${application.name} Onboarding Form! (Failed to auto-copy link to clipboard).`);
                }
            } else {
                setGlobalSuccess(`Successfully unlocked ${application.name} Onboarding Form!`);
            }
        } else {
            const error = res.errors?.[0] || 'Failed to unlock organization onboarding.';
            setCommonAppProgressState(current => ({ ...current, loading: false, error }));
            setGlobalError(error);
        }
    };

    const openDeleteModal = (apiKey: ApiKeyModel) => {
        setDeleteError('');
        setApiKeyToDelete(apiKey);
    };

    const openProgressDetails = async (application: ApplicationModel, summary: ProgressSummary, progress?: OnboardingProgressModel | null) => {
        const isCommon = application.id === props.commonApp?.id;
        let latestState: ProgressState;

        if (isCommon) {
            setCommonAppProgressState(current => ({ ...current, loading: true, error: '' }));
            latestState = await fetchProgressStateForApplication(application as LocationApplication);
            setCommonAppProgressState(current => ({ ...current, ...latestState }));
        } else {
            latestState = await loadApplicationProgressForApp(application as LocationApplication);
        }

        const nextSummary = latestState.summary || summary;
        const nextProgress = latestState.data === undefined ? progress : latestState.data;

        if (!latestState.error && nextSummary) {
            setProgressDetailsDialog({ application: application as LocationApplication, summary: nextSummary, progress: nextProgress });
            return;
        }

        if (summary) {
            setProgressDetailsDialog({ application: application as LocationApplication, summary, progress });
        }
    };

    const closeProgressDetails = () => {
        setProgressDetailsDialog(null);
    };

    const openOutboundResponses = async (application: LocationApplication, progress?: OnboardingProgressModel | null) => {
        setOutboundResponsesDialog({
            application,
            progress,
            telephonyDeployments: telephonyDeploymentsByAppId[application.id],
            loadingDeployments: !telephonyDeploymentsByAppId[application.id]
        });
        try {
            const res = await getTelephonyDeployments(application.id);
            if (res.resultType === 'Ok' && res.data) {
                setTelephonyDeploymentsByAppId(prev => ({
                    ...prev,
                    [application.id]: res.data
                }));
                setOutboundResponsesDialog(current => current && current.application.id === application.id ? {
                    ...current,
                    telephonyDeployments: res.data,
                    loadingDeployments: false
                } : current);
            } else {
                setOutboundResponsesDialog(current => current && current.application.id === application.id ? {
                    ...current,
                    telephonyDeployments: current.telephonyDeployments || [],
                    loadingDeployments: false
                } : current);
            }
        } catch (err) {
            setOutboundResponsesDialog(current => current && current.application.id === application.id ? {
                ...current,
                telephonyDeployments: current.telephonyDeployments || [],
                loadingDeployments: false
            } : current);
        }
    };

    const closeOutboundResponses = () => {
        setOutboundResponsesDialog(null);
    };

    const openTestingFeedback = async (application: ApplicationModel, summary: ProgressSummary, progress?: OnboardingProgressModel | null) => {
        const isCommon = application.id === props.commonApp?.id;
        let latestState: ProgressState;

        if (isCommon) {
            setCommonAppProgressState(current => ({ ...current, loading: true, error: '' }));
            latestState = await fetchProgressStateForApplication(application as LocationApplication);
            setCommonAppProgressState(current => ({ ...current, ...latestState }));
        } else {
            latestState = await loadApplicationProgressForApp(application as LocationApplication);
        }
        const nextSummary = latestState.summary || summary;
        const nextProgress = latestState.data === undefined ? progress : latestState.data;

        const resolution = getTestingFeedbackResolutionState(nextProgress);
        setFeedbackFixed(resolution.fixedStatus);
        setFeedbackNotes(resolution.internalFixNotes);

        if (!latestState.error && nextSummary) {
            setTestingFeedbackDialog({ application: application as LocationApplication, summary: nextSummary, progress: nextProgress });
            return;
        }

        if (summary) {
            setTestingFeedbackDialog({ application: application as LocationApplication, summary, progress });
        }
    };

    const closeTestingFeedback = () => {
        setTestingFeedbackDialog(null);
        setDialogError('');
        setDialogSuccess('');
    };

    const handleSaveFeedbackProgress = async () => {
        if (!testingFeedbackDialog?.application) {
            return;
        }
        setIsSavingFeedback(true);
        setDialogError('');
        setDialogSuccess('');

        const app = testingFeedbackDialog.application;
        const progress = testingFeedbackDialog.progress || progressStateByAppId[app.id]?.data || {
            applicationId: app.id,
            detailsJson: '{}'
        };

        try {
            const existingDetails = parseOnboardingProgressDetails(progress.detailsJson);
            const updatedProgress = {
                ...progress,
                applicationId: app.id,
                detailsJson: serializeOnboardingProgressDetails(setTestingFeedbackResolutionState(existingDetails, {
                    fixedStatus: feedbackFixed,
                    internalFixNotes: feedbackNotes
                }))
            };
            const saveAttempt = await saveOnboardingProgressForApplication(
                app.id,
                updatedProgress,
                onboardingProgressApiKeysRef.current[app.id]
            );
            if (saveAttempt.apiKey) {
                onboardingProgressApiKeysRef.current[app.id] = saveAttempt.apiKey;
            }
            const res = saveAttempt.result;
            if (res.resultType === 'Ok' && res.data) {
                const verificationResult = await getOnboardingProgressForApplication(app.id);
                if (verificationResult.resultType !== 'Ok' || !verificationResult.data) {
                    setDialogError(verificationResult.errors?.[0] || 'The save completed, but the persisted feedback could not be verified.');
                    return;
                }

                const persistedResolution = getTestingFeedbackResolutionState(verificationResult.data);
                const fixedKeys = Object.keys(feedbackFixed);
                const noteKeys = Object.keys(feedbackNotes);
                const fixedStatusMatches = fixedKeys.length === Object.keys(persistedResolution.fixedStatus).length
                    && fixedKeys.every(stepId => persistedResolution.fixedStatus[stepId] === feedbackFixed[stepId]);
                const internalNotesMatch = noteKeys.length === Object.keys(persistedResolution.internalFixNotes).length
                    && noteKeys.every(stepId => persistedResolution.internalFixNotes[stepId] === feedbackNotes[stepId]);

                if (!fixedStatusMatches || !internalNotesMatch) {
                    setDialogError('The server did not retain the feedback fixes and notes. Please try again.');
                    return;
                }

                const nextProgress = verificationResult.data;
                const nextSummary = buildProgressSummary(app, nextProgress, orgLocationSpecificFieldKeys);
                setProgressStateByAppId(current => ({
                    ...current,
                    [app.id]: {
                        ...current[app.id],
                        data: nextProgress,
                        summary: nextSummary
                    }
                }));
                setTestingFeedbackDialog(current => current ? { ...current, progress: nextProgress, summary: nextSummary || current.summary } : null);
                setDialogSuccess('Feedback fixes and notes saved successfully!');
                setTimeout(() => setDialogSuccess(''), 3000);
            } else {
                setDialogError(res.errors?.[0] || 'Failed to save feedback fixes.');
                setTimeout(() => setDialogError(''), 3000);
            }
        } catch (error) {
            setDialogError(String(error));
        } finally {
            setIsSavingFeedback(false);
        }
    };

    const handleResolveRound = async () => {
        if (!testingFeedbackDialog?.application) {
            return;
        }
        setIsResolvingRound(true);
        setDialogError('');
        setDialogSuccess('');

        const app = testingFeedbackDialog.application;
        const progress = testingFeedbackDialog.progress || progressStateByAppId[app.id]?.data || {
            applicationId: app.id,
            detailsJson: '{}'
        };

        const existingDetails = parseOnboardingProgressDetails(progress.detailsJson);
        const testing = (existingDetails.testing || {}) as any;
        const rounds = testing.rounds || [];
        const currentRoundNumber = rounds.length + 1;
        const targetRounds = parseInt(testing.targetRounds || '2', 10);

        const newRound = {
            roundNumber: currentRoundNumber,
            completedAt: new Date().toISOString(),
            feedback: testing.stepFeedbackByStepId || {},
            fixedStatus: feedbackFixed,
            fixNotes: feedbackNotes
        };

        const nextRounds = [...rounds, newRound];
        const completedAt = nextRounds.length >= targetRounds ? new Date().toISOString() : undefined;
        const opensAnotherRound = nextRounds.length < targetRounds;
        const defaultTestingStepId = detectFlowType(app) === 'healthcare' ? 'greeting-office-info' : 'greeting';

        const updatedTesting = {
            ...testing,
            rounds: nextRounds,
            completedAt,
            stepFeedbackByStepId: {},
            stepNoFeedbackByStepId: {},
            completedStepIds: [],
            currentStepId: opensAnotherRound ? defaultTestingStepId : testing.currentStepId,
            startedAt: opensAnotherRound ? undefined : testing.startedAt
        };

        const updatedProgress = {
            ...progress,
            applicationId: app.id,
            detailsJson: serializeOnboardingProgressDetails(setTestingFeedbackResolutionState({
                ...existingDetails,
                testing: updatedTesting
            }, {
                fixedStatus: {},
                internalFixNotes: {}
            }))
        };

        const saveAttempt = await saveOnboardingProgressForApplication(
            app.id,
            updatedProgress,
            onboardingProgressApiKeysRef.current[app.id]
        );
        if (saveAttempt.apiKey) {
            onboardingProgressApiKeysRef.current[app.id] = saveAttempt.apiKey;
        }
        const res = saveAttempt.result;
        setIsResolvingRound(false);
        if (res.resultType === 'Ok' && res.data) {
            const nextProgress = res.data;
            const nextSummary = buildProgressSummary(app, nextProgress, orgLocationSpecificFieldKeys);
            setProgressStateByAppId(current => ({
                ...current,
                [app.id]: {
                    ...current[app.id],
                    data: nextProgress,
                    summary: nextSummary
                }
            }));
            setTestingFeedbackDialog(current => current ? { ...current, progress: nextProgress, summary: nextSummary || current.summary } : null);
            setDialogSuccess(opensAnotherRound
                ? `Successfully resolved fixes and opened Next Round!`
                : `Successfully resolved round!`);
            setTimeout(() => setDialogSuccess(''), 3000);
            closeTestingFeedback();
        } else {
            setDialogError(res.errors?.[0] || 'Failed to resolve round.');
            setTimeout(() => setDialogError(''), 3000);
        }
    };

    const closeDeleteModal = () => {
        setDeleteError('');
        setApiKeyToDelete(null);
    };

    const handleDeleteLink = async () => {
        if (!apiKeyToDelete?.id) {
            setDeleteError('Unable to delete onboarding link without an API key id.');
            return;
        }

        clearFeedback();
        setDeleteError('');

        const result = await props.apiKeyContainer.deleteApiKey(apiKeyToDelete.id);
        if (result.resultType !== 'Ok') {
            setDeleteError(result.errors?.[0] ?? 'Failed to delete onboarding link.');
            return;
        }

        await loadOnboardingApiKeys(false);
        closeDeleteModal();
    };

    const getExpirationLabel = (apiKey: ApiKeyModel) => {
        if (!apiKey.expirationDate) {
            return 'Never';
        }
        return moment(apiKey.expirationDate).format('MM/DD/YYYY');
    };

    // User/Gating Check
    const isAccessDenied = props.userContainer.state.currentUser?.canCreateOrganizations === false;

    if (isAccessDenied) {
        return (
            <SettingsCard title="Access Denied" icon={lockShieldIcon}>
                <AccessDeniedContainer>
                    <img src={lockShieldIcon} alt="Access Denied" />
                    <h3>Access Denied</h3>
                    <p>
                        You do not have permission to view or manage onboarding links or progress for this organization.
                    </p>
                </AccessDeniedContainer>
            </SettingsCard>
        );
    }

    // Memoized Organization Onboarding Status
    const orgOnboardingStatus = useMemo(() => {
        if (!props.commonApp) return 'Not Invited';
        const progress = commonAppProgressState.data;
        const isComplete = !!progress?.isComplete;
        const percent = getDisplayedPercentComplete(props.commonApp as unknown as LocationApplication, progress);
        const isSharedConfigComplete = !props.appliedTemplateContainer?.state.perApplicationAppliedTemplates?.find(
            t => t.applicationId === props.commonApp?.id
        )?.appliedTemplates?.some(template => {
            return (props.appliedTemplateContainer?.getIncompleteSections(template) ?? []).length > 0;
        }) && (props.appliedTemplateContainer?.state.perApplicationAppliedTemplates?.find(
            t => t.applicationId === props.commonApp?.id
        )?.appliedTemplates?.length ?? 0) > 0;

        const parsedDetails = parseOnboardingProgressDetails(progress?.detailsJson);
        const emailTriggers = parsedDetails.emailTriggers || {};
        const emailSent = emailTriggers['org-launched']?.status === 'Sent';
        const matchingApiKey = onboardingApiKeys.find(key => key.name === `Onboarding - ${props.commonApp?.name}` || key.name === `Testing - ${props.commonApp?.name}` || key.name?.includes(props.commonApp?.name));
        const hasApiKey = !!matchingApiKey;

        return getLocationTimelineStatus(hasApiKey, emailSent, percent, isComplete || isSharedConfigComplete, progress);
    }, [props.commonApp, commonAppProgressState, props.appliedTemplateContainer?.state.perApplicationAppliedTemplates, onboardingApiKeys]);

    // Map each Location App to its status and metadata
    const locationAppsWithStatus = useMemo(() => {
        const sortFn = (a: LocationApplication, b: LocationApplication) => {
            const aLive = !!a.isLive;
            const bLive = !!b.isLive;

            // 1. Targets (not live) first, then lives (live)
            if (!aLive && bLive) return -1;
            if (aLive && !bLive) return 1;

            const getValidDateValue = (dateStr: string | null | undefined) => {
                if (!dateStr) return null;
                const m = moment.utc(dateStr);
                return m.isValid() ? m.valueOf() : null;
            };

            // 2. Both have same live status
            if (!aLive) {
                // Both not live (targets): sort by target launch date ascending (earliest/soonest first)
                const dateA = getValidDateValue(a.targetLaunchDate);
                const dateB = getValidDateValue(b.targetLaunchDate);

                if (dateA !== null && dateB !== null) {
                    const diff = dateA - dateB;
                    if (diff !== 0) return diff;
                } else if (dateA !== null) {
                    return -1;
                } else if (dateB !== null) {
                    return 1;
                }
            } else {
                // Both live (lives): sort by launch date descending (most recent first)
                const dateA = getValidDateValue(a.launchDate);
                const dateB = getValidDateValue(b.launchDate);

                if (dateA !== null && dateB !== null) {
                    const diff = dateB - dateA;
                    if (diff !== 0) return diff;
                } else if (dateA !== null) {
                    return -1;
                } else if (dateB !== null) {
                    return 1;
                }
            }

            // 3. Fallback to alphabetical sorting by name
            return (a.name || '').localeCompare(b.name || '');
        };

        const mappedApps = [...(props.locationApps || [])]
            .filter(isLocationApplication)
            .map(rawApp => ({
                ...rawApp,
                ...(localAppOverrides[rawApp.id] || {})
            } as unknown as LocationApplication));

        const sortedApps = mappedApps.sort(sortFn);

        return sortedApps.map(app => {
            const linkState = linkStateByAppId[app.id] ?? {};
            const progressState = progressStateByAppId[app.id] || ({} as ProgressState);
            const progress = progressState.data;
            const percentComplete = getDisplayedPercentComplete(app, progress);
            const parsedDetails = parseOnboardingProgressDetails(progress?.detailsJson);
            const emailTriggers = parsedDetails.emailTriggers || {};
            const matchingApiKey = onboardingApiKeys.find(key => key.name === `Onboarding - ${app.name}` || key.name === `Testing - ${app.name}` || key.name?.includes(app.name));

            const hasApiKey = !!matchingApiKey;
            const emailSent = emailTriggers['loc-launched']?.status === 'Sent';
            const status = getLaunchReadinessTab(app, progress, progressState, props.commonApp, commonAppProgressState.data, isOrgOnboardingComplete);

            return {
                app,
                status,
                percentComplete,
                progressState,
                progress,
                matchingApiKey,
                isLocAtLeastOneFieldComplete: isAtLeastOneFieldComplete(progressState)
            };
        });
    }, [props.locationApps, linkStateByAppId, progressStateByAppId, onboardingApiKeys, localAppOverrides, props.commonApp, commonAppProgressState, isOrgOnboardingComplete]);

    const failedLocationSyncs = useMemo(() => locationAppsWithStatus
        .map(({ app, progress }) => ({
            app,
            sync: parseOnboardingProgressDetails(progress?.detailsJson).locationSync
        }))
        .filter(({ sync }) => sync?.status === 'failed'), [locationAppsWithStatus]);

    const searchLower = appSearch.trim().toLowerCase();

    // Check if Organization matches Search
    const isOrgSearchMatch = useMemo(() => {
        if (!props.commonApp) return false;
        if (!searchLower) return true;
        const orgName = (props.commonApp.name || '').toLowerCase();
        return orgName.includes(searchLower) ||
               'organization'.includes(searchLower) ||
               'organization onboarding'.includes(searchLower);
    }, [props.commonApp, searchLower]);

    // Check if Locations match Search
    const searchFilteredLocations = useMemo(() => {
        if (!searchLower) return locationAppsWithStatus;
        return locationAppsWithStatus.filter(loc => (loc.app.name || '').toLowerCase().includes(searchLower));
    }, [locationAppsWithStatus, searchLower]);

    // Tab counts mapped to statuses
    const tabCounts = useMemo(() => {
        let all = 0;
        let notStarted = 0;
        let inProgress = 0;
        let complete = 0;

        if (props.commonApp && isOrgSearchMatch) {
            all++;
            const orgTab = getLaunchReadinessTab(props.commonApp, commonAppProgressState.data, commonAppProgressState, props.commonApp, commonAppProgressState.data, isOrgOnboardingComplete);
            if (orgTab === 'Not Started') notStarted++;
            else if (orgTab === 'In Progress') inProgress++;
            else if (orgTab === 'Complete') complete++;
        }

        searchFilteredLocations.forEach(loc => {
            all++;
            const locTab = getLaunchReadinessTab(loc.app, loc.progress, loc.progressState, props.commonApp, commonAppProgressState.data, isOrgOnboardingComplete);
            if (locTab === 'Not Started') notStarted++;
            else if (locTab === 'In Progress') inProgress++;
            else if (locTab === 'Complete') complete++;
        });

        return {
            All: all,
            'Not Started': notStarted,
            'In Progress': inProgress,
            Complete: complete
        };
    }, [props.commonApp, isOrgSearchMatch, commonAppProgressState, searchFilteredLocations, isOrgOnboardingComplete]);

    const renderOrgCardBody = () => {
        const application = props.commonApp;
        if (!application) return null;
        const linkState = commonAppLinkState;
        const progressState = commonAppProgressState;
        const progress = progressState.data;
        const percentComplete = getDisplayedPercentComplete(application as LocationApplication, progress);
        const onboardingPercentComplete = getDisplayedOnboardingPercentComplete(progress);
        const isComplete = !!progress?.isComplete;
        const parsedDetails = parseOnboardingProgressDetails(progress?.detailsJson);
        const emailTriggers = parsedDetails.emailTriggers || {};
        const matchingApiKey = onboardingApiKeys.find(key => key.name === `Onboarding - ${application.name}` || key.name === `Testing - ${application.name}` || key.name?.includes(application.name));

        const isOrgAtLeastOneFieldComplete = isAtLeastOneFieldComplete(commonAppProgressState);

        return (
            <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {isComplete ? (
                        <LockedOverlay>
                            <LockHeader>
                                <div>
                                    <LockTitle>Organization Onboarding Locked & Completed</LockTitle>
                                    <LockMeta>Submitted on {moment(progress?.lastUpdated).format('MM/DD/YYYY [at] hh:mm A')}</LockMeta>
                                </div>
                            </LockHeader>
                            <LockActions>
                                <Button
                                    themes={['primary-small']}
                                    text="Unlock Org Form for Edits & Copy Link"
                                    type="button"
                                    disabled={!isOrgAdmin || isExistingClient}
                                    onClick={handleUnlockOrgOnboarding}
                                />
                                 {matchingApiKey && (
                                     <TooltipWrapper
                                         text="This removes the underlying organization API key and immediately invalidates any onboarding URLs that rely on it."
                                         place="top"
                                         maxWidth={300}
                                     >
                                          <Button
                                              themes={['secondary-small', 'destructive']}
                                              className={disableLinkButtonStyle}
                                              text="Disable Link"
                                              type="button"
                                              disabled={isExistingClient}
                                              onClick={() => openDeleteModal(matchingApiKey)}
                                          />
                                     </TooltipWrapper>
                                 )}
                            </LockActions>
                        </LockedOverlay>
                    ) : (
                        <InlineControlGroup>
                            <InlineButtonGroup>
                                <Button
                                    themes={['primary-small']}
                                    text="See Onboarding Responses"
                                    type="button"
                                    disabled={!isOrgAtLeastOneFieldComplete}
                                    onClick={() => openProgressDetails(application, progressState.summary!, progress)}
                                />
                                {matchingApiKey && (
                                    <TooltipWrapper
                                        text="This removes the underlying organization API key and immediately invalidates any onboarding URLs that rely on it."
                                        place="top"
                                        maxWidth={300}
                                    >
                                         <Button
                                             themes={['secondary-small', 'destructive']}
                                             className={disableLinkButtonStyle}
                                             text="Disable Link"
                                             type="button"
                                             disabled={isExistingClient}
                                             onClick={() => openDeleteModal(matchingApiKey)}
                                         />
                                    </TooltipWrapper>
                                )}
                             </InlineButtonGroup>
                        </InlineControlGroup>
                    )}

                    {progress && shouldDisplayOnboardingProgress(onboardingPercentComplete) && (
                        <ProgressBarSection>
                            <ProgressBarHeader>
                                <ProgressBarLabel>Onboarding Completion Progress</ProgressBarLabel>
                                <ProgressBarPercent>{onboardingPercentComplete}%</ProgressBarPercent>
                            </ProgressBarHeader>
                            <ProgressBarTrack>
                                <ProgressBarFill percent={onboardingPercentComplete} />
                            </ProgressBarTrack>
                        </ProgressBarSection>
                    )}
                </div>
            </div>
        );
    };

    return (
        <SettingsCard
            title="Onboarding"
            icon={linkIcon}
            isLoading={props.isLoading || isProgressLoading}
        >
            <CardContent>
                <style dangerouslySetInnerHTML={{ __html: bounceTooltipKeyframes }} />
                {globalError && <Notice type="error">{globalError}</Notice>}
                {globalSuccess && <Notice type="success">{globalSuccess}</Notice>}
                {failedLocationSyncs.length > 0 && (
                    <Notice type="error">
                        Location onboarding was completed, but assistant sync failed for: {failedLocationSyncs.map(({ app, sync }) => `${app.name}${sync?.error ? ` (${sync.error})` : ''}`).join(', ')}. The launch timeline was not blocked; sync these assistants manually.
                    </Notice>
                )}

                {/* Action Required Section */}
                {props.commonApp && (() => {
                    const orgAction = getOrgActionItem();
                    const activeActions: OnboardingActionItem[] = [];

                    if (orgAction && orgAction.type === 'action_needed') {
                        activeActions.push({
                            id: 'org',
                            action: orgAction,
                            isOrg: true,
                            app: props.commonApp!
                        });
                    }

                    locationAppsWithStatus.forEach(({ app, status, progress, progressState }, idx) => {
                        const action = getLocationActionItem({ app, status, progress, progressSummary: progressState.summary });
                        if (action && action.type === 'action_needed') {
                            activeActions.push({
                                id: app.id,
                                action,
                                isOrg: false,
                                app,
                                index: idx
                            });
                        }

                        const locDetails = parseOnboardingProgressDetails(progress?.detailsJson);
                        const isSparkDashboardInvited = !!locDetails.isSparkDashboardInvited;

                        const hasReachedLaunchReady = action?.triggerId === 'launch-ready'
                            || action?.triggerId === 'go-live'
                            || action?.type === 'completed';
                        if (hasReachedLaunchReady && !app.isLive && !isSparkDashboardInvited) {
                            activeActions.push({
                                id: `${app.id}-spark-dashboard-invite`,
                                action: {
                                    prefix: 'Invite Client to Spark Dashboard for ',
                                    suffix: '',
                                    type: 'action_needed',
                                    buttonText: 'Invite Client',
                                    onClick: () => setSparkInviteApplication(app)
                                },
                                isOrg: false,
                                app,
                                index: idx
                            });
                        }

                        // Top-level action required: Review outbound onboarding form and enable outbound
                        const hasOutboundCalling = app.featureFlags?.some(flag => flag.id === OutboundCallFeatureFlagId);
                        const outboundOnboarding = locDetails.outboundOnboarding || {};
                        const isOutboundFormCompleted = outboundOnboarding.status === 'Submitted' || outboundOnboarding.status === 'Complete';

                        if (hasOutboundCalling && isOutboundFormCompleted) {
                            const appDeployments = telephonyDeploymentsByAppId[app.id] || [];
                            const outboundDeps = appDeployments.filter(d => d.useForOutboundCalling === true);
                            const isOutboundLive = outboundDeps.length > 0 && outboundDeps.some(
                                d => d.outboundCallingEnabled === true || (d.outboundCallingEnabled as any) === 'true'
                            );

                            if (!isOutboundLive) {
                                activeActions.push({
                                    id: `${app.id}-outbound-review-enable`,
                                    action: {
                                        prefix: 'Review outbound onboarding form and enable outbound for ',
                                        suffix: '',
                                        type: 'action_needed',
                                        buttonText: 'Review Responses',
                                        onClick: () => openOutboundResponses(app, progress)
                                    },
                                    isOrg: false,
                                    app,
                                    index: idx
                                });
                            }
                        }
                    });

                    return (
                        <OnboardingActionRequiredSection
                            activeActions={activeActions}
                            isLoading={isOnboardingLoading}
                            onGoToLocation={handleGoToLocation}
                            onGoToOrganization={handleGoToOrganization}
                        />
                    );
                })()}

                {/* CONSOLIDATED ONBOARDING FORMS SECTION */}
                <ContentCollapsingSection
                    title="Launch Readiness"
                    isCollapsedByDefault={false}
                    className={onboardingFormsSectionStyle}
                    bottom={true}
                >
                    <OnboardingFormsBodyContainer>
                        <SearchContainer style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', maxWidth: '100%' }}>
                            <SearchInput
                                type="search"
                                value={appSearch}
                                onChange={(event) => setAppSearch(event.target.value)}
                                placeholder="Search organization or locations by name..."
                                aria-label="Search onboarding forms"
                                style={{ flex: 1, minWidth: '250px' }}
                            />
                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                <Button
                                    themes={['secondary-small']}
                                    text="Manage email triggers"
                                    type="button"
                                    disabled={locationApps.length === 0 || isOnboardingLoading}
                                    onClick={() => setIsBulkEmailTriggerManagerOpen(true)}
                                />
                                <Button
                                    themes={['primary-small']}
                                    text="Send All Onboarding Links"
                                    type="button"
                                    disabled={locationApps.length === 0}
                                    onClick={handleBulkGenerateOnboardingLinks}
                                />
                                {areAllOnboardingLinksComplete ? (
                                    <Button
                                        themes={['primary-small']}
                                        text="Send All Testing Links"
                                        type="button"
                                        disabled={locationApps.length === 0}
                                        onClick={handleBulkGenerateTestingLinks}
                                    />
                                ) : (
                                    <TooltipWrapper
                                        text="Testing links shouldn't be enabled until all onboarding links are complete."
                                        place="top"
                                        maxWidth={300}
                                    >
                                        <div style={{ display: 'inline-block' }}>
                                            <Button
                                                themes={['primary-small']}
                                                text="Send All Testing Links"
                                                type="button"
                                                disabled={true}
                                            />
                                        </div>
                                    </TooltipWrapper>
                                )}
                            </div>
                        </SearchContainer>

                        <TabContainer>
                            {(['All', 'Not Started', 'In Progress', 'Complete'] as const).map((tab) => {
                                const isActive = activeOnboardingTab === tab;
                                const count = tabCounts[tab];
                                return (
                                    <TabButton
                                        key={tab}
                                        isActive={isActive}
                                        onClick={() => setActiveOnboardingTab(tab)}
                                        type="button"
                                    >
                                        {tab} <TabCount isActive={isActive}>{count}</TabCount>
                                    </TabButton>
                                );
                            })}
                        </TabContainer>

                        {/* Organization & Location Collapsible List */}
                        {tabCounts[activeOnboardingTab] > 0 && (
                            <LocationStagesContainer style={{ gap: '16px' }}>
                             {/* Organization Onboarding Row if applicable */}
                              {props.commonApp && isOrgSearchMatch && (activeOnboardingTab === 'All' || getLaunchReadinessTab(props.commonApp, commonAppProgressState.data, commonAppProgressState, props.commonApp, commonAppProgressState.data, isOrgOnboardingComplete) === activeOnboardingTab) && (() => {
                                const application = props.commonApp;
                                const progress = commonAppProgressState.data;
                                const isOrgAtLeastOneFieldComplete = isAtLeastOneFieldComplete(commonAppProgressState);
                                const matchingApiKey = onboardingApiKeys.find(key => key.name === `Onboarding - ${application.name}` || key.name === `Testing - ${application.name}` || key.name?.includes(application.name));

                                  const getOrgButtons = () => {
                                      const buttons = [];

                                      const orgProgress = commonAppProgressState.data;
                                      const orgDetails = parseOnboardingProgressDetails(orgProgress?.detailsJson);
                                      const orgTriggers = orgDetails.emailTriggers || {};
                                      const isLaunched = orgTriggers['org-launched']?.status === 'Sent';

                                      const orgLaunchedState = orgTriggers['org-launched'];
                                      const isOrgLaunchedSkipped = orgLaunchedState?.status === 'Skipped';
                                      const isOrgLaunchedSentManually = !!orgLaunchedState?.sentManually;

                                       if (progress?.isComplete) {
                                          buttons.push({
                                              text: "Onboarding Responses",
                                              onClick: () => openProgressDetails(application, commonAppProgressState.summary!, progress),
                                              disabled: !isOrgAtLeastOneFieldComplete,
                                              themes: ['primary-small' as const]
                                          });
                                      } else if (isOrgLaunchedSkipped || isOrgLaunchedSentManually) {
                                          buttons.push({
                                              text: getEmailTriggerButtonText('org-launched', orgTriggers),
                                              onClick: () => handleOpenOnboardingPreview(props.commonApp as LocationApplication, 'org-launched', progress),
                                              disabled: isExistingClient,
                                              themes: ['primary-small' as const]
                                          });

                                          if (isOrgAtLeastOneFieldComplete) {
                                              buttons.push({
                                                  text: "Onboarding Responses",
                                                  onClick: () => openProgressDetails(application, commonAppProgressState.summary!, progress),
                                                  disabled: false,
                                                  themes: ['secondary-small' as const]
                                              });
                                          }
                                      } else if (isLaunched) {
                                          let nextTrigger: OnboardingEmailTriggerId = 'org-launched';
                                          if (orgTriggers['org-reminder']?.status !== 'Sent') {
                                              nextTrigger = 'org-reminder';
                                          } else if (orgTriggers['telephony-reminder']?.status !== 'Sent') {
                                              nextTrigger = 'telephony-reminder';
                                          }

                                          buttons.push({
                                              text: getEmailTriggerButtonText(nextTrigger, orgTriggers),
                                              onClick: () => handleOpenOnboardingPreview(props.commonApp as LocationApplication, nextTrigger, progress),
                                              disabled: isExistingClient,
                                              themes: isOrgAtLeastOneFieldComplete ? ['secondary-small' as const] : ['primary-small' as const]
                                          });

                                          if (isOrgAtLeastOneFieldComplete) {
                                              buttons.push({
                                                  text: "Onboarding Responses",
                                                  onClick: () => openProgressDetails(application, commonAppProgressState.summary!, progress),
                                                  disabled: false,
                                                  themes: ['primary-small' as const]
                                              });
                                          }
                                      } else {
                                          buttons.push({
                                              text: getEmailTriggerButtonText('org-launched', orgTriggers),
                                              onClick: () => handleOpenOnboardingPreview(props.commonApp as LocationApplication, 'org-launched', progress),
                                              disabled: isExistingClient,
                                              themes: ['primary-small' as const]
                                          });

                                          if (isOrgAtLeastOneFieldComplete) {
                                              buttons.push({
                                                  text: "Onboarding Responses",
                                                  onClick: () => openProgressDetails(application, commonAppProgressState.summary!, progress),
                                                  disabled: false,
                                                  themes: ['secondary-small' as const]
                                              });
                                          }
                                      }

                                      return buttons.reverse();
                                  };

                                 const bounceTooltipKeyframes = `
                                     @keyframes bounceTooltip {
                                         0% {
                                             opacity: 0;
                                             transform: translateX(-50%) translateY(0) scale(0.9);
                                         }
                                         50% {
                                             opacity: 1;
                                             transform: translateX(-50%) translateY(-12px) scale(1.05);
                                         }
                                         100% {
                                             opacity: 1;
                                             transform: translateX(-50%) translateY(-8px) scale(1);
                                         }
                                     }
                                 `;

                                  const buttons = getOrgButtons();
                                  const isOrgFormComplete = !!progress?.isComplete || isOrgOnboardingComplete;
                                  const isOrgLocked = isOrgFormComplete && isExistingClient;

                                   return (
                                       <OrgLineItem
                                          id="organization-onboarding-section"
                                          key="organization-onboarding"
                                          bottom={searchFilteredLocations.filter(loc => activeOnboardingTab === 'All' || loc.status === activeOnboardingTab).length === 0}
                                       >
                                          <style dangerouslySetInnerHTML={{ __html: bounceTooltipKeyframes }} />
                                           <div className="left-content">
                                               <div className="org-icon-container">
                                                   <img src={linkIcon} alt="Organization" />
                                               </div>
                                                <div className="title-block">
                                                    <UrlLabelRow>
                                                        <h4>Organization Onboarding</h4>
                                                       <TooltipWrapper
                                                           text="The answers in this form map to the Shared Configuration."
                                                           place="top"
                                                           maxWidth={280}
                                                       >
                                                           <UrlInfoIcon src={infoIcon} alt="info" />
                                                       </TooltipWrapper>
                                                       {isOrgLocked && (
                                                           <TooltipWrapper
                                                               text="Organization onboarding is locked for editing because this organization has live locations or location launches in progress. To make organization-level changes, use the Configuration tab and Spark forms."
                                                               place="top"
                                                               maxWidth={320}
                                                           >
                                                               <OrgLockIconContainer
                                                                   role="img"
                                                                   aria-label="Organization onboarding is locked for editing"
                                                               >
                                                                   <LockIconSvg size={13} color="#64748b" />
                                                               </OrgLockIconContainer>
                                                           </TooltipWrapper>
                                                       )}
                                                   </UrlLabelRow>
                                               </div>
                                           </div>
                                         <div className="right-content">
                                             {buttons.map((btn, idx) => {
                                                 const isCopyBtn = btn.text === "Copy Link" || btn.text === "Generate & Copy Link";
                                                 return (
                                                     <div key={idx} style={{ position: 'relative' }}>
                                                          <Button
                                                              themes={btn.themes}
                                                              text={btn.text}
                                                              type="button"
                                                              loading={btn.loading}
                                                              disabled={btn.disabled || btn.loading}
                                                              onClick={btn.onClick}
                                                          />
                                                         {isCopyBtn && commonAppLinkState.copied && (
                                                             <div style={{
                                                                 position: 'absolute',
                                                                 bottom: '100%',
                                                                 left: '50%',
                                                                 transform: 'translateX(-50%) translateY(-8px)',
                                                                 backgroundColor: '#1e293b',
                                                                 color: '#ffffff',
                                                                 padding: '6px 12px',
                                                                 borderRadius: '6px',
                                                                 fontSize: '11px',
                                                                 fontWeight: 600,
                                                                 whiteSpace: 'nowrap',
                                                                 boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                                                 zIndex: 10,
                                                                 animation: 'bounceTooltip 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards',
                                                                 pointerEvents: 'none'
                                                             }}>
                                                                 copied to clipboard
                                                                 <div style={{
                                                                     position: 'absolute',
                                                                     top: '100%',
                                                                     left: '50%',
                                                                     transform: 'translateX(-50%)',
                                                                     borderWidth: '5px',
                                                                     borderStyle: 'solid',
                                                                     borderColor: '#1e293b transparent transparent transparent'
                                                                 }} />
                                                             </div>
                                                         )}
                                                     </div>
                                                 );
                                             })}
                                         </div>
                                     </OrgLineItem>
                                 );
                            })()}

                            {/* Location Onboarding Rows if applicable */}
                             {searchFilteredLocations
                                .filter(loc => activeOnboardingTab === 'All' || loc.status === activeOnboardingTab)
                                .map((loc, index, arr) => {
                                    const { app, status, percentComplete, progressState, progress, matchingApiKey, isLocAtLeastOneFieldComplete } = loc;

                                      // Right row header info
                                      const titleRightRowComponent = (
                                          <HeaderBadgeContainer>
                                              {progressState.loading && (
                                                  <ProgressSyncIndicator
                                                      role="status"
                                                      aria-label={`Refreshing onboarding progress for ${app.name}`}
                                                      title="Refreshing onboarding progress"
                                                  >
                                                      <ProgressSyncSpinner aria-hidden="true" />
                                                  </ProgressSyncIndicator>
                                              )}
                                              <TooltipWrapper text={getStatusTooltipText(status)} place="top">
                                                 <StatusBadge variant={status}>
                                                     {status}
                                                 </StatusBadge>
                                             </TooltipWrapper>
                                              <TooltipWrapper text={app.isLive ? "Launch date" : "Target launch date"} place="top">
                                                  <LiveBadge isLive={!!app.isLive}>
                                                      <UserLiveIcon 
                                                          size={14} 
                                                          color={app.isLive ? color_colors_growth : color_colors_gold} 
                                                          style={{ marginRight: '6px' }} 
                                                      />
                                                       {app.isLive
                                                           ? (app.launchDate ? moment.utc(app.launchDate).format('MM/DD/YYYY') : 'Live')
                                                           : (app.targetLaunchDate ? moment.utc(app.targetLaunchDate).format('MM/DD/YYYY') : 'TBD')
                                                       }
                                                  </LiveBadge>
                                              </TooltipWrapper>
                                          </HeaderBadgeContainer>
                                     );

                                     return (
                                         <ContentCollapsingSection
                                             key={app.id}
                                             id={`location-section-${app.id}`}
                                             title={app.name}
                                             isCollapsedByDefault={true}
                                             forceOpen={forcedOpenLocations[app.id]}
                                             titleRightRowComponent={titleRightRowComponent}
                                             className={nestedCollapsibleSectionStyle}
                                             bottom={true}
                                         >
                                             <LocationCardBody>
                                                 <div style={{ width: '100%' }}>
                                                       <LocationOnboardingAndTestingCardBody
                                                           app={app}
                                                           progress={progress}
                                                           onUpdateProgress={(updatedProgress) => {
                                                               setProgressStateByAppId(current => ({
                                                                   ...current,
                                                                   [app.id]: {
                                                                       ...current[app.id],
                                                                       data: updatedProgress,
                                                                       summary: buildProgressSummary(app, updatedProgress, orgLocationSpecificFieldKeys)
                                                                   }
                                                               }));
                                                           }}
                                                          orgLocationSpecificFieldKeys={orgLocationSpecificFieldKeys}
                                                          isOrgOnboardingComplete={isOrgOnboardingComplete}
                                                         apiKeyContainer={props.apiKeyContainer}
                                                         memberContainer={props.memberContainer}
                                                         userContainer={props.userContainer}
                                                         organizationId={props.organizationId}
                                                         apiKeyToDelete={apiKeyToDelete}
                                                         setApiKeyToDelete={setApiKeyToDelete}
                                                           percentComplete={percentComplete}
                                                           onSeeOnboardingResponses={() => openProgressDetails(app, progressState.summary!, progress)}
                                                           onSeeOutboundResponses={() => openOutboundResponses(app, progress)}
                                                           onSeeClientTestingFeedback={() => openTestingFeedback(app, progressState.summary!, progress)}
                                                          matchingApiKey={matchingApiKey}
                                                         onDisableLink={() => openDeleteModal(matchingApiKey)}
                                                         isLocAtLeastOneFieldComplete={isLocAtLeastOneFieldComplete}
                                                         testingStatus={getClientTestingStatus(progress)}
                                                         onOpenOnboardingPreview={handleOpenOnboardingPreview}
                                                         onUpdateEmailTrigger={handleUpdateEmailTrigger}
                                                         commonApp={props.commonApp}
                                                         commonAppProgress={commonAppProgressState.data}
                                                         onUpdateCommonProgress={(updatedProgress) => {
                                                             setCommonAppProgressState(current => ({
                                                                 ...current,
                                                                 data: updatedProgress
                                                             }));
                                                         }}
                                                         onSaveProgressForApplication={async (applicationId, updatedProgress) => {
                                                             const saveAttempt = await saveOnboardingProgressForApplication(
                                                                 applicationId,
                                                                 updatedProgress,
                                                                 onboardingProgressApiKeysRef.current[applicationId]
                                                             );
                                                             if (saveAttempt.apiKey) {
                                                                 onboardingProgressApiKeysRef.current[applicationId] = saveAttempt.apiKey;
                                                             }
                                                             return saveAttempt.result;
                                                         }}
                                                          setStatusModal={setStatusModal}
                                                            onOpenLaunchDateModal={handleOpenLaunchDateModal}
                                                            onBypassStep={handleBypassStep}
                                                        />
                                                 </div>
                                             </LocationCardBody>
                                        </ContentCollapsingSection>
                                    );
                                 })}
                            </LocationStagesContainer>
                        )}

                        {/* Empty state when active tab is empty */}
                        {tabCounts[activeOnboardingTab] === 0 && (
                            <EmptyState>
                                No onboarding forms found matching your search or selected status tab.
                            </EmptyState>
                        )}
                    </OnboardingFormsBodyContainer>
                </ContentCollapsingSection>

            </CardContent>

            {isBulkEmailTriggerManagerOpen && (
                <BulkEmailTriggerManagerDialog
                    locations={locationApps.map(application => ({
                        application,
                        progress: progressStateByAppId[application.id]?.data,
                        loading: progressStateByAppId[application.id]?.loading,
                        error: progressStateByAppId[application.id]?.error
                    }))}
                    onClose={() => setIsBulkEmailTriggerManagerOpen(false)}
                    onPersistentDefaultsChange={handlePersistentLocationEmailTriggerDefaults}
                    onProgressUpdated={(application, updatedProgress) => {
                        setProgressStateByAppId(current => ({
                            ...current,
                            [application.id]: {
                                ...current[application.id],
                                loading: false,
                                data: updatedProgress,
                                summary: buildProgressSummary(application, updatedProgress, orgLocationSpecificFieldKeys),
                                error: ''
                            }
                        }));
                    }}
                />
            )}

            {apiKeyToDelete && (
                <ConfirmationDialog
                    title="Delete Onboarding Link"
                    deleteText="Yes, Delete"
                    isLoading={props.apiKeyContainer.state.isLoading}
                    onClose={closeDeleteModal}
                    onConfirm={handleDeleteLink}
                >
                    <DialogContent>
                        <DialogTitle>
                            Delete <b>{apiKeyToDelete.name ?? 'this onboarding link'}</b>?
                        </DialogTitle>
                        <DialogCopy>
                            This removes the underlying organization API key and immediately invalidates any onboarding URLs that rely on it.
                        </DialogCopy>
                        {deleteError && <DialogError>{deleteError}</DialogError>}
                    </DialogContent>
                </ConfirmationDialog>
            )}

            {progressDetailsDialog && (
                <ConfirmationDialog
                    title={progressDetailsDialog.application.id === props.commonApp?.id
                        ? 'Organization Onboarding Form Responses'
                        : `${progressDetailsDialog.application.name} Onboarding Form Responses`}
                    deleteText="Close"
                    warning={true}
                    onClose={closeProgressDetails}
                    onConfirm={closeProgressDetails}
                    size="large"
                    contentScrollable={true}
                    contentClassName={progressDetailsContentClass}
                >
                    <DetailsDialogContent>
                        <DetailsDialogHeader>
                            <DetailsDialogMeta>
                                <ProgressStatusBadge isComplete={getOnboardingProgressPhase(progressDetailsDialog.progress) === 'testing' ? isTestingProgressComplete(progressDetailsDialog.progress) : !!progressDetailsDialog.progress?.isComplete}>
                                    {getProgressStatusLabel(progressDetailsDialog.progress)}
                                </ProgressStatusBadge>
                                <span>{getDisplayedPercentComplete(progressDetailsDialog.application, progressDetailsDialog.progress)}% complete</span>
                                {progressDetailsDialog.progress?.lastUpdated && (
                                    <span>Updated {moment(progressDetailsDialog.progress.lastUpdated).fromNow()}</span>
                                )}
                            </DetailsDialogMeta>
                        </DetailsDialogHeader>

                        {progressDetailsDialog.summary.sectionTitle && (
                            <DetailsCurrentSection>Current section: {progressDetailsDialog.summary.sectionTitle}</DetailsCurrentSection>
                        )}

                        <DetailsSections>
                            {progressDetailsDialog.summary.responseSections.map(section => (
                                <DetailsSection key={section.key}>
                                    <DetailsSectionTitle>{section.title}</DetailsSectionTitle>
                                    <DetailsAnswerList>
                                        {section.items.map(item => (
                                            <DetailsAnswerItem key={item.key}>
                                                <DetailsAnswerLabel>{item.label}</DetailsAnswerLabel>
                                                <DetailsAnswerValueRow>
                                                    <DetailsAnswerValue isMultiline={!!item.isMultiline}>{item.value}</DetailsAnswerValue>
                                                    <CopyValueButton
                                                        type="button"
                                                        onClick={async () => {
                                                            if (!navigator?.clipboard?.writeText) {
                                                                return;
                                                            }
                                                            try {
                                                                await navigator.clipboard.writeText(item.value ?? '');
                                                                setCopiedKey(item.key);
                                                                setTimeout(() => setCopiedKey(null), 1500);
                                                            } catch (error) {
                                                                return;
                                                            }
                                                        }}
                                                        title={copiedKey === item.key ? 'Copied!' : 'Copy to clipboard'}
                                                        aria-label={copiedKey === item.key ? `Copied ${item.label}` : `Copy ${item.label} to clipboard`}
                                                    >
                                                        <img src={copyIcon} alt="" />
                                                    </CopyValueButton>
                                                </DetailsAnswerValueRow>
                                            </DetailsAnswerItem>
                                        ))}
                                    </DetailsAnswerList>
                                </DetailsSection>
                            ))}
                        </DetailsSections>
                    </DetailsDialogContent>
                </ConfirmationDialog>
            )}

            {outboundResponsesDialog && (() => {
                const dialogApp = outboundResponsesDialog.application;
                const dialogProgress = outboundResponsesDialog.progress;
                const details = parseOnboardingProgressDetails(dialogProgress?.detailsJson);
                const outbound = details.outboundOnboarding || {};
                const answers = outbound.answers || {};
                const submitter = outbound.submitter;
                const statusStr = outbound.status || 'Not Started';
                const percent = outbound.percentComplete ?? (statusStr === 'Submitted' || statusStr === 'Complete' ? 100 : 0);
                const isFormComplete = statusStr === 'Submitted' || statusStr === 'Complete';

                const outboundDeployments = (outboundResponsesDialog.telephonyDeployments || []).filter(
                    d => d.useForOutboundCalling === true
                );
                const isOutboundLive = outboundDeployments.length > 0 && outboundDeployments.some(
                    d => d.outboundCallingEnabled === true || (d.outboundCallingEnabled as any) === 'true'
                );

                const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

                const generalItems: Array<{ key: string; label: string; value: string; isMultiline?: boolean }> = [
                    {
                        key: 'caller-id',
                        label: 'Caller ID Phone Number',
                        value: answers.callerIdPhoneNumber ? answers.callerIdPhoneNumber : 'Not Selected'
                    },
                    {
                        key: 'callback-number',
                        label: 'Callback Number',
                        value: answers.callbackNumber ? answers.callbackNumber : 'Default to Caller ID'
                    },
                    {
                        key: 'leave-voicemail',
                        label: 'Leave Voicemail',
                        value: answers.leaveVoicemail !== undefined ? (answers.leaveVoicemail ? 'Yes' : 'No') : 'Yes'
                    }
                ];

                const activeDays = (answers.schedulePolicy?.dayOfWeekOverrides || [])
                    .filter(d => (d.timeWindows && d.timeWindows.length > 0) || (d.retryUnconfirmedTimeWindows && d.retryUnconfirmedTimeWindows.length > 0));

                if (activeDays.length > 0) {
                    const scheduleSummary = activeDays.map(d => {
                        const windows = d.timeWindows?.join(', ') || 'None';
                        const retry = d.retryUnconfirmedTimeWindows?.length ? ` (Retry: ${d.retryUnconfirmedTimeWindows.join(', ')})` : '';
                        return `${DAY_NAMES[d.dayOfWeek]}: ${windows}${retry}`;
                    }).join('\n');

                    generalItems.push({
                        key: 'calling-schedule',
                        label: 'Calling Schedule Policy',
                        value: scheduleSummary,
                        isMultiline: true
                    });
                }

                const confirmationsItems: Array<{ key: string; label: string; value: string; isMultiline?: boolean }> = [
                    {
                        key: 'confirmations-status',
                        label: 'Confirmations Campaign',
                        value: answers.confirmAppointmentsEnabled ? 'Enabled' : 'Disabled'
                    }
                ];
                if (answers.confirmAppointmentsEnabled) {
                    confirmationsItems.push(
                        {
                            key: 'confirmations-days-before',
                            label: 'Days Before Appointment',
                            value: answers.daysBeforeAppointment !== undefined ? `${answers.daysBeforeAppointment} day${answers.daysBeforeAppointment === 1 ? '' : 's'}` : '2 days'
                        },
                        {
                            key: 'confirmations-max-attempts',
                            label: 'Max Call Attempts',
                            value: answers.callMaxAttempts !== undefined ? `${answers.callMaxAttempts}` : '2'
                        },
                        {
                            key: 'confirmations-same-day',
                            label: 'Confirm Same-Day Appointments',
                            value: answers.confirmSameDayAppointments ? 'Yes' : 'No'
                        },
                        {
                            key: 'confirmations-procedure-types',
                            label: 'Procedure Types',
                            value: answers.procedureTypeIds && answers.procedureTypeIds.length > 0 ? answers.procedureTypeIds.join(', ') : 'All'
                        },
                        {
                            key: 'confirmations-providers',
                            label: 'Providers',
                            value: answers.providerIds && answers.providerIds.length > 0 ? answers.providerIds.join(', ') : 'All'
                        },
                        {
                            key: 'confirmations-operatories',
                            label: 'Operatories',
                            value: answers.operatoryIds && answers.operatoryIds.length > 0 ? answers.operatoryIds.join(', ') : 'All'
                        }
                    );
                    if (answers.confirmationVoicemail) {
                        confirmationsItems.push({
                            key: 'confirmations-voicemail',
                            label: 'Voicemail Script',
                            value: answers.confirmationVoicemail,
                            isMultiline: true
                        });
                    }
                }

                const recallItems: Array<{ key: string; label: string; value: string; isMultiline?: boolean }> = [
                    {
                        key: 'recall-status',
                        label: 'Recall Campaign',
                        value: answers.recallCampaignEnabled ? 'Enabled' : 'Disabled'
                    }
                ];
                if (answers.recallCampaignEnabled) {
                    recallItems.push(
                        {
                            key: 'recall-start-days',
                            label: 'Start Calling Before Due',
                            value: answers.recallStartDaysBefore !== undefined ? `${answers.recallStartDaysBefore} days` : '30 days'
                        },
                        {
                            key: 'recall-max-attempts',
                            label: 'Max Recall Attempts',
                            value: answers.recallMaxAttempts !== undefined ? `${answers.recallMaxAttempts}` : '3'
                        }
                    );
                    if (answers.recallVoicemail) {
                        recallItems.push({
                            key: 'recall-voicemail',
                            label: 'Voicemail Script',
                            value: answers.recallVoicemail,
                            isMultiline: true
                        });
                    }
                }

                const reactivationItems: Array<{ key: string; label: string; value: string; isMultiline?: boolean }> = [
                    {
                        key: 'reactivation-status',
                        label: 'Reactivation Campaign',
                        value: answers.reactivationCampaignEnabled ? 'Enabled' : 'Disabled'
                    }
                ];
                if (answers.reactivationCampaignEnabled) {
                    reactivationItems.push({
                        key: 'reactivation-lookback',
                        label: 'Days Overdue Lookback Window',
                        value: answers.reactivationDaysOverdue !== undefined ? `${answers.reactivationDaysOverdue} days` : '180 days'
                    });
                    if (answers.reactivationVoicemail) {
                        reactivationItems.push({
                            key: 'reactivation-voicemail',
                            label: 'Voicemail Script',
                            value: answers.reactivationVoicemail,
                            isMultiline: true
                        });
                    }
                }

                const missedItems: Array<{ key: string; label: string; value: string; isMultiline?: boolean }> = [
                    {
                        key: 'missed-status',
                        label: 'Missed Appointment Campaign',
                        value: answers.missedAppointmentEnabled ? 'Enabled' : 'Disabled'
                    }
                ];
                if (answers.missedAppointmentEnabled) {
                    missedItems.push({
                        key: 'missed-lookback',
                        label: 'Missed Appointment Lookback',
                        value: answers.missedAppointmentLookbackDays !== undefined ? `${answers.missedAppointmentLookbackDays} days` : '5 days'
                    });
                    if (answers.missedAppointmentVoicemail) {
                        missedItems.push({
                            key: 'missed-voicemail',
                            label: 'Voicemail Script',
                            value: answers.missedAppointmentVoicemail,
                            isMultiline: true
                        });
                    }
                }

                const sections = [
                    { key: 'general', title: 'General Outbound Settings', items: generalItems },
                    { key: 'confirmations', title: 'Appointment Confirmations Campaign', items: confirmationsItems },
                    { key: 'recall', title: 'Recall Campaign', items: recallItems },
                    { key: 'reactivation', title: 'Reactivation Campaign', items: reactivationItems },
                    { key: 'missed', title: 'Missed Appointment Rescheduling', items: missedItems }
                ];

                return (
                    <ConfirmationDialog
                        title={`${dialogApp.name} Outbound Form Responses`}
                        deleteText="Close"
                        warning={true}
                        onClose={closeOutboundResponses}
                        onConfirm={closeOutboundResponses}
                        size="large"
                        contentScrollable={true}
                        contentClassName={progressDetailsContentClass}
                    >
                        <DetailsDialogContent>
                            <DetailsDialogHeader>
                                <DetailsDialogTitle>Outbound Campaign Setup Responses</DetailsDialogTitle>
                                <DetailsDialogMeta>
                                    <ProgressStatusBadge isComplete={isFormComplete}>
                                        {statusStr}
                                    </ProgressStatusBadge>
                                    {outboundResponsesDialog.loadingDeployments ? (
                                        <ProgressStatusBadge isComplete={false}>
                                            Checking outbound status...
                                        </ProgressStatusBadge>
                                    ) : (
                                        <TooltipWrapper
                                            text={
                                                isOutboundLive
                                                    ? 'Outbound calling is live (enabled in telephony deployment settings).'
                                                    : 'Outbound calling is not live (disabled in telephony deployment settings).'
                                            }
                                            place="top"
                                        >
                                            <ProgressStatusBadge isComplete={isOutboundLive}>
                                                {isOutboundLive ? 'Outbound live' : 'Outbound not live'}
                                            </ProgressStatusBadge>
                                        </TooltipWrapper>
                                    )}
                                    <span>{percent}% complete</span>
                                    {submitter && (
                                        <span>Submitted by <strong>{submitter.firstName} {submitter.lastName}</strong> ({submitter.email})</span>
                                    )}
                                    {outbound.completedAt && (
                                        <span>Completed {moment(outbound.completedAt).fromNow()}</span>
                                    )}
                                    {!outbound.completedAt && outbound.lastSavedAt && (
                                        <span>Saved {moment(outbound.lastSavedAt).fromNow()}</span>
                                    )}
                                </DetailsDialogMeta>
                            </DetailsDialogHeader>

                            <DetailsSections>
                                {sections.map(section => (
                                    <DetailsSection key={section.key}>
                                        <DetailsSectionTitle>{section.title}</DetailsSectionTitle>
                                        <DetailsAnswerList>
                                            {section.items.map(item => (
                                                <DetailsAnswerItem key={item.key}>
                                                    <DetailsAnswerLabel>{item.label}</DetailsAnswerLabel>
                                                    <DetailsAnswerValueRow>
                                                        <DetailsAnswerValue isMultiline={!!item.isMultiline}>{item.value}</DetailsAnswerValue>
                                                        <CopyValueButton
                                                            type="button"
                                                            onClick={async () => {
                                                                if (!navigator?.clipboard?.writeText) {
                                                                    return;
                                                                }
                                                                try {
                                                                    await navigator.clipboard.writeText(item.value ?? '');
                                                                    setCopiedKey(item.key);
                                                                    setTimeout(() => setCopiedKey(null), 1500);
                                                                } catch (error) {
                                                                    return;
                                                                }
                                                            }}
                                                            title={copiedKey === item.key ? 'Copied!' : 'Copy to clipboard'}
                                                            aria-label={copiedKey === item.key ? `Copied ${item.label}` : `Copy ${item.label} to clipboard`}
                                                        >
                                                            <img src={copyIcon} alt="" />
                                                        </CopyValueButton>
                                                    </DetailsAnswerValueRow>
                                                </DetailsAnswerItem>
                                            ))}
                                        </DetailsAnswerList>
                                    </DetailsSection>
                                ))}
                            </DetailsSections>
                        </DetailsDialogContent>
                    </ConfirmationDialog>
                );
            })()}

            {testingFeedbackDialog && (() => {
                const existingDetails = parseOnboardingProgressDetails(testingFeedbackDialog.progress?.detailsJson);
                const existingRounds = (existingDetails.testing as any)?.rounds || [];
                const targetRounds = parseInt((existingDetails.testing as any)?.targetRounds || '2', 10);
                const willOpenNextRound = (existingRounds.length + 1) < targetRounds;
                const hasFeedbackSections = testingFeedbackDialog.summary.testingFeedbackSections.length > 0;
                const allFeedbackCleared = hasFeedbackSections && testingFeedbackDialog.summary.feedbackCount === 0;
                const testingStatus = getClientTestingStatus(testingFeedbackDialog.progress);
                const testingPercentComplete = getDisplayedTestingPercentComplete(testingFeedbackDialog.application, testingFeedbackDialog.progress);

                return (
                    <ConfirmationDialog
                        title={`${testingFeedbackDialog.application.name} Testing Feedback`}
                        warning={true}
                        onClose={closeTestingFeedback}
                        size="large"
                        contentScrollable={true}
                        contentClassName={progressDetailsContentClass}
                        customFooter={(
                            <div style={{ display: 'flex', width: '100%', justifyContent: 'flex-end', gap: '12px', alignItems: 'center' }}>
                                <Button
                                    themes={['white', 'secondary']}
                                    text="Close"
                                    type="button"
                                    onClick={closeTestingFeedback}
                                />
                                <Button
                                    themes={['white', 'secondary']}
                                    text={isSavingFeedback ? "Saving..." : "Save Progress"}
                                    type="button"
                                    disabled={isSavingFeedback || isResolvingRound}
                                    onClick={handleSaveFeedbackProgress}
                                />
                                <Button
                                    themes={['primary']}
                                    text={isResolvingRound ? "Resolving..." : (willOpenNextRound ? "Resolve Round & Open Next Round" : "Resolve Round")}
                                    type="button"
                                    disabled={isSavingFeedback || isResolvingRound}
                                    onClick={handleResolveRound}
                                />
                            </div>
                        )}
                    >
                        <DetailsDialogContent isCompact={isTestingProgressComplete(testingFeedbackDialog.progress)}>
                        <DetailsDialogHeader>
                            <DetailsDialogMeta>
                                <ProgressStatusBadge isComplete={testingStatus === 'Complete'}>
                                    {testingStatus}
                                </ProgressStatusBadge>
                                {allFeedbackCleared && (
                                    <FeedbackClearedStatusBadge>
                                        Feedback cleared
                                    </FeedbackClearedStatusBadge>
                                )}
                                <span>{testingPercentComplete}% complete</span>
                                {!!testingFeedbackDialog.summary.feedbackCount && <span>{testingFeedbackDialog.summary.feedbackCount} feedback note{testingFeedbackDialog.summary.feedbackCount === 1 ? '' : 's'}</span>}
                                {testingFeedbackDialog.progress?.lastUpdated && (
                                    <span>Updated {moment(testingFeedbackDialog.progress.lastUpdated).fromNow()}</span>
                                )}
                            </DetailsDialogMeta>
                        </DetailsDialogHeader>

                        {dialogSuccess && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '12px 16px',
                                margin: '16px 0',
                                backgroundColor: '#ecfdf5',
                                borderLeft: '4px solid #10b981',
                                borderRadius: '4px',
                                color: '#065f46',
                                fontSize: '14px',
                                fontWeight: 500,
                                fontFamily: 'Muli, sans-serif',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                            }}>
                                <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" style={{ flexShrink: 0, color: '#10b981' }}>
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l5-5z" clipRule="evenodd" />
                                </svg>
                                <span>{dialogSuccess}</span>
                            </div>
                        )}

                        {dialogError && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '12px 16px',
                                margin: '16px 0',
                                backgroundColor: '#fef2f2',
                                borderLeft: '4px solid #ef4444',
                                borderRadius: '4px',
                                color: '#991b1b',
                                fontSize: '14px',
                                fontWeight: 500,
                                fontFamily: 'Muli, sans-serif',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                            }}>
                                <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" style={{ flexShrink: 0, color: '#ef4444' }}>
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <span>{dialogError}</span>
                            </div>
                        )}
 
                        {testingFeedbackDialog.summary.sectionTitle && !isTestingProgressComplete(testingFeedbackDialog.progress) && (
                            <DetailsCurrentSection>Current testing step: {testingFeedbackDialog.summary.sectionTitle}</DetailsCurrentSection>
                        )}
 
                        {testingFeedbackDialog.summary.testingFeedbackSections.length === 0 ? (
                            <ProgressEmptyState>No testing feedback has been saved yet.</ProgressEmptyState>
                        ) : (
                            <DetailsSections>
                                {testingFeedbackDialog.summary.testingFeedbackSections.map(section => (
                                    <DetailsSection key={section.key}>
                                        <DetailsSectionTitle>{section.title}</DetailsSectionTitle>
                                        <DetailsAnswerList>
                                            {section.items.map(item => (
                                                <DetailsAnswerItem key={item.key}>
                                                    <DetailsAnswerLabel>{item.label}</DetailsAnswerLabel>
                                                    <DetailsAnswerValue isMultiline={!!item.isMultiline}>{item.value}</DetailsAnswerValue>
                                                    
                                                    {!!item.stepId && !!item.value.trim() && (
                                                        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px', background: '#f1f5f9', borderRadius: '6px' }}>
                                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 'bold', color: '#334155', cursor: 'pointer' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={!!feedbackFixed[item.stepId]}
                                                                    onChange={(e) => {
                                                                        const checked = e.target.checked;
                                                                        setFeedbackFixed(prev => ({
                                                                            ...prev,
                                                                            [item.stepId!]: checked
                                                                        }));
                                                                    }}
                                                                />
                                                                Mark as Fixed / Resolved
                                                            </label>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#475569' }}>Internal Fix Notes / Reply:</span>
                                                                <ModalInput
                                                                    type="text"
                                                                    placeholder="Type reply or resolution notes here..."
                                                                    value={feedbackNotes[item.stepId] || ''}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;
                                                                        setFeedbackNotes(prev => ({
                                                                            ...prev,
                                                                            [item.stepId!]: val
                                                                        }));
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}

                                                    {!!item.stepId && !item.value.trim() && (
                                                        <ClearedFeedbackNotice>
                                                            Feedback cleared — no action required.
                                                        </ClearedFeedbackNotice>
                                                    )}

                                                    {!!item.history?.length && (
                                                        <DetailsHistoryList>
                                                            <DetailsHistoryHeading>Previous feedback</DetailsHistoryHeading>
                                                            {item.history.map((entry, index) => (
                                                                <DetailsHistoryItem key={`${item.key}-history-${index}`}>
                                                                    <DetailsHistoryTimestamp>{moment(entry.savedAt).format('MM/DD/YYYY h:mm A')}</DetailsHistoryTimestamp>
                                                                    <DetailsHistoryValue isMultiline={entry.value.includes('\n')}>{entry.value}</DetailsHistoryValue>
                                                                </DetailsHistoryItem>
                                                            ))}
                                                        </DetailsHistoryList>
                                                    )}
                                                </DetailsAnswerItem>
                                            ))}
                                        </DetailsAnswerList>
                                    </DetailsSection>
                                ))}
                            </DetailsSections>
                        )}
                    </DetailsDialogContent>
                </ConfirmationDialog>
                );
            })()}

            {previewEmailModal && (
                <ConfirmationDialog
                    title="Preview & Edit Outgoing Email"
                    onClose={() => setPreviewEmailModal(null)}
                    size="large"
                    contentScrollable={true}
                    customFooter={(() => {
                        if (previewEmailModal.isLoadingPreview) {
                            return (
                                <PreviewEmailFooterContainer>
                                    <PreviewEmailFooterLeft>
                                        <Button
                                            themes={["white", "secondary"]}
                                            text="Cancel"
                                            type="button"
                                            onClick={() => setPreviewEmailModal(null)}
                                        />
                                    </PreviewEmailFooterLeft>
                                </PreviewEmailFooterContainer>
                            );
                        }

                        const isCommon = previewEmailModal.app.id === props.commonApp?.id;
                        const email = isCommon ? orgContactEmail : (locContactEmail[previewEmailModal.app.id] || '');
                        const isEmailMissing = !email || !email.trim();
                        const progressState = isCommon ? commonAppProgressState : (progressStateByAppId[previewEmailModal.app.id] || {});
                        const isSending = !!progressState.loading;

                        const details = parseOnboardingProgressDetails(previewEmailModal.progress?.detailsJson);
                        const triggerState = details.emailTriggers?.[previewEmailModal.triggerKey];
                        const isAlreadySent = triggerState?.status === 'Sent';

                        const actionButtonText = isManualEmail 
                            ? (isAlreadySent ? "Copy Link & Mark Resent" : "Copy Link & Mark Sent")
                            : (isAlreadySent ? "Resend Email" : "Send Email");

                        return (
                            <PreviewEmailFooterContainer>
                                <PreviewEmailFooterLeft>
                                    <Button
                                        themes={["white", "secondary"]}
                                        text="Cancel"
                                        type="button"
                                        onClick={() => setPreviewEmailModal(null)}
                                    />
                                    {!(triggerState?.status === 'Sent' || triggerState?.status === 'Skipped') && (
                                        <Button
                                            themes={["white", "secondary"]}
                                            text="Skip Step"
                                            type="button"
                                            disabled={isSending}
                                            onClick={async () => {
                                                const app = previewEmailModal.app;
                                                const progress = previewEmailModal.progress;
                                                const triggerId = previewEmailModal.triggerKey;
                                                await handleUpdateEmailTrigger(app, progress, triggerId, 'skip');
                                                setPreviewEmailModal(null);
                                            }}
                                        />
                                    )}
                                </PreviewEmailFooterLeft>
                                <PreviewEmailFooterRight>
                                    <PreviewEmailLabel>
                                        <input
                                            type="checkbox"
                                            checked={isManualEmail}
                                            onChange={(e) => setIsManualEmail(e.target.checked)}
                                            style={{
                                                width: '16px',
                                                height: '16px',
                                                cursor: 'pointer',
                                                accentColor: '#0284c7'
                                            }}
                                        />
                                        I'm sending the email manually
                                    </PreviewEmailLabel>
                                     <Button
                                         themes={["primary"]}
                                         text={actionButtonText}
                                         type="button"
                                         loading={isSending}
                                         onClick={handleSendEmailFromModal}
                                         disabled={(!isManualEmail && isEmailMissing) || isSending}
                                     />
                                </PreviewEmailFooterRight>
                            </PreviewEmailFooterContainer>
                        );
                    })()}
                >
                    <div style={{ padding: '24px', fontFamily: 'Muli, sans-serif', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {previewEmailModal.isLoadingPreview ?
                            <div className={modalPreviewLoaderStyle}>
                                <Loader />
                            </div>
                        :
                            <>
                                {(() => {
                                    const details = parseOnboardingProgressDetails(previewEmailModal.progress?.detailsJson);
                                    const triggerState = details.emailTriggers?.[previewEmailModal.triggerKey];
                                    const isConsolidatedOnboarding = previewEmailModal.title === 'Consolidated Onboarding Links';
                                    const isConsolidatedTesting = previewEmailModal.title === 'Consolidated Testing Links';

                                    let warningMessage = '';

                                    if (isConsolidatedOnboarding) {
                                        const orgSent = triggerState?.status === 'Sent';
                                        const sentOnboardingLocations = locationApps.filter(app => {
                                            const progressState = progressStateByAppId[app.id] || {};
                                            const progress = progressState.data;
                                            const details = parseOnboardingProgressDetails(progress?.detailsJson);
                                            return details.emailTriggers?.['loc-launched']?.status === 'Sent';
                                        });
                                        const locsSentCount = sentOnboardingLocations.length;

                                        if (orgSent && locsSentCount > 0) {
                                            warningMessage = `The organization onboarding email and ${locsSentCount} location onboarding email(s) have already been sent. You can make edits below and click Resend.`;
                                        } else if (orgSent) {
                                            warningMessage = `The organization onboarding email has already been sent. You can make edits below and click Resend.`;
                                        } else if (locsSentCount > 0) {
                                            warningMessage = `${locsSentCount} location onboarding email(s) have already been sent. You can make edits below and click Resend.`;
                                        }
                                    } else if (isConsolidatedTesting) {
                                        const orgSent = triggerState?.status === 'Sent';
                                        const sentTestingLocations = locationApps.filter(app => {
                                            const progressState = progressStateByAppId[app.id] || {};
                                            const progress = progressState.data;
                                            const details = parseOnboardingProgressDetails(progress?.detailsJson);
                                            return details.emailTriggers?.['testing-ready']?.status === 'Sent';
                                        });
                                        const locsSentCount = sentTestingLocations.length;

                                        if (orgSent && locsSentCount > 0) {
                                            warningMessage = `The organization testing email and ${locsSentCount} location testing email(s) have already been sent. You can make edits below and click Resend.`;
                                        } else if (orgSent) {
                                            warningMessage = `The organization testing email has already been sent. You can make edits below and click Resend.`;
                                        } else if (locsSentCount > 0) {
                                            warningMessage = `${locsSentCount} location testing email(s) have already been sent. You can make edits below and click Resend.`;
                                        }
                                    } else if (triggerState?.status === 'Sent') {
                                        warningMessage = `This email was already sent on ${moment(triggerState.firstSentAt || triggerState.sentAt).format('MM/DD/YYYY [at] h:mm A')} (${(triggerState.sentManually || !isAutomatedTrigger(previewEmailModal.triggerKey)) ? 'Manually' : 'Automated'}). You can make edits below and click Resend.`;
                                    }

                                    if (warningMessage) {
                                        return (
                                            <div style={{
                                                background: '#f0fdf4',
                                                border: '1px solid #bbf7d0',
                                                borderRadius: '6px',
                                                padding: '12px 16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                color: '#166534',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                lineHeight: '1.4'
                                            }}>
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#166534', flexShrink: 0 }}>
                                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                                    <polyline points="22 4 12 14.01 9 11.01" />
                                                </svg>
                                                <span>
                                                     {warningMessage}
                                                 </span>
                                            </div>
                                        );
                                    }

                                    if (triggerState?.status === 'Skipped') {
                                        return (
                                            <div style={{
                                                background: '#fef3c7',
                                                border: '1px solid #fde68a',
                                                borderRadius: '6px',
                                                padding: '12px 16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                color: '#78350f',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                lineHeight: '1.4'
                                            }}>
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#d97706', flexShrink: 0 }}>
                                                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                                    <line x1="12" y1="9" x2="12" y2="13" />
                                                    <line x1="12" y1="17" x2="12.01" y2="17" />
                                                </svg>
                                                <span>
                                                    This step was marked as Skipped. You can still edit the fields below and send the email.
                                                </span>
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}
                        {(() => {
                            const isCommon = previewEmailModal.app.id === props.commonApp?.id;
                            const firstName = isCommon ? orgContactFirstName : (locContactFirstName[previewEmailModal.app.id] || '');
                            const email = isCommon ? orgContactEmail : (locContactEmail[previewEmailModal.app.id] || '');

                            const setFirstName = (val: string) => {
                                if (isCommon) {
                                    setOrgContactFirstName(val);
                                } else {
                                    setLocContactFirstName({
                                        ...locContactFirstName,
                                        [previewEmailModal.app.id]: val
                                    });
                                }
                            };

                            const setEmail = (val: string) => {
                                if (isCommon) {
                                    setOrgContactEmail(val);
                                } else {
                                    setLocContactEmail({
                                        ...locContactEmail,
                                        [previewEmailModal.app.id]: val
                                    });
                                }
                            };

                            return (
                                <>
                                    <ModalField>
                                        <ModalLabel>Contact First Name:</ModalLabel>
                                        <ModalInput
                                            type="text"
                                            placeholder="e.g. Jane"
                                            value={firstName}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setFirstName(val);
                                                const oldFirstName = firstName.trim() || 'there';
                                                const newFirstName = val.trim() || 'there';
                                                if (editBody.includes(`Hi ${oldFirstName},`)) {
                                                    setEditBody(editBody.replace(`Hi ${oldFirstName},`, `Hi ${newFirstName},`));
                                                } else if (editBody.includes(`Hi ${oldFirstName}\n`)) {
                                                    setEditBody(editBody.replace(`Hi ${oldFirstName}\n`, `Hi ${newFirstName}\n`));
                                                }
                                            }}
                                        />
                                    </ModalField>
                                    <ModalField>
                                        <UrlLabelRow>
                                            <ModalLabel>Contact Email:</ModalLabel>
                                            <TooltipWrapper
                                                text="Enter a single email address, or a list of multiple email addresses separated by semicolons."
                                                place="top"
                                                maxWidth={280}
                                            >
                                                <UrlInfoIcon src={infoIcon} alt="info" />
                                            </TooltipWrapper>
                                        </UrlLabelRow>
                                        <ModalInput
                                            type="text"
                                            placeholder="e.g. jane@company.com; john@company.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                        />
                                    </ModalField>
                                </>
                            );
                        })()}
                        <ModalField style={{ gap: '4px' }}>
                            <ModalLabel>From:</ModalLabel>
                            <span style={{ fontSize: '14px', color: `${color_shades_darkest}`, fontWeight: 500, paddingLeft: '2px' }}>support@voicify.com</span>
                        </ModalField>
                        <ModalField>
                            <ModalLabel>Subject:</ModalLabel>
                            <ModalInput
                                type="text"
                                value={editSubject}
                                onChange={(e) => setEditSubject(e.target.value)}
                            />
                        </ModalField>
                        <ModalField>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <ModalLabel>Email Body:</ModalLabel>
                                {previewEmailModal.triggerKey === 'telephony-reminder' && (
                                    <Button
                                        themes={["secondary-small"]}
                                        text={copiedTemplate ? "Copied!" : "Copy Provider Template"}
                                        type="button"
                                        onClick={async () => {
                                            const parts = editBody.split(/---+/);
                                            const templateText = parts.length >= 3 ? parts[1].trim() : editBody;
                                            await navigator.clipboard.writeText(templateText);
                                            setCopiedTemplate(true);
                                            setTimeout(() => setCopiedTemplate(false), 2000);
                                        }}
                                    />
                                )}
                            </div>
                            <ModalTextarea
                                value={editBody}
                                onChange={(e) => setEditBody(e.target.value)}
                            />
                        </ModalField>
                        <ModalField style={{ marginTop: '16px' }}>
                            <ModalLabel>Live Preview (Simulated Mobile & Desktop):</ModalLabel>
                            <div style={{
                                width: '100%',
                                border: '1px solid #cbd5e1',
                                borderRadius: '6px',
                                overflow: 'hidden',
                                background: '#f4f6f8',
                                padding: '10px',
                                boxSizing: 'border-box'
                            }}>
                                <iframe
                                    title="Branded Email Preview"
                                    srcDoc={buildBrandedEmailHtml(editBody, previewEmailModal.link, previewEmailModal.triggerKey)}
                                    style={{
                                        width: '100%',
                                        height: '350px',
                                        border: 'none',
                                        background: '#f4f6f8'
                                    }}
                                />
                             </div>
                        </ModalField>
                            </>
                        }
                    </div>
                </ConfirmationDialog>
            )}

            {launchDateModal && (
                <ConfirmationDialog
                    title={launchDateModal.isLive ? "Update Go Live Date" : "Update Target Launch Date"}
                    onClose={() => setLaunchDateModal(null)}
                    onConfirm={() => handleSaveLaunchDate(tempLaunchDateVal)}
                    deleteText={isSavingLaunchDate ? "Saving..." : "Save Date"}
                    warning={true}
                    disabled={isSavingLaunchDate}
                >
                    <div style={{ padding: '24px', fontFamily: 'Muli, sans-serif', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <p style={{ fontSize: '14px', color: '#475569', margin: 0 }}>
                            {launchDateModal.isLive ? (
                                <>
                                    Update <strong>{launchDateModal.app.name}</strong>'s go-live date.
                                </>
                            ) : (
                                <>
                                    Update <strong>{launchDateModal.app.name}</strong>'s estimated target launch date. You can update this at any time. It's used for forecasting purposes only.
                                </>
                            )}
                        </p>
                        <ModalField>
                            <ModalLabel>Select Date:</ModalLabel>
                            <ModalInput
                                type="date"
                                value={tempLaunchDateVal}
                                onChange={(e) => setTempLaunchDateVal(e.target.value)}
                                disabled={isSavingLaunchDate}
                            />
                        </ModalField>
                        {launchDateError && (
                            <p style={{ color: '#dc2626', fontSize: '13px', margin: '4px 0 0 0', fontWeight: '600' }}>
                                {launchDateError}
                            </p>
                        )}
                    </div>
                </ConfirmationDialog>
            )}

            {sparkInviteApplication && (
                <InviteSparkFormModal
                    applicationId={sparkInviteApplication.id}
                    appName={sparkInviteApplication.name}
                    isShareFormOpen={true}
                    setIsShareFormOpen={(isOpen) => {
                        if (!isOpen) {
                            setSparkInviteApplication(null);
                        }
                    }}
                    onInviteSent={handleSparkInviteSent}
                    memberContainer={props.memberContainer}
                />
            )}

            {statusModal && (
                <ConfirmationDialog
                    title={statusModal.title}
                    onClose={() => setStatusModal(null)}
                    onConfirm={() => setStatusModal(null)}
                    deleteText="Close"
                    customFooter={statusModal.customFooter}
                >
                    <div style={{ padding: '24px', fontFamily: 'Muli, sans-serif' }}>
                        {statusModal.details}
                    </div>
                </ConfirmationDialog>
            )}
        </SettingsCard>
    );
};

const ModalField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    font-family: ${fontFamilyStack};
`;

const ModalLabel = styled.label`
    font-family: ${fontFamilyStack};
    font-size: 13px;
    font-weight: 700;
    color: #1e293b;
`;

const UrlLabelRow = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
`;

const UrlInfoIcon = styled.img`
    width: 16px;
    height: 16px;
    opacity: 0.55;
    cursor: pointer;
    transition: opacity 0.15s;

    &:hover {
        opacity: 1;
    }
`;

const OrgLockIconContainer = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #f1f5f9;
    border: 1px solid #cbd5e1;
    color: #64748b;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    margin-left: 2px;

    &:hover {
        background: #e2e8f0;
        border-color: #94a3b8;
        color: #334155;
        transform: scale(1.08);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
    }
`;

const ModalInput = styled.input`
    height: 38px;
    border-radius: 6px;
    border: 1px solid #cbd5e1;
    background: #ffffff;
    color: #1e293b;
    padding: 0 12px;
    font-size: 13px;
    font-family: ${fontFamilyStack};
    width: 100%;
    box-sizing: border-box;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
        outline: none;
        border-color: #0284c7;
        box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.15);
    }
`;

const ModalTextarea = styled.textarea`
    height: 300px;
    border-radius: 6px;
    border: 1px solid #cbd5e1;
    background: #ffffff;
    color: #1e293b;
    padding: 12px;
    font-size: 13px;
    font-family: ${fontFamilyStack};
    width: 100%;
    box-sizing: border-box;
    resize: vertical;
    overflow-y: auto;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
        outline: none;
        border-color: #0284c7;
        box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.15);
    }
`;

const InlineControlGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: flex-end;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 16px;
    margin-top: 16px;
    margin-bottom: 16px;
    width: 100%;
    font-family: ${fontFamilyStack};

    @media (max-width: 640px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

const InlineField = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 200px;
    font-family: ${fontFamilyStack};
`;

const InlineLabel = styled.label`
    font-family: ${fontFamilyStack};
    font-size: 13px;
    font-weight: 700;
    color: #1e293b;
`;

const InlineInput = styled.input`
    height: 38px;
    border-radius: 6px;
    border: 1px solid #cbd5e1;
    background: #ffffff;
    color: #1e293b;
    padding: 0 12px;
    font-size: 13px;
    font-family: ${fontFamilyStack};
    width: 100%;
    box-sizing: border-box;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
        outline: none;
        border-color: #0284c7;
        box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.15);
    }
`;

const InlineButtonGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    height: 38px;
    font-family: ${fontFamilyStack};

    @media (max-width: 640px) {
        margin-top: 12px;
        justify-content: space-between;
    }
`;

const CopyLinkButton = styled.button`
    background: none;
    border: none;
    color: #0284c7;
    font-family: ${fontFamilyStack};
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    text-decoration: underline;
    padding: 0;
    transition: color 0.15s ease;

    &:hover {
        color: #0369a1;
    }

    &:disabled {
        color: #94a3b8;
        cursor: not-allowed;
    }
`;

// Access Denied Styled Components
const AccessDeniedContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px 32px;
    text-align: center;
    background: #ffffff;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    font-family: ${fontFamilyStack};

    img {
        width: 80px;
        height: 80px;
        margin-bottom: 20px;
    }

    h3 {
        margin: 0 0 12px 0;
        font-size: 20px;
        font-weight: 700;
        color: #1e293b;
        font-family: ${fontFamilyStack};
    }

    p {
        margin: 0;
        color: #64748b;
        font-size: 14px;
        max-width: 440px;
        line-height: 1.5;
        font-family: ${fontFamilyStack};
    }
`;

const CardContent = styled.div`
    position: relative;
    font-family: ${fontFamilyStack};
`;

const SectionBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 24px;
    font-family: ${fontFamilyStack};
`;

const SectionTitleRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #e2e8f0;
    padding-bottom: 12px;
    margin-bottom: 16px;
    font-family: ${fontFamilyStack};
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
    font-family: ${fontFamilyStack};
`;

const OrgCardBody = styled.div`
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #ffffff;
    padding: 24px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 1px 2px 0 rgba(0, 0, 0, 0.02);
    font-family: ${fontFamilyStack};
`;

const GridContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 340px;
    gap: 32px;
    font-family: ${fontFamilyStack};

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
    }
`;

const GridMain = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    font-family: ${fontFamilyStack};
`;

const GridSidebar = styled.div`
    background: #ffffff;
    border-left: 1px solid #e2e8f0;
    padding-left: 32px;
    font-family: ${fontFamilyStack};

    @media (max-width: 1024px) {
        border-left: none;
        padding-left: 0;
        border-top: 1px solid #e2e8f0;
        padding-top: 24px;
    }
`;

const LinkGenerationBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    font-family: ${fontFamilyStack};
`;

const LinkActionButtons = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
    font-family: ${fontFamilyStack};

    button {
        margin: 0;
    }
`;

const LockedOverlay = styled.div`
    background: #f0f9ff;
    border: 1px solid #bae6fd;
    border-radius: 8px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    font-family: ${fontFamilyStack};
`;

const LockHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    font-family: ${fontFamilyStack};
`;

const LockIcon = styled.span`
    font-size: 32px;
    font-family: ${fontFamilyStack};
`;

const LockTitle = styled.h4`
    margin: 0 0 4px 0;
    font-size: 16px;
    font-weight: 700;
    color: #1e293b;
    font-family: ${fontFamilyStack};
`;

const LockMeta = styled.p`
    margin: 0;
    font-size: 13px;
    color: #64748b;
    font-family: ${fontFamilyStack};
`;

const LockActions = styled.div`
    display: flex;
    gap: 12px;
    font-family: ${fontFamilyStack};
`;

const ProgressBarSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-family: ${fontFamilyStack};
`;

const ProgressBarHeader = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    font-family: ${fontFamilyStack};
`;

const ProgressBarLabel = styled.span`
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 11px;
    font-family: ${fontFamilyStack};
`;

const ProgressBarPercent = styled.span`
    font-size: 13px;
    font-weight: 700;
    color: #0284c7;
    font-family: ${fontFamilyStack};
`;

const ResponsesBox = styled.div`
    padding-top: 16px;
    border-top: 1px solid #e2e8f0;
    font-family: ${fontFamilyStack};
`;

const EmptySummaryText = styled.p`
    margin: 0;
    font-size: 13px;
    color: #64748b;
    font-style: italic;
    font-family: ${fontFamilyStack};
`;

const TimelineTitle = styled.h4`
    margin: 0 0 16px 0;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    color: #1e293b;
    letter-spacing: 0.05em;
    font-family: ${fontFamilyStack};
`;

const Timeline = styled.div`
    display: flex;
    flex-direction: column;
    font-family: ${fontFamilyStack};
`;

const TimelineItem = styled.div<{ isLast: boolean }>`
    display: flex;
    gap: 16px;
    position: relative;
    padding-bottom: ${props => props.isLast ? '0' : '24px'};
    font-family: ${fontFamilyStack};

    &::after {
        content: '';
        position: absolute;
        left: 6px;
        top: 16px;
        bottom: 0;
        width: 1px;
        border-left: 2px dashed #cbd5e1;
        display: ${props => props.isLast ? 'none' : 'block'};
    }
`;

const TimelineDot = styled.div<{ isSent: boolean; isSkipped?: boolean }>`
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background-color: ${props => props.isSkipped ? '#d97706' : props.isSent ? '#16a34a' : '#cbd5e1'};
    border: 3px solid #ffffff;
    box-shadow: 0 0 0 1px ${props => props.isSkipped ? '#d97706' : props.isSent ? '#16a34a' : '#cbd5e1'};
    z-index: 1;
    margin-top: 2px;
    flex-shrink: 0;
    font-family: ${fontFamilyStack};
`;

const TimelineContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    font-family: ${fontFamilyStack};
`;

const TimelineLabel = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    font-family: ${fontFamilyStack};
`;

const TimelineMeta = styled.span`
    font-size: 12px;
    color: #16a34a;
    line-height: 1.4;
    font-family: ${fontFamilyStack};
`;

const SkippedEmailBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 2px 7px;
    margin-top: 6px;
    border: 1px solid #f59e0b;
    border-radius: 4px;
    background-color: #fffbeb;
    color: #92400e;
    font-family: ${fontFamilyStack};
    font-size: 10px;
    font-weight: 800;
    line-height: 1.2;
    text-transform: uppercase;
    white-space: nowrap;
`;

const TimelineResetLink = styled.span`
    display: block;
    margin-top: 4px;
    font-size: 11px;
    font-weight: 600;
    color: #dc2626;
    cursor: pointer;
    text-decoration: underline;
    width: fit-content;
    font-family: ${fontFamilyStack};

    &:hover {
        opacity: 0.8;
    }
`;

const TimelineActions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-family: ${fontFamilyStack};
`;

const TimelineBypassRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: ${fontFamilyStack};

    input {
        cursor: pointer;
        accent-color: #0284c7;
    }

    label {
        font-size: 11px;
        color: #64748b;
        cursor: pointer;
        font-family: ${fontFamilyStack};
    }
`;

const LocationSectionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    font-family: ${fontFamilyStack};

    @media (max-width: 640px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
`;

const GatedOverlayBlock = styled.div`
    background: #ffffff;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    font-family: ${fontFamilyStack};
`;

const NoticeBanner = styled.div`
    background: #fffbeb;
    border: 1px solid #fde68a;
    color: #92400e;
    border-radius: 6px;
    padding: 14px 16px;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.5;
    font-family: ${fontFamilyStack};
`;

const GatedDisabledArea = styled.div`
    opacity: 0.5;
    pointer-events: none;
    font-family: ${fontFamilyStack};
`;

const LocationStagesContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    font-family: ${fontFamilyStack};
`;

const StageGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    font-family: ${fontFamilyStack};
`;

const StageHeader = styled.h4`
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: #1e293b;
    border-bottom: 1px solid #e2e8f0;
    padding-bottom: 8px;
    font-family: ${fontFamilyStack};
`;

const StageList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    font-family: ${fontFamilyStack};
`;

const HeaderBadgeContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: ${fontFamilyStack};
`;

const ProgressSyncIndicator = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    flex: 0 0 24px;
`;

const ProgressSyncSpinner = styled.span`
    width: 14px;
    height: 14px;
    border: 2px solid #bae6fd;
    border-top-color: #0284c7;
    border-radius: 50%;
    animation: onboarding-progress-spin 0.75s linear infinite;

    @keyframes onboarding-progress-spin {
        to {
            transform: rotate(360deg);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const LiveBadge = styled.span<{ isLive: boolean }>`
    display: inline-flex;
    align-items: center;
    padding: 3px 9px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    color: ${props => props.isLive ? '#15803d' : '#b45309'};
    background-color: ${props => props.isLive ? '#f0fdf4' : '#fffbeb'};
    border: 1px solid ${props => props.isLive ? '#bbf7d0' : '#fde68a'};
    font-family: ${fontFamilyStack};
`;

const LocationCardBody = styled.div`
    padding: 16px 20px 20px 20px;
    border-radius: 8px;
    font-family: ${fontFamilyStack};

    @media (max-width: 640px) {
        padding: 12px;
    }
`;

const ResponsesRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top: 1px solid #e2e8f0;
    padding-top: 16px;
    font-family: ${fontFamilyStack};
`;

const StatusBadge = styled.span<{ variant: string }>`
    display: inline-flex;
    align-items: center;
    padding: 3px 9px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    font-family: ${fontFamilyStack};
    color: ${props => {
        const v = props.variant;
        if (v === 'Testing Complete' || v === 'Completed' || v === 'Complete') return '#15803d';
        if (v === 'In Progress' || v === 'Round 1 Testing' || v === 'Round 2 Testing') return '#0284c7';
        return '#475569';
    }};
    background-color: ${props => {
        const v = props.variant;
        if (v === 'Testing Complete' || v === 'Completed' || v === 'Complete') return '#f0fdf4';
        if (v === 'In Progress' || v === 'Round 1 Testing' || v === 'Round 2 Testing') return '#f0f9ff';
        return '#f8fafc';
    }};
    border: 1px solid ${props => {
        const v = props.variant;
        if (v === 'Testing Complete' || v === 'Completed' || v === 'Complete') return '#bbf7d0';
        if (v === 'In Progress' || v === 'Round 1 Testing' || v === 'Round 2 Testing') return '#bae6fd';
        return '#cbd5e1';
    }};
`;

const SearchInput = styled.input`
    width: 100%;
    max-width: 320px;
    min-height: 38px;
    border-radius: 6px;
    border: 1px solid #cbd5e1;
    background: #ffffff;
    color: #1e293b;
    padding: 0 14px;
    font-size: 13px;
    line-height: 20px;
    font-family: ${fontFamilyStack};
    box-sizing: border-box;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
        outline: none;
        border-color: #0284c7;
        box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.15);
    }

    &::placeholder {
        color: #94a3b8;
    }
`;

const EmptyState = styled.div`
    border: 1px dashed #cbd5e1;
    background: #f8fafc;
    color: #64748b;
    border-radius: 8px;
    padding: 24px;
    font-family: ${fontFamilyStack};
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
`;

const SecondaryEmptyState = styled.div`
    border: 1px dashed #cbd5e1;
    background: #f8fafc;
    color: #64748b;
    border-radius: 8px;
    padding: 16px 24px;
    font-size: 13px;
    font-family: ${fontFamilyStack};
`;

const InlineError = styled.p`
    margin: -8px 0 0 0;
    color: #dc2626;
    font-size: 13px;
    font-family: ${fontFamilyStack};
`;

const ProgressStatusBadge = styled.span<{ isComplete: boolean }>`
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 999px;
    background: ${props => props.isComplete ? '#f0fdf4' : '#f0f9ff'};
    color: ${props => props.isComplete ? '#15803d' : '#0284c7'};
    border: 1px solid ${props => props.isComplete ? '#bbf7d0' : '#bae6fd'};
    font-size: 11px;
    font-weight: 700;
    font-family: ${fontFamilyStack};
`;

const FeedbackClearedStatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 999px;
    background: #f0fdf4;
    color: #15803d;
    border: 1px solid #bbf7d0;
    font-size: 11px;
    font-weight: 700;
    font-family: ${fontFamilyStack};
`;

const ProgressBarTrack = styled.div`
    margin-top: 16px;
    width: 100%;
    height: 8px;
    border-radius: 999px;
    background: #e2e8f0;
    overflow: hidden;
`;

const ProgressBarFill = styled.div<{ percent: number }>`
    width: ${props => `${props.percent}%`};
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, #0284c7 0%, #38bdf8 100%);
`;

const progressDetailsContentClass = css`
    display: flex;
    flex-direction: column;
    min-height: 0;
    box-sizing: border-box;
    padding-bottom: 0 !important;
`;

const DetailsDialogContent = styled.div<{ isCompact?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: ${props => props.isCompact ? '16px' : '20px'};
    padding: ${props => props.isCompact ? '12px 24px 32px' : '24px 24px 32px'};
    min-height: fit-content;
    font-family: ${fontFamilyStack};

    @media (max-width: 640px) {
        padding: ${props => props.isCompact ? '12px 20px 24px' : '20px 20px 24px'};
    }
`;

const DetailsDialogHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    font-family: ${fontFamilyStack};
`;

const DetailsDialogTitle = styled.h4`
    margin: 0;
    color: #1e293b;
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
    font-family: ${fontFamilyStack};
`;

const DetailsDialogMeta = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    color: #64748b;
    font-size: 12px;
    line-height: 18px;
    font-family: ${fontFamilyStack};
`;

const DetailsCurrentSection = styled.p`
    margin: 0;
    color: #1e293b;
    font-size: 13px;
    line-height: 20px;
    font-weight: 600;
    font-family: ${fontFamilyStack};
`;

const DetailsSections = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    font-family: ${fontFamilyStack};
`;

const DetailsSection = styled.div`
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #f8fafc;
    padding: 16px;
    font-family: ${fontFamilyStack};
`;

const DetailsSectionTitle = styled.h5`
    margin: 0 0 12px 0;
    color: #1e293b;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    font-family: ${fontFamilyStack};
`;

const DetailsAnswerList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    font-family: ${fontFamilyStack};
`;

const DetailsAnswerItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-family: ${fontFamilyStack};
`;

const DetailsAnswerLabel = styled.p`
    margin: 0;
    color: #334155;
    font-size: 13px;
    line-height: 18px;
    font-weight: 700;
    font-family: ${fontFamilyStack};
`;

const DetailsAnswerValue = styled.pre<{ isMultiline: boolean }>`
    margin: 0;
    color: #1e293b;
    font-size: 13px;
    line-height: 18px;
    font-family: ${fontFamilyStack};
    white-space: ${props => props.isMultiline ? 'pre-wrap' : 'normal'};
    word-break: break-word;
    flex: 1;
`;

const DetailsAnswerValueRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-family: ${fontFamilyStack};
`;

const CopyValueButton = styled.button`
    flex-shrink: 0;
    padding: 2px;
    background: transparent;
    border: none;
    cursor: pointer;
    opacity: 0.6;
    line-height: 0;
    font-family: ${fontFamilyStack};
    transition: opacity 0.15s ease;
    &:hover {
        opacity: 1;
    }
    img {
        width: 16px;
        height: 16px;
    }
`;

const ClearedFeedbackNotice = styled.div`
    margin-top: 8px;
    padding: 8px 10px;
    border-radius: 6px;
    background: #f1f5f9;
    color: #64748b;
    font-family: ${fontFamilyStack};
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
`;

const DetailsHistoryList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px dashed #cbd5e1;
    font-family: ${fontFamilyStack};
`;

const DetailsHistoryHeading = styled.div`
    color: #475569;
    font-size: 11px;
    line-height: 16px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-family: ${fontFamilyStack};
`;

const DetailsHistoryItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 12px;
    border-radius: 6px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    font-family: ${fontFamilyStack};
`;

const DetailsHistoryTimestamp = styled.div`
    color: #64748b;
    font-size: 11px;
    line-height: 16px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-family: ${fontFamilyStack};
`;

const DetailsHistoryValue = styled.pre<{ isMultiline: boolean }>`
    margin: 0;
    color: #1e293b;
    font-size: 12px;
    line-height: 17px;
    font-family: ${fontFamilyStack};
    white-space: ${props => props.isMultiline ? 'pre-wrap' : 'normal'};
    word-break: break-word;
`;

const ProgressEmptyState = styled.p`
    margin: 16px 0 0 0;
    color: #64748b;
    font-size: 13px;
    line-height: 18px;
    font-family: ${fontFamilyStack};
`;

const Notice = styled.div<{ type: 'error' | 'success' }>`
    border-radius: 8px;
    padding: 14px 16px;
    margin-bottom: 16px;
    font-size: 13px;
    font-weight: 600;
    color: ${props => props.type === 'success' ? '#166534' : '#991b1b'};
    background: ${props => props.type === 'success' ? '#f0fdf4' : '#fef2f2'};
    border: 1px solid ${props => props.type === 'success' ? '#bbf7d0' : '#fecaca'};
    font-family: ${fontFamilyStack};
`;

const fieldContainerStyle = css`
    background: ${color_shades_lightest};
    border-color: ${color_shades_dark};
`;

const DialogContent = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 32px;
    gap: 16px;
    font-family: ${fontFamilyStack};
`;

const DialogTitle = styled.p`
    margin: 0;
    color: #1e293b;
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
    font-family: ${fontFamilyStack};
`;

const DialogCopy = styled.p`
    margin: 0;
    color: #475569;
    font-size: 14px;
    line-height: 20px;
    font-family: ${fontFamilyStack};
`;

const DialogError = styled.p`
    margin: 0;
    color: #dc2626;
    font-size: 13px;
    line-height: 20px;
    font-family: ${fontFamilyStack};
`;

const PreviewEmailFooterContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0 24px;
    box-sizing: border-box;
    font-family: ${fontFamilyStack};

    @media (max-width: 640px) {
        flex-direction: column-reverse;
        gap: 12px;
        padding: 0 12px;
        align-items: stretch;
    }
`;

const PreviewEmailFooterLeft = styled.div`
    display: flex;
    gap: 12px;

    @media (max-width: 640px) {
        justify-content: space-between;
    }
`;

const PreviewEmailFooterRight = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;

    @media (max-width: 640px) {
        flex-direction: column-reverse;
        align-items: stretch;
        gap: 12px;
    }
`;

const PreviewEmailLabel = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #334155;
    font-weight: 500;
    cursor: pointer;
    user-select: none;
    font-family: ${fontFamilyStack};

    @media (max-width: 640px) {
        justify-content: center;
    }
`;

const Selector = styled.select`
    padding: 6px 12px;
    border-radius: 6px;
    border: 1px solid #cbd5e1;
    background: #ffffff;
    font-size: 12px;
    color: #334155;
    outline: none;
    cursor: pointer;
    font-weight: 600;
    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
    font-family: ${fontFamilyStack};
    transition: border-color 0.15s ease;

    &:focus {
        border-color: #0284c7;
        box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.15);
    }
`;

const HorizontalTimelineWrapper = styled.div`
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 20px 24px;
    margin-bottom: 24px;
    font-family: ${fontFamilyStack};
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    @media (max-width: 640px) {
        padding: 16px;
        margin-bottom: 16px;
    }

    &::-webkit-scrollbar {
        height: 6px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 3px;
    }
`;

const TimelineTrack = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    margin: 24px 0;
    font-family: ${fontFamilyStack};
    min-width: 1400px;

    &::before {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        top: 15px;
        height: 2px;
        background: #e2e8f0;
        z-index: 1;
    }
`;

const TimelineNode = styled.div<{ status: 'past' | 'active' | 'future' }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 2;
    flex: 1;
    cursor: pointer;
    text-align: center;
    font-family: ${fontFamilyStack};
`;

const TimelineCircle = styled.div<{ status: 'past' | 'active' | 'future' }>`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: ${props => props.status === 'past' ? '#16a34a' : props.status === 'active' ? '#0284c7' : '#ffffff'};
    border: 3px solid #ffffff;
    box-shadow: 0 0 0 ${props => props.status === 'past' ? '1px #16a34a' : props.status === 'active' ? '4px rgba(2, 132, 199, 0.18)' : '1px #cbd5e1'};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    color: ${props => props.status === 'future' ? '#94a3b8' : '#ffffff'};
    font-weight: bold;
    transition: all 0.2s ease;
    font-family: ${fontFamilyStack};

    &:hover {
        transform: scale(1.1);
    }
`;

const TimelineText = styled.div<{ status: 'past' | 'active' | 'future' }>`
    margin-top: 8px;
    font-size: 12px;
    font-weight: ${props => props.status === 'active' ? '700' : '600'};
    color: ${props => props.status === 'active' ? '#1e293b' : '#64748b'};
    font-family: ${fontFamilyStack};
    max-width: 140px;
`;

const TimelineBadge = styled.span<{ type: 'manual_email' | 'automated_email' | 'info_onboarding' | 'info_testing' }>`
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    padding: 2px 7px;
    border-radius: 4px;
    margin-top: 6px;
    display: inline-block;
    white-space: nowrap;
    font-family: ${fontFamilyStack};
    
    ${props => {
        if (props.type === 'manual_email') {
            return `
                background-color: #fee2e2;
                color: #b91c1c;
                border: 1px solid #fca5a5;
            `;
        }
        if (props.type === 'automated_email') {
            return `
                background-color: #eff6ff;
                color: #1d4ed8;
                border: 1px solid #bfdbfe;
            `;
        }
        if (props.type === 'info_onboarding') {
            return `
                background-color: #fef3c7;
                color: #b45309;
                border: 1px solid #fde68a;
            `;
        }
        // info_testing
        return `
            background-color: #f3e8ff;
            color: #7e22ce;
            border: 1px solid #e9d5ff;
        `;
    }}
`;

const SearchContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    width: 100%;
    margin-bottom: 8px;
    font-family: ${fontFamilyStack};
`;

const TabContainer = styled.div`
    display: flex;
    flex-direction: row;
    border-bottom: 1px solid #e2e8f0;
    gap: 8px;
    overflow-x: auto;
    width: 100%;
    padding-bottom: 1px;
    margin-bottom: 16px;
    font-family: ${fontFamilyStack};

    /* Webkit scrollbar styles for cleaner mobile scroll */
    &::-webkit-scrollbar {
        height: 4px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 2px;
    }
`;

interface TabButtonProps {
    isActive: boolean;
}

const TabButton = styled.button<TabButtonProps>`
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: none;
    border-bottom: 3px solid ${props => props.isActive ? '#0284c7' : 'transparent'};
    padding: 10px 16px;
    font-family: ${fontFamilyStack};
    font-size: 13px;
    font-weight: ${props => props.isActive ? '700' : '600'};
    color: ${props => props.isActive ? '#0284c7' : '#64748b'};
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.2s ease;
    margin-bottom: -1px;
    position: relative;
    z-index: 2;

    &:hover {
        color: #0284c7;
    }

    &:focus {
        outline: none;
    }
`;

const TabCount = styled.span<TabButtonProps>`
    background: ${props => props.isActive ? '#0284c7' : '#e2e8f0'};
    color: ${props => props.isActive ? '#ffffff' : '#475569'};
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 12px;
    transition: all 0.2s ease;
    font-family: ${fontFamilyStack};
`;

const onboardingFormsSectionStyle = css`
    margin-left: -32px;
    margin-right: -32px;

    @media (max-width: 640px) {
        margin-left: -16px;
        margin-right: -16px;
    }

    && .card-title-container {
        padding: 24px 32px;
        @media (max-width: 640px) {
            padding: 16px;
        }

        .card-hover-icon {
            display: none;
        }
    }

    && .card-body {
        padding: 32px;
        @media (max-width: 640px) {
            padding: 16px;
        }
    }
`;

const OnboardingFormsBodyContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-top: 16px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 24px;
    font-family: ${fontFamilyStack};
`;

const OrgLineItem = styled.div<{ bottom?: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 18px 24px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-left: 5px solid #0284c7;
    border-radius: 8px;
    margin-left: 0;
    margin-right: 0;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 1px 2px 0 rgba(0, 0, 0, 0.02);
    margin-top: 8px;
    margin-bottom: 12px;
    transition: all 0.2s ease-in-out;
    font-family: ${fontFamilyStack};

    &:hover {
        background: #f8fafc;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
    }

    .left-content {
        display: flex;
        align-items: center;
        gap: 16px;

        .org-icon-container {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 38px;
            height: 38px;
            background: #f0f9ff;
            border-radius: 50%;
            border: 1px solid #bae6fd;

            img {
                width: 20px;
                height: 20px;
            }
        }

        .title-block {
            display: flex;
            flex-direction: column;
            gap: 2px;
            
            h4 {
                font-family: ${fontFamilyStack};
                font-size: 15px;
                font-weight: 700;
                color: #1e293b;
                margin: 0;
            }

            .subtitle {
                font-family: ${fontFamilyStack};
                font-size: 12px;
                color: #64748b;
                font-weight: 500;
            }
        }
    }

    .right-content {
        display: flex;
        align-items: center;
        gap: 12px;
    }
`;

const nestedCollapsibleSectionStyle = css`
    margin-left: -24px;
    margin-right: -24px;

    && .card-title-container {
        padding-left: 24px;
        padding-right: 24px;
    }

    && .left-content > h4 {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        max-width: 400px;
        
        @media (max-width: 1200px) {
            max-width: 300px;
        }
        @media (max-width: 768px) {
            max-width: 180px;
        }
    }

    && .card-body {
        padding: 0;
    }
`;

export default OnboardingLinksCard;
